import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X as LucideX, Save, Trash2, Plus, Move, Pencil, ZoomIn, ZoomOut, Download, Upload, RotateCw, Check, Database, Tag, Maximize2, Grid3x3, Copy, Layers, Settings, Hand, MousePointer2, Minus, Type, Image, GitBranch, CircleDot, Navigation, Undo2, Redo2, Clipboard, Magnet, AlignStartHorizontal, AlignCenterHorizontal, AlignEndHorizontal, AlignStartVertical, AlignCenterVertical, AlignEndVertical, ChevronsUp, ChevronsDown, ChevronUp, ChevronDown, FlipHorizontal, FlipVertical, Scissors, CopyPlus, Group, Ungroup, Lock, CheckSquare } from 'lucide-react';
import questdbService from '../../services/questdb';
import ScadaToolbar from './Scadatoolbar';
// Import new SCADA components
import {
  ResizeHandles,
  calculateResize,
  TextBoxElement,
  DEFAULT_TEXTBOX,
  TextBoxConfigModal,
  BackgroundUploader,
  PipeElement,
  PIPE_STYLES,
  DEFAULT_PIPE,
  generatePipePath,
  PipeConfigModal,
  CustomImageUploader,
  SearchableTagSelect,
  PumpConfigModal,
  ValveConfigModal,
  ControlValveConfigModal,
  LedConfigModal
} from './scada';



// ── Inline arrow components (no separate file needed) ─────────────────────

// ──────────────────────────────────────────────────────────────────────────

const PUMP_TYPES = new Set([
  'pump3D','centrifugalPump2','centrifugalPump4','classicPump1','coolPump',
  'horizontalPump3','horizontalPump4','horizontalSplitCasePump','magDriveNonMetallic',
  'magDrivePump2','meteringPump4','seallessPump','selfPrimingCentrifugalPump',
  'srhPump','verticalPump9','yellowPump'
]);

// Non-control valve types (excludes control valves)
// SOV (Shut-Off Valves) — use valve config + faceplate
const VALVE_TYPES = new Set([
  'handControlValve','valveWithActuator','ballValve1','blowdownValve','compactValve',
  'handValve1','handValve3','handValve4','pvcTwoWayBallValve'
]);
// Control Valves (PID) — use control-valve config + faceplate
const CONTROL_VALVE_TYPES = new Set([
  'controlValveHandActuator','blueControlValve','controlValve3','controlValveDiaphragm',
  'controlValveGrayFitting','controlValue','valve3D','pressureBalancedDiaphragm',
  'safetyShutoffValve','regulatorExternalPressure','regulatorSelfContained',
  'rotaryPlugValve','valveLongShaft','valveFailLocked','handValveHorizontal'
]);

// React component imports (all with dynamic color support)
import BlowdownValveComponent from '../svg/3D-Blowdown-valve';
import PressureBalancedDiaphragm from '../svg/3-D Pressure-balanced-diaphragm-actuated';
import ClassicPump1 from '../svg/Classic_pump_1';
import RegulatorExternalPressure from '../svg/RegulatorExternalPressure';
import RegulatorSelfContained from '../svg/RegulatorSelfContained';
import RotaryPlugValve from '../svg/RotaryPlugValve';
import ValveLongShaft from '../svg/ValveLongShaft';
import ValveFailLocked from '../svg/ValveFailLocked';
import ControlValveHandActuator from '../svg/ControlValveHandActuator';
import HandControlValveIcon from '../svg/HandControlValveIcon';
import Pipe90Curve1Icon from '../svg/Pipe90Curve1Icon';
import Pipe90Curve2Icon from '../svg/Pipe90Curve2Icon';
import Pipe90Curve3Icon from '../svg/Pipe90Curve3Icon';
import Pipe90Curve4Icon from '../svg/Pipe90Curve4Icon';
import PipeCutEnd1Icon from '../svg/PipeCutEnd1Icon';
import PipeCutEnd2Icon from '../svg/PipeCutEnd2Icon';
import PipeIntersection1Icon from '../svg/PipeIntersection1Icon';
import PipeLongHorizontalIcon from '../svg/PipeLongHorizontalIcon';
import PipeLongVerticalIcon from '../svg/PipeLongVerticalIcon';
import PipeOpenEnd1Icon from '../svg/PipeOpenEnd1Icon';
import PipeOpenEnd2Icon from '../svg/PipeOpenEnd2Icon';
import PipeRightAngle1Icon from '../svg/PipeRightAngle1Icon';
import PipeRightAngle2Icon from '../svg/PipeRightAngle2Icon';
import PipeRightAngle3Icon from '../svg/PipeRightAngle3Icon';
import PipeRightAngle4Icon from '../svg/PipeRightAngle4Icon';
import PipeShortHorizontalIcon from '../svg/PipeShortHorizontalIcon';
import PipeShortVerticalIcon from '../svg/PipeShortVerticalIcon';
import PipeTee1Icon from '../svg/PipeTee1Icon';
import PipeTee2Icon from '../svg/PipeTee2Icon';
import PipeTee3Icon from '../svg/PipeTee3Icon';
import PipeTee4Icon from '../svg/PipeTee4Icon';
import Pump3DIcon from '../svg/Pump3DIcon';
import Valve3DIcon from '../svg/Valve3DIcon';
import ValveWithActuatorIcon from '../svg/ValveWithActuatorIcon';
import BallValve1Icon from '../svg/BallValve1Icon';
import PVCTwoWayBallValveIcon from '../svg/PVCTwoWayBallValveIcon';
import BlueControlValveIcon from '../svg/BlueControlValveIcon';
import CompactValveIcon from '../svg/CompactValveIcon';
import ControlValve3Icon from '../svg/ControlValve3Icon';
import ControlValveDiaphragmIcon from '../svg/ControlValveDiaphragmIcon';
import ControlValveGrayFittingIcon from '../svg/ControlValveGrayFittingIcon';
import ControlValueIcon from '../svg/ControlValueIcon';
import SafetyShutoffValveIcon from '../svg/SafetyShutoffValveIcon';
import HandValveHorizontalIcon from '../svg/HandValveHorizontalIcon';
import HandValve1Icon from '../svg/HandValve1Icon';
import HandValve3Icon from '../svg/HandValve3Icon';
import HandValve4Icon from '../svg/HandValve4Icon';
import CentrifugalPump2Icon from '../svg/CentrifugalPump2Icon';
import CentrifugalPump4Icon from '../svg/CentrifugalPump4Icon';
import CoolPumpIcon from '../svg/CoolPumpIcon';
import HorizontalPump3Icon from '../svg/HorizontalPump3Icon';
import HorizontalPump4Icon from '../svg/HorizontalPump4Icon';
import HorizontalSplitCasePumpIcon from '../svg/HorizontalSplitCasePumpIcon';
import MagDriveNonMetallicIcon from '../svg/MagDriveNonMetallicIcon';
import MagDrivePump2Icon from '../svg/MagDrivePump2Icon';
import MeteringPump4Icon from '../svg/MeteringPump4Icon';
import SeallessPumpIcon from '../svg/SeallessPumpIcon';
import SelfPrimingCentrifugalPumpIcon from '../svg/SelfPrimingCentrifugalPumpIcon';
import SRHPumpIcon from '../svg/SRHPumpIcon';
import VerticalPump9Icon from '../svg/VerticalPump9Icon';
import YellowPumpIcon from '../svg/YellowPumpIcon';
import FlowTotalizerIcon from '../svg/FlowTotalizerIcon';
import Flowmeter2Icon from '../svg/Flowmeter2Icon';
import MagneticFlowmeter2Icon from '../svg/MagneticFlowmeter2Icon';
import MagneticFlowmeter3Icon from '../svg/MagneticFlowmeter3Icon';
import MassFlowmeterIcon from '../svg/MassFlowmeterIcon';
import TurbineMeter1Icon from '../svg/TurbineMeter1Icon';
import TurbineMeter3Icon from '../svg/TurbineMeter3Icon';
import FlamelessThermalOxidizerIcon from '../svg/FlamelessThermalOxidizerIcon';
import HeatExchanger1Icon from '../svg/HeatExchanger1Icon';
import HeatExchanger10Icon from '../svg/HeatExchanger10Icon';
import HeatExchanger3Icon from '../svg/HeatExchanger3Icon';
import HeatExchanger4Icon from '../svg/HeatExchanger4Icon';
import SimpleSteamGenerator2Icon from '../svg/SimpleSteamGenerator2Icon';
import ThermalFluidHeater1Icon from '../svg/ThermalFluidHeater1Icon';
import Mixer3DIcon from '../svg/Mixer3DIcon';
import ImpellerIcon from '../svg/ImpellerIcon';
import Mixer1Icon from '../svg/Mixer1Icon';
import Mixer2Icon from '../svg/Mixer2Icon';
import MixerBladeIcon from '../svg/MixerBladeIcon';
import PneumaticMixer1Icon from '../svg/PneumaticMixer1Icon';
import PneumaticMixer2Icon from '../svg/PneumaticMixer2Icon';
import ScrewTypeMixerIcon from '../svg/ScrewTypeMixerIcon';
import SimpleMixerIcon from '../svg/SimpleMixerIcon';
import SubmersibleMixerIcon from '../svg/SubmersibleMixerIcon';
import TankWithAgitatorIcon from '../svg/TankWithAgitatorIcon';
import TurbineAgitator1Icon from '../svg/TurbineAgitator1Icon';
import TurbineAgitator2Icon from '../svg/TurbineAgitator2Icon';
import TurbineAgitator3Icon from '../svg/TurbineAgitator3Icon';
import Boiler10Icon from '../svg/Boiler10Icon';
import Boiler5Icon from '../svg/Boiler5Icon';
import Boiler9Icon from '../svg/Boiler9Icon';
import BoilerWithFlamesPipeIcon from '../svg/BoilerWithFlamesPipeIcon';
import BoilerWithFlamesIcon from '../svg/BoilerWithFlamesIcon';
import GasFiredBoiler1Icon from '../svg/GasFiredBoiler1Icon';
import PackagedFiretubeBoilerIcon from '../svg/PackagedFiretubeBoilerIcon';
import CoolingTower1Icon from '../svg/CoolingTower1Icon';
import CoolingTower2Icon from '../svg/CoolingTower2Icon';
import CoolingTower3Icon from '../svg/CoolingTower3Icon';
import CoolingTower4Icon from '../svg/CoolingTower4Icon';
import CoolingUnitIcon from '../svg/CoolingUnitIcon';
import LargePortableChillerIcon from '../svg/LargePortableChillerIcon';
import TemperatureControlUnitIcon from '../svg/TemperatureControlUnitIcon';
import ChamberWithFlangeIcon from '../svg/ChamberWithFlangeIcon';
import HopperIcon from '../svg/HopperIcon';
import PolyethyleneTankIcon from '../svg/PolyethyleneTankIcon';
import Reactor6Icon from '../svg/Reactor6Icon';
import Reactor7Icon from '../svg/Reactor7Icon';
import ReactorTankIcon from '../svg/ReactorTankIcon';
import StabilizationTankIcon from '../svg/StabilizationTankIcon';
import StainlessSteelCylinderIcon from '../svg/StainlessSteelCylinderIcon';
import StorageFacility2Icon from '../svg/StorageFacility2Icon';
import StorageTankWithLadderIcon from '../svg/StorageTankWithLadderIcon';
import Tank11Icon from '../svg/Tank11Icon';
import Tank13Icon from '../svg/Tank13Icon';
import Tank15Icon from '../svg/Tank15Icon';
import Tank2Icon from '../svg/Tank2Icon';
import Tank21Icon from '../svg/Tank21Icon';
import Tank23Icon from '../svg/Tank23Icon';
import Tank27Icon from '../svg/Tank27Icon';
import Tank28Icon from '../svg/Tank28Icon';
import Tank37Icon from '../svg/Tank37Icon';
import Tank40Icon from '../svg/Tank40Icon';
import Tank9Icon from '../svg/Tank9Icon';
import TankWithHatchIcon from '../svg/TankWithHatchIcon';
import TankWithRivetsLadderIcon from '../svg/TankWithRivetsLadderIcon';


const X = LucideX;

// Inline arrow SVG components (no separate file)
const ArrowRight = ({ width = 60, height = 30, startColor = '#ef4444' }) => (
  <svg width={width} height={height} viewBox="0 0 60 30"><line x1="4" y1="15" x2="46" y2="15" stroke={startColor} strokeWidth="4" strokeLinecap="round"/><polygon points="44,7 58,15 44,23" fill={startColor}/></svg>
);
const ArrowLeft = ({ width = 60, height = 30, startColor = '#ef4444' }) => (
  <svg width={width} height={height} viewBox="0 0 60 30"><line x1="56" y1="15" x2="14" y2="15" stroke={startColor} strokeWidth="4" strokeLinecap="round"/><polygon points="16,7 2,15 16,23" fill={startColor}/></svg>
);
const ArrowUp = ({ width = 30, height = 60, startColor = '#ef4444' }) => (
  <svg width={width} height={height} viewBox="0 0 30 60"><line x1="15" y1="56" x2="15" y2="14" stroke={startColor} strokeWidth="4" strokeLinecap="round"/><polygon points="7,16 15,2 23,16" fill={startColor}/></svg>
);
const ArrowDown = ({ width = 30, height = 60, startColor = '#ef4444' }) => (
  <svg width={width} height={height} viewBox="0 0 30 60"><line x1="15" y1="4" x2="15" y2="46" stroke={startColor} strokeWidth="4" strokeLinecap="round"/><polygon points="7,44 15,58 23,44" fill={startColor}/></svg>
);
const ArrowBoth = ({ width = 60, height = 30, startColor = '#ef4444' }) => (
  <svg width={width} height={height} viewBox="0 0 60 30"><line x1="14" y1="15" x2="46" y2="15" stroke={startColor} strokeWidth="4" strokeLinecap="round"/><polygon points="16,7 2,15 16,23" fill={startColor}/><polygon points="44,7 58,15 44,23" fill={startColor}/></svg>
);

// Module-level color helpers (available everywhere in this file)
const lightenColor = (hex, percent) => {
  const num = parseInt((hex || '#64748b').replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, (num >> 16) + amt);
  const G = Math.min(255, ((num >> 8) & 0x00FF) + amt);
  const B = Math.min(255, (num & 0x0000FF) + amt);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};
const darkenColor = (hex, percent) => {
  const num = parseInt((hex || '#64748b').replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, (num >> 16) - amt);
  const G = Math.max(0, ((num >> 8) & 0x00FF) - amt);
  const B = Math.max(0, (num & 0x0000FF) - amt);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};

// Memoized SVG icon — prevents gradient ID re-generation on every re-render
const MemoizedIcon = React.memo(function MemoizedIcon({
  Component, width, height,
  startColor, midColor, endColor,
  stemStartColor, stemMidColor, stemEndColor,
  fillPercent, fillColor
}) {
  return (
    <Component
      width={width} height={height}
      startColor={startColor} midColor={midColor} endColor={endColor}
      stemStartColor={stemStartColor} stemMidColor={stemMidColor} stemEndColor={stemEndColor}
      fillPercent={fillPercent} fillColor={fillColor}
    />
  );
});

const SVG_COMPONENTS = {
  // === PUMPS ===
  pump3D: { name: '3D Pump', width: 90, height: 80, component: Pump3DIcon, type: 'component', color: '#3b82f6', category: 'pump' },
  centrifugalPump2: { name: 'Centrifugal Pump 2', width: 90, height: 80, component: CentrifugalPump2Icon, type: 'component', color: '#3b82f6', category: 'pump' },
  centrifugalPump4: { name: 'Centrifugal Pump 4', width: 80, height: 80, component: CentrifugalPump4Icon, type: 'component', color: '#3b82f6', category: 'pump' },
  classicPump1: { name: 'Classic Pump', width: 70, height: 70, component: ClassicPump1, type: 'component', color: '#3b82f6', category: 'pump' },
  coolPump: { name: 'Cool Pump', width: 80, height: 80, component: CoolPumpIcon, type: 'component', color: '#06b6d4', category: 'pump' },
  horizontalPump3: { name: 'Horizontal Pump 3', width: 100, height: 70, component: HorizontalPump3Icon, type: 'component', color: '#3b82f6', category: 'pump' },
  horizontalPump4: { name: 'Horizontal Pump 4', width: 100, height: 70, component: HorizontalPump4Icon, type: 'component', color: '#3b82f6', category: 'pump' },
  horizontalSplitCasePump: { name: 'Split Case Pump', width: 100, height: 80, component: HorizontalSplitCasePumpIcon, type: 'component', color: '#3b82f6', category: 'pump' },
  magDriveNonMetallic: { name: 'Mag Drive (Non-Metal)', width: 80, height: 80, component: MagDriveNonMetallicIcon, type: 'component', color: '#06b6d4', category: 'pump' },
  magDrivePump2: { name: 'Mag Drive Pump 2', width: 90, height: 80, component: MagDrivePump2Icon, type: 'component', color: '#3b82f6', category: 'pump' },
  meteringPump4: { name: 'Metering Pump', width: 80, height: 80, component: MeteringPump4Icon, type: 'component', color: '#8b5cf6', category: 'pump' },
  seallessPump: { name: 'Sealless Pump', width: 80, height: 80, component: SeallessPumpIcon, type: 'component', color: '#10b981', category: 'pump' },
  selfPrimingCentrifugalPump: { name: 'Self-Priming Pump', width: 90, height: 80, component: SelfPrimingCentrifugalPumpIcon, type: 'component', color: '#3b82f6', category: 'pump' },
  srhPump: { name: 'SRH Pump', width: 90, height: 90, component: SRHPumpIcon, type: 'component', color: '#3b82f6', category: 'pump' },
  verticalPump9: { name: 'Vertical Pump', width: 70, height: 100, component: VerticalPump9Icon, type: 'component', color: '#3b82f6', category: 'pump' },
  yellowPump: { name: 'Yellow Pump', width: 80, height: 80, component: YellowPumpIcon, type: 'component', color: '#eab308', category: 'pump' },
  // === VALVES ===
  // === SOV (Shut-Off Valves) ===
  handControlValve: { name: 'Hand Control Valve', width: 60, height: 70, component: HandControlValveIcon, type: 'component', color: '#f59e0b', category: 'sov' },
  valveWithActuator: { name: 'Valve with Actuator', width: 70, height: 90, component: ValveWithActuatorIcon, type: 'component', color: '#10b981', category: 'sov' },
  ballValve1: { name: 'Ball Valve', width: 70, height: 70, component: BallValve1Icon, type: 'component', color: '#64748b', category: 'sov' },
  blowdownValve: { name: 'Blowdown Valve', width: 60, height: 80, component: BlowdownValveComponent, type: 'component', color: '#ef4444', category: 'sov' },
  compactValve: { name: 'Compact Valve', width: 60, height: 70, component: CompactValveIcon, type: 'component', color: '#06b6d4', category: 'sov' },
  handValve1: { name: 'Hand Valve 1', width: 60, height: 80, component: HandValve1Icon, type: 'component', color: '#f59e0b', category: 'sov' },
  handValve3: { name: 'Hand Valve 2', width: 70, height: 80, component: HandValve3Icon, type: 'component', color: '#f59e0b', category: 'sov' },
  handValve4: { name: 'Hand Valve 3', width: 70, height: 80, component: HandValve4Icon, type: 'component', color: '#f59e0b', category: 'sov' },
  pvcTwoWayBallValve: { name: 'PVC Two-Way Ball Valve', width: 60, height: 70, component: PVCTwoWayBallValveIcon, type: 'component', color: '#64748b', category: 'sov' },
  // === Control Valves (PID) ===
  controlValveHandActuator: { name: 'Control Valve (Hand)', width: 70, height: 80, component: ControlValveHandActuator, type: 'component', color: '#3b82f6', category: 'controlValve' },
  pressureBalancedDiaphragm: { name: 'Pressure Balanced Diaphragm', width: 70, height: 90, component: PressureBalancedDiaphragm, type: 'component', color: '#06b6d4', category: 'controlValve' },
  valve3D: { name: 'Valve Top', width: 60, height: 80, component: Valve3DIcon, type: 'component', color: '#f59e0b', category: 'controlValve' },
  controlValue: { name: 'Control Valve', width: 70, height: 80, component: ControlValueIcon, type: 'component', color: '#3b82f6', category: 'controlValve' },
  blueControlValve: { name: 'Blue Control Valve', width: 70, height: 90, component: BlueControlValveIcon, type: 'component', color: '#3b82f6', category: 'controlValve' },
  controlValve3: { name: 'Control Valve 3', width: 70, height: 80, component: ControlValve3Icon, type: 'component', color: '#3b82f6', category: 'controlValve' },
  controlValveDiaphragm: { name: 'Control Valve (Diaphragm)', width: 70, height: 90, component: ControlValveDiaphragmIcon, type: 'component', color: '#3b82f6', category: 'controlValve' },
  controlValveGrayFitting: { name: 'Control Valve (Gray)', width: 70, height: 90, component: ControlValveGrayFittingIcon, type: 'component', color: '#64748b', category: 'controlValve' },
  safetyShutoffValve: { name: 'Safety Shutoff Valve', width: 70, height: 90, component: SafetyShutoffValveIcon, type: 'component', color: '#ef4444', category: 'controlValve' },
  regulatorExternalPressure: { name: 'Regulator (External)', width: 80, height: 80, component: RegulatorExternalPressure, type: 'component', color: '#8b5cf6', category: 'controlValve' },
  regulatorSelfContained: { name: 'Regulator (Self-Contained)', width: 80, height: 80, component: RegulatorSelfContained, type: 'component', color: '#8b5cf6', category: 'controlValve' },
  rotaryPlugValve: { name: 'Rotary Plug Valve', width: 70, height: 70, component: RotaryPlugValve, type: 'component', color: '#f59e0b', category: 'controlValve' },
  valveLongShaft: { name: 'Valve Long Shaft', width: 60, height: 90, component: ValveLongShaft, type: 'component', color: '#f59e0b', category: 'controlValve' },
  valveFailLocked: { name: 'Valve Fail Locked', width: 70, height: 80, component: ValveFailLocked, type: 'component', color: '#ef4444', category: 'controlValve' },
  handValveHorizontal: { name: 'Hand Valve 1', width: 80, height: 60, component: HandValveHorizontalIcon, type: 'component', color: '#f59e0b', category: 'controlValve' },
  handValve4: { name: 'Hand Valve 4', width: 70, height: 80, component: HandValve4Icon, type: 'component', color: '#f59e0b', category: 'valve' },
  // === FLOW METERS ===
  flowTotalizer: { name: 'Flow Totalizer', width: 80, height: 80, component: FlowTotalizerIcon, type: 'component', color: '#10b981', category: 'flowmeter' },
  flowmeter2: { name: 'Flowmeter 2', width: 80, height: 80, component: Flowmeter2Icon, type: 'component', color: '#10b981', category: 'flowmeter' },
  magneticFlowmeter2: { name: 'Magnetic Flowmeter 2', width: 80, height: 80, component: MagneticFlowmeter2Icon, type: 'component', color: '#10b981', category: 'flowmeter' },
  magneticFlowmeter3: { name: 'Magnetic Flowmeter 3', width: 80, height: 80, component: MagneticFlowmeter3Icon, type: 'component', color: '#10b981', category: 'flowmeter' },
  massFlowmeter: { name: 'Mass Flowmeter', width: 80, height: 80, component: MassFlowmeterIcon, type: 'component', color: '#10b981', category: 'flowmeter' },
  turbineMeter1: { name: 'Turbine Meter 1', width: 80, height: 80, component: TurbineMeter1Icon, type: 'component', color: '#10b981', category: 'flowmeter' },
  turbineMeter3: { name: 'Turbine Meter 3', width: 80, height: 80, component: TurbineMeter3Icon, type: 'component', color: '#10b981', category: 'flowmeter' },
  // === HEATERS ===
  flamelessThermalOxidizer: { name: 'Flameless Oxidizer', width: 80, height: 80, component: FlamelessThermalOxidizerIcon, type: 'component', color: '#f59e0b', category: 'heater' },
  heatExchanger1: { name: 'Heat Exchanger 1', width: 80, height: 80, component: HeatExchanger1Icon, type: 'component', color: '#ef4444', category: 'heater' },
  heatExchanger10: { name: 'Heat Exchanger 10', width: 80, height: 80, component: HeatExchanger10Icon, type: 'component', color: '#ef4444', category: 'heater' },
  heatExchanger3: { name: 'Heat Exchanger 3', width: 80, height: 80, component: HeatExchanger3Icon, type: 'component', color: '#ef4444', category: 'heater' },
  heatExchanger4: { name: 'Heat Exchanger 4', width: 80, height: 80, component: HeatExchanger4Icon, type: 'component', color: '#ef4444', category: 'heater' },
  simpleSteamGenerator2: { name: 'Steam Generator 2', width: 80, height: 80, component: SimpleSteamGenerator2Icon, type: 'component', color: '#f59e0b', category: 'heater' },
  thermalFluidHeater1: { name: 'Thermal Fluid Heater', width: 80, height: 80, component: ThermalFluidHeater1Icon, type: 'component', color: '#ef4444', category: 'heater' },
  // === STIRRERS ===
  mixer3D: { name: '3D Mixer', width: 70, height: 90, component: Mixer3DIcon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  impeller: { name: 'Impeller', width: 70, height: 70, component: ImpellerIcon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  mixer1: { name: 'Mixer 1', width: 70, height: 90, component: Mixer1Icon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  mixer2: { name: 'Mixer 2', width: 70, height: 90, component: Mixer2Icon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  mixerBlade: { name: 'Mixer Blade', width: 70, height: 70, component: MixerBladeIcon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  pneumaticMixer1: { name: 'Pneumatic Mixer 1', width: 70, height: 90, component: PneumaticMixer1Icon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  pneumaticMixer2: { name: 'Pneumatic Mixer 2', width: 70, height: 90, component: PneumaticMixer2Icon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  screwTypeMixer: { name: 'Screw Mixer', width: 70, height: 90, component: ScrewTypeMixerIcon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  simpleMixer: { name: 'Simple Mixer', width: 70, height: 90, component: SimpleMixerIcon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  submersibleMixer: { name: 'Submersible Mixer', width: 70, height: 70, component: SubmersibleMixerIcon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  tankWithAgitator: { name: 'Tank w/ Agitator', width: 80, height: 90, component: TankWithAgitatorIcon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  turbineAgitator1: { name: 'Turbine Agitator 1', width: 70, height: 90, component: TurbineAgitator1Icon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  turbineAgitator2: { name: 'Turbine Agitator 2', width: 70, height: 90, component: TurbineAgitator2Icon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  turbineAgitator3: { name: 'Turbine Agitator 3', width: 70, height: 90, component: TurbineAgitator3Icon, type: 'component', color: '#8b5cf6', category: 'stirrer' },
  // === BOILERS ===
  boiler10: { name: 'Boiler 10', width: 80, height: 100, component: Boiler10Icon, type: 'component', color: '#ef4444', category: 'boiler' },
  boiler5: { name: 'Boiler 5', width: 80, height: 100, component: Boiler5Icon, type: 'component', color: '#ef4444', category: 'boiler' },
  boiler9: { name: 'Boiler 9', width: 80, height: 100, component: Boiler9Icon, type: 'component', color: '#ef4444', category: 'boiler' },
  boilerWithFlamesPipe: { name: 'Boiler w/ Pipe', width: 100, height: 100, component: BoilerWithFlamesPipeIcon, type: 'component', color: '#ef4444', category: 'boiler' },
  boilerWithFlames: { name: 'Boiler w/ Flames', width: 80, height: 100, component: BoilerWithFlamesIcon, type: 'component', color: '#ef4444', category: 'boiler' },
  gasFiredBoiler1: { name: 'Gas-Fired Boiler', width: 80, height: 100, component: GasFiredBoiler1Icon, type: 'component', color: '#ef4444', category: 'boiler' },
  packagedFiretubeBoiler: { name: 'Firetube Boiler', width: 100, height: 80, component: PackagedFiretubeBoilerIcon, type: 'component', color: '#ef4444', category: 'boiler' },
  // === COOLERS ===
  coolingTower1: { name: 'Cooling Tower 1', width: 80, height: 100, component: CoolingTower1Icon, type: 'component', color: '#06b6d4', category: 'cooler' },
  coolingTower2: { name: 'Cooling Tower 2', width: 80, height: 100, component: CoolingTower2Icon, type: 'component', color: '#06b6d4', category: 'cooler' },
  coolingTower3: { name: 'Cooling Tower 3', width: 80, height: 100, component: CoolingTower3Icon, type: 'component', color: '#06b6d4', category: 'cooler' },
  coolingTower4: { name: 'Cooling Tower 4', width: 80, height: 100, component: CoolingTower4Icon, type: 'component', color: '#06b6d4', category: 'cooler' },
  coolingUnit: { name: 'Cooling Unit', width: 80, height: 80, component: CoolingUnitIcon, type: 'component', color: '#06b6d4', category: 'cooler' },
  largePortableChiller: { name: 'Portable Chiller', width: 80, height: 80, component: LargePortableChillerIcon, type: 'component', color: '#06b6d4', category: 'cooler' },
  temperatureControlUnit: { name: 'Temp Control Unit', width: 80, height: 80, component: TemperatureControlUnitIcon, type: 'component', color: '#06b6d4', category: 'cooler' },
  // === TANKS ===
  chamberWithFlange: { name: 'Chamber w/ Flange', width: 80, height: 100, component: ChamberWithFlangeIcon, type: 'component', color: '#64748b', category: 'tank' },
  hopper: { name: 'Hopper', width: 80, height: 100, component: HopperIcon, type: 'component', color: '#64748b', category: 'tank' },
  polyethyleneTank: { name: 'Polyethylene Tank', width: 80, height: 100, component: PolyethyleneTankIcon, type: 'component', color: '#64748b', category: 'tank' },
  reactor6: { name: 'Reactor 6', width: 80, height: 100, component: Reactor6Icon, type: 'component', color: '#8b5cf6', category: 'tank' },
  reactor7: { name: 'Reactor 7', width: 80, height: 100, component: Reactor7Icon, type: 'component', color: '#8b5cf6', category: 'tank' },
  reactorTank: { name: 'Reactor Tank', width: 80, height: 100, component: ReactorTankIcon, type: 'component', color: '#8b5cf6', category: 'tank' },
  stabilizationTank: { name: 'Stabilization Tank', width: 80, height: 100, component: StabilizationTankIcon, type: 'component', color: '#64748b', category: 'tank' },
  stainlessSteelCylinder: { name: 'SS Cylinder', width: 70, height: 100, component: StainlessSteelCylinderIcon, type: 'component', color: '#94a3b8', category: 'tank' },
  storageFacility2: { name: 'Storage Facility 2', width: 100, height: 100, component: StorageFacility2Icon, type: 'component', color: '#64748b', category: 'tank' },
  storageTankWithLadder: { name: 'Tank w/ Ladder', width: 80, height: 100, component: StorageTankWithLadderIcon, type: 'component', color: '#64748b', category: 'tank' },
  tank11: { name: 'Tank 11', width: 80, height: 100, component: Tank11Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank13: { name: 'Tank 13', width: 80, height: 100, component: Tank13Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank15: { name: 'Tank 15', width: 80, height: 100, component: Tank15Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank2: { name: 'Tank 2', width: 80, height: 100, component: Tank2Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank21: { name: 'Tank 21', width: 80, height: 100, component: Tank21Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank23: { name: 'Tank 23', width: 80, height: 100, component: Tank23Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank27: { name: 'Tank 27', width: 80, height: 100, component: Tank27Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank28: { name: 'Tank 28', width: 80, height: 100, component: Tank28Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank37: { name: 'Tank 37', width: 80, height: 100, component: Tank37Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank40: { name: 'Tank 40', width: 80, height: 100, component: Tank40Icon, type: 'component', color: '#64748b', category: 'tank' },
  tank9: { name: 'Tank 9', width: 80, height: 100, component: Tank9Icon, type: 'component', color: '#64748b', category: 'tank' },
  tankWithHatch: { name: 'Tank w/ Hatch', width: 80, height: 100, component: TankWithHatchIcon, type: 'component', color: '#64748b', category: 'tank' },
  tankWithRivetsLadder: { name: 'Tank Rivets+Ladder', width: 80, height: 100, component: TankWithRivetsLadderIcon, type: 'component', color: '#64748b', category: 'tank' },
  // === ARROWS ===
  arrowRight: { name: 'Arrow →', width: 60, height: 30, component: ArrowRight, type: 'component', color: '#ef4444', category: 'arrow' },
  arrowLeft:  { name: 'Arrow ←', width: 60, height: 30, component: ArrowLeft,  type: 'component', color: '#ef4444', category: 'arrow' },
  arrowUp:    { name: 'Arrow ↑', width: 30, height: 60, component: ArrowUp,    type: 'component', color: '#ef4444', category: 'arrow' },
  arrowDown:  { name: 'Arrow ↓', width: 30, height: 60, component: ArrowDown,  type: 'component', color: '#ef4444', category: 'arrow' },
  arrowBoth:  { name: 'Arrow ↔', width: 60, height: 30, component: ArrowBoth,  type: 'component', color: '#ef4444', category: 'arrow' },
  // === Pipe Fittings ===
  pipeLongHorizontal:  { name: 'Long horizontal pipe', width: 80, height: 12,  component: PipeLongHorizontalIcon,  type: 'component', color: '#64748b', category: 'pipe' },
  pipeLongVertical:    { name: 'Long vertical pipe',   width: 12,  height: 80,  component: PipeLongVerticalIcon,    type: 'component', color: '#64748b', category: 'pipe' },
  pipeIntersection1:   { name: 'Intersection 1',       width: 64,  height: 64,  component: PipeIntersection1Icon,   type: 'component', color: '#64748b', category: 'pipe' },
  pipeTee1:            { name: 'Tee 1',                width: 64,  height: 64,  component: PipeTee1Icon,            type: 'component', color: '#64748b', category: 'pipe' },
  pipeTee2:            { name: 'Tee 2',                width: 64,  height: 64,  component: PipeTee2Icon,            type: 'component', color: '#64748b', category: 'pipe' },
  pipeTee3:            { name: 'Tee 3',                width: 64,  height: 64,  component: PipeTee3Icon,            type: 'component', color: '#64748b', category: 'pipe' },
  pipeTee4:            { name: 'Tee 4',                width: 64,  height: 64,  component: PipeTee4Icon,            type: 'component', color: '#64748b', category: 'pipe' },
  pipe90Curve1:        { name: '90 curve 1',           width: 64,  height: 64,  component: Pipe90Curve1Icon,        type: 'component', color: '#64748b', category: 'pipe' },
  pipe90Curve2:        { name: '90 curve 2',           width: 64,  height: 64,  component: Pipe90Curve2Icon,        type: 'component', color: '#64748b', category: 'pipe' },
  pipe90Curve3:        { name: '90 curve 3',           width: 64,  height: 64,  component: Pipe90Curve3Icon,        type: 'component', color: '#64748b', category: 'pipe' },
  pipe90Curve4:        { name: '90 curve 4',           width: 64,  height: 64,  component: Pipe90Curve4Icon,        type: 'component', color: '#64748b', category: 'pipe' },
  pipeRightAngle1:     { name: 'Right angle 1',        width: 64,  height: 64,  component: PipeRightAngle1Icon,     type: 'component', color: '#64748b', category: 'pipe' },
  pipeRightAngle2:     { name: 'Right angle 2',        width: 64,  height: 64,  component: PipeRightAngle2Icon,     type: 'component', color: '#64748b', category: 'pipe' },
  pipeRightAngle3:     { name: 'Right angle 3',        width: 64,  height: 64,  component: PipeRightAngle3Icon,     type: 'component', color: '#64748b', category: 'pipe' },
  pipeRightAngle4:     { name: 'Right angle 4',        width: 64,  height: 64,  component: PipeRightAngle4Icon,     type: 'component', color: '#64748b', category: 'pipe' },
  pipeShortHorizontal: { name: 'Short horizontal pipe', width: 80, height: 24,  component: PipeShortHorizontalIcon, type: 'component', color: '#64748b', category: 'pipe' },
  pipeShortVertical:   { name: 'Short vertical pipe',  width: 24,  height: 80,  component: PipeShortVerticalIcon,   type: 'component', color: '#64748b', category: 'pipe' },
  pipeOpenEnd1:        { name: 'Open end 1',           width: 40,  height: 64,  component: PipeOpenEnd1Icon,        type: 'component', color: '#64748b', category: 'pipe' },
  pipeOpenEnd2:        { name: 'Open end 2',           width: 64,  height: 40,  component: PipeOpenEnd2Icon,        type: 'component', color: '#64748b', category: 'pipe' },
  pipeCutEnd1:         { name: 'Cut end 1',            width: 48,  height: 64,  component: PipeCutEnd1Icon,         type: 'component', color: '#64748b', category: 'pipe' },
  pipeCutEnd2:         { name: 'Cut end 2',            width: 64,  height: 48,  component: PipeCutEnd2Icon,         type: 'component', color: '#64748b', category: 'pipe' }
};

// Component Tag Configuration Modal
function ComponentTagModal({ component, availableTags, onSave, onClose, darkMode }) {
  // Migrate old single-tag format to new multi-tag conditions format
  const migrateConditions = (comp) => {
    if (comp.conditions) return comp.conditions;
    // Migrate from old colorMappings format
    if (comp.tagName && comp.colorMappings?.length) {
      return comp.colorMappings.map(m => ({
        id: Math.random().toString(36).substr(2, 9),
        rules: [{ tagName: comp.tagName, operator: '==', value: m.value }],
        logic: 'AND',
        color: m.color,
        label: m.label
      }));
    }
    return [{ id: Math.random().toString(36).substr(2, 9), rules: [{ tagName: '', operator: '==', value: '' }], logic: 'AND', color: '#ef4444', label: '' }];
  };

  const [label, setLabel] = useState(component?.label || '');
  const [defaultColor, setDefaultColor] = useState(component?.defaultColor || '#64748b');
  const [conditions, setConditions] = useState(migrateConditions(component));
  const [showStatusLabel, setShowStatusLabel] = useState(component?.showStatusLabel !== false);
  const [showTagValue, setShowTagValue] = useState(component?.showTagValue !== false);
  const [displayTrend, setDisplayTrend] = useState(component?.displayTrend || false);

  const theme = darkMode ? {
    bg: '#0f1117', card: '#1a1d29', text: '#e5e7eb', textSecondary: '#9ca3af',
    border: '#374151', accent: '#667eea', hover: '#232837', danger: '#ef4444', success: '#10b981'
  } : {
    bg: '#ffffff', card: '#f9fafb', text: '#111827', textSecondary: '#6b7280',
    border: '#e5e7eb', accent: '#667eea', hover: '#f3f4f6', danger: '#ef4444', success: '#10b981'
  };

  const addCondition = () => {
    setConditions([...conditions, {
      id: Math.random().toString(36).substr(2, 9),
      rules: [{ tagName: '', operator: '==', value: '' }],
      logic: 'AND',
      color: '#3b82f6',
      label: ''
    }]);
  };

  const removeCondition = (idx) => {
    setConditions(conditions.filter((_, i) => i !== idx));
  };

  const updateCondition = (idx, field, value) => {
    const updated = [...conditions];
    updated[idx] = { ...updated[idx], [field]: value };
    setConditions(updated);
  };

  const addRule = (condIdx) => {
    const updated = [...conditions];
    updated[condIdx].rules = [...updated[condIdx].rules, { tagName: '', operator: '==', value: '' }];
    setConditions(updated);
  };

  const removeRule = (condIdx, ruleIdx) => {
    const updated = [...conditions];
    updated[condIdx].rules = updated[condIdx].rules.filter((_, i) => i !== ruleIdx);
    if (updated[condIdx].rules.length === 0) {
      updated[condIdx].rules = [{ tagName: '', operator: '==', value: '' }];
    }
    setConditions(updated);
  };

  const updateRule = (condIdx, ruleIdx, field, value) => {
    const updated = [...conditions];
    updated[condIdx].rules = [...updated[condIdx].rules];
    updated[condIdx].rules[ruleIdx] = { ...updated[condIdx].rules[ruleIdx], [field]: value };
    setConditions(updated);
  };

  const handleSave = () => {
    // Also keep backward-compatible tagName from first rule of first condition
    const firstTag = conditions[0]?.rules?.[0]?.tagName || '';
    // Build backward-compatible colorMappings for simple cases
    const colorMappings = conditions.map(c => ({
      value: c.rules[0]?.value || '',
      color: c.color,
      label: c.label
    }));
    onSave({
      ...component,
      label,
      defaultColor,
      conditions,
      showStatusLabel,
      showTagValue,
      displayTrend,
      tagName: firstTag,
      colorMappings
    });
  };

  const operators = [
    { value: '==', label: '==' },
    { value: '!=', label: '!=' },
    { value: '>', label: '>' },
    { value: '<', label: '<' },
    { value: '>=', label: '>=' },
    { value: '<=', label: '<=' },
    { value: 'contains', label: 'contains' }
  ];

  const inputStyle = { width: '100%', padding: '10px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: '6px', color: theme.text, fontSize: '13px', boxSizing: 'border-box' };
  const selectStyle = { ...inputStyle, cursor: 'pointer' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' }}>
      <div style={{ background: theme.bg, borderRadius: '16px', width: '100%', maxWidth: '720px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `2px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Database size={24} color={theme.accent} />
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: theme.text }}>Configure Component Tag</h3>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: theme.textSecondary }}>Assign tags and define conditional rules</p>
            </div>
          </div>
          <button onClick={onClose} style={{ padding: '8px', background: 'transparent', border: 'none', color: theme.textSecondary, cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {/* Label */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: theme.text, fontWeight: '600' }}>Component Label</label>
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Enter label..." style={{ ...inputStyle, padding: '12px', fontSize: '14px', border: `2px solid ${theme.border}` }} />
          </div>

          {/* Default Color */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: theme.text, fontWeight: '600' }}>Default Color</label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input type="color" value={defaultColor} onChange={(e) => setDefaultColor(e.target.value)} style={{ width: '60px', height: '44px', border: `2px solid ${theme.border}`, borderRadius: '8px', cursor: 'pointer' }} />
              <input type="text" value={defaultColor} onChange={(e) => setDefaultColor(e.target.value)} style={{ flex: 1, padding: '12px', background: theme.card, border: `2px solid ${theme.border}`, borderRadius: '8px', color: theme.text, fontSize: '14px', fontFamily: 'monospace' }} />
            </div>
          </div>

          {/* Display toggles */}
          <div style={{ marginBottom: '24px', display: 'flex', gap: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: theme.text }}>
              <input type="checkbox" checked={showStatusLabel} onChange={(e) => setShowStatusLabel(e.target.checked)} style={{ width: '16px', height: '16px' }} />
              Show status label
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: theme.text }}>
              <input type="checkbox" checked={showTagValue} onChange={(e) => setShowTagValue(e.target.checked)} style={{ width: '16px', height: '16px' }} />
              Show tag value
            </label>
          </div>

          {/* Conditions */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <label style={{ fontSize: '14px', color: theme.text, fontWeight: '600' }}>Conditions</label>
              <button onClick={addCondition} style={{ padding: '8px 16px', background: theme.accent, border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Plus size={16} />Add Condition
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {conditions.map((cond, condIdx) => (
                <div key={cond.id || condIdx} style={{ padding: '16px', background: theme.card, border: `2px solid ${theme.border}`, borderRadius: '10px' }}>
                  {/* Condition header: IF ... THEN color + label */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: theme.accent, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      IF {cond.rules.length > 1 ? `(${cond.logic})` : ''}
                    </span>
                    <button onClick={() => removeCondition(condIdx)} style={{ padding: '6px', background: 'transparent', border: 'none', color: theme.danger, cursor: 'pointer' }}>
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {/* Rules */}
                  {cond.rules.map((rule, ruleIdx) => (
                    <div key={ruleIdx}>
                      {ruleIdx > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
                          <select
                            value={cond.logic}
                            onChange={(e) => updateCondition(condIdx, 'logic', e.target.value)}
                            style={{ padding: '4px 12px', background: theme.accent, border: 'none', borderRadius: '4px', color: 'white', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                          >
                            <option value="AND">AND</option>
                            <option value="OR">OR</option>
                          </select>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <SearchableTagSelect
                          value={rule.tagName}
                          onChange={(val) => updateRule(condIdx, ruleIdx, 'tagName', val)}
                          tags={availableTags}
                          placeholder="Select tag..."
                          darkMode={darkMode}
                          style={{ flex: 2 }}
                        />
                        <select value={rule.operator} onChange={(e) => updateRule(condIdx, ruleIdx, 'operator', e.target.value)} style={{ ...selectStyle, flex: 0, width: '80px', minWidth: '80px' }}>
                          {operators.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                        </select>
                        <input type="text" value={rule.value} onChange={(e) => updateRule(condIdx, ruleIdx, 'value', e.target.value)} placeholder="Value" style={{ ...inputStyle, flex: 1 }} />
                        {cond.rules.length > 1 && (
                          <button onClick={() => removeRule(condIdx, ruleIdx)} style={{ padding: '6px', background: 'transparent', border: 'none', color: theme.danger, cursor: 'pointer', flexShrink: 0 }}>
                            <Minus size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  <button onClick={() => addRule(condIdx)} style={{ marginTop: '10px', padding: '6px 12px', background: theme.hover, border: `1px dashed ${theme.border}`, borderRadius: '6px', color: theme.textSecondary, cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Plus size={14} /> Add Rule
                  </button>

                  {/* THEN: color + label */}
                  <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: `1px solid ${theme.border}` }}>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: theme.success, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px', display: 'block' }}>THEN</span>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <div style={{ width: '60px' }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', color: theme.textSecondary, fontWeight: '600' }}>Color</label>
                        <input type="color" value={cond.color} onChange={(e) => updateCondition(condIdx, 'color', e.target.value)} style={{ width: '100%', height: '38px', border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', color: theme.textSecondary, fontWeight: '600' }}>Label</label>
                        <input type="text" value={cond.label} onChange={(e) => updateCondition(condIdx, 'label', e.target.value)} placeholder="e.g. Fault, Running..." style={inputStyle} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: `2px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={displayTrend}
              onChange={(e) => setDisplayTrend(e.target.checked)}
              style={{ width: '15px', height: '15px' }}
            />
            <span style={{ fontSize: '13px', fontWeight: '600', color: theme.text }}>Display Trend on Click</span>
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={onClose} style={{ padding: '12px 24px', background: theme.card, border: `2px solid ${theme.border}`, borderRadius: '8px', color: theme.text, cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>Cancel</button>
            <button onClick={handleSave} style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}
// Navigation Button Configuration Modal
function NavButtonConfigModal({ component, availablePanels, onSave, onClose, darkMode }) {
  const [label, setLabel] = useState(component?.label || 'Go to Panel');
  const [targetPanelId, setTargetPanelId] = useState(component?.targetPanelId || '');
  const [buttonColor, setButtonColor] = useState(component?.buttonColor || '#667eea');
  const [transparentBg, setTransparentBg] = useState(component?.transparentBg || false);
  const [textColor, setTextColor] = useState(component?.textColor || '#ffffff');
  const [fontSize, setFontSize] = useState(component?.fontSize || 13);
  const [fontWeight, setFontWeight] = useState(component?.fontWeight || '700');
  const [textAlign, setTextAlign] = useState(component?.textAlign || 'center');
  const [displayTrend, setDisplayTrend] = useState(component?.displayTrend || false);

  const theme = darkMode ? {
    bg: '#0f1117', card: '#1a1d29', text: '#e5e7eb', textSecondary: '#9ca3af',
    border: '#374151', accent: '#667eea'
  } : {
    bg: '#ffffff', card: '#f9fafb', text: '#111827', textSecondary: '#6b7280',
    border: '#e5e7eb', accent: '#667eea'
  };

  const inputStyle = { width: '100%', padding: '10px 12px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: '6px', color: theme.text, fontSize: '13px', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: theme.text };

  const previewBg = transparentBg ? 'transparent' : buttonColor;
  const alignMap = { left: 'flex-start', center: 'center', right: 'flex-end' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ background: theme.bg, borderRadius: '16px', width: '460px', maxHeight: '90vh', overflow: 'auto', padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: `1px solid ${theme.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Navigation size={20} color={theme.accent} />
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: theme.text }}>Configure Navigation Button</h3>
          </div>
          <button onClick={onClose} style={{ padding: '6px', background: 'transparent', border: 'none', color: theme.textSecondary, cursor: 'pointer' }}><X size={18} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Label */}
          <div>
            <label style={labelStyle}>Button Label</label>
            <input type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Go to Overview" style={inputStyle} />
          </div>

          {/* Target Panel */}
          <div>
            <label style={labelStyle}>Target Panel</label>
            <select value={targetPanelId} onChange={e => setTargetPanelId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="">-- Select a Panel --</option>
              {availablePanels.map(p => (
                <option key={p.id} value={p.id}>{p.dashboardName} → {p.title}</option>
              ))}
            </select>
            {availablePanels.length === 0 && (
              <p style={{ margin: '6px 0 0', fontSize: '11px', color: theme.textSecondary }}>No SCADA panels found. Create panels in the Dashboard first.</p>
            )}
          </div>

          {/* Colors */}
          <div>
            <label style={labelStyle}>Colors</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ ...labelStyle, fontSize: '11px', fontWeight: '500' }}>Text Color</label>
                <input type="color" value={textColor} onChange={e => setTextColor(e.target.value)} style={{ width: '100%', padding: '4px', height: '36px', border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer', background: theme.bg }} />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: '11px', fontWeight: '500' }}>Background</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <input type="checkbox" id="navTransparentBg" checked={transparentBg} onChange={e => setTransparentBg(e.target.checked)} style={{ width: '14px', height: '14px' }} />
                  <label htmlFor="navTransparentBg" style={{ fontSize: '11px', color: theme.textSecondary, cursor: 'pointer' }}>Transparent</label>
                </div>
                <input type="color" value={buttonColor} onChange={e => setButtonColor(e.target.value)} disabled={transparentBg} style={{ width: '100%', padding: '4px', height: '36px', border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: transparentBg ? 'not-allowed' : 'pointer', opacity: transparentBg ? 0.4 : 1, background: theme.bg }} />
              </div>
            </div>
          </div>

          {/* Text Settings */}
          <div>
            <label style={labelStyle}>Text Settings</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ ...labelStyle, fontSize: '11px', fontWeight: '500' }}>Font Size (px)</label>
                <input type="number" value={fontSize} onChange={e => setFontSize(parseInt(e.target.value) || 13)} min={8} max={48} style={inputStyle} />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: '11px', fontWeight: '500' }}>Font Weight</label>
                <select value={fontWeight} onChange={e => setFontWeight(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="normal">Normal</option>
                  <option value="500">Medium</option>
                  <option value="600">Semibold</option>
                  <option value="700">Bold</option>
                </select>
              </div>
            </div>
            <div style={{ marginTop: '12px' }}>
              <label style={{ ...labelStyle, fontSize: '11px', fontWeight: '500' }}>Text Align</label>
              <div style={{ display: 'flex', gap: '4px' }}>
                {['left', 'center', 'right'].map(a => (
                  <button key={a} onClick={() => setTextAlign(a)} style={{ flex: 1, padding: '8px', border: `1px solid ${textAlign === a ? theme.accent : theme.border}`, borderRadius: '6px', background: textAlign === a ? (darkMode ? '#1e3a5f' : '#dbeafe') : theme.bg, color: textAlign === a ? theme.accent : theme.text, cursor: 'pointer', fontSize: '12px', fontWeight: '500', textTransform: 'capitalize' }}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Preview */}
          <div style={{ padding: '12px', background: theme.card, borderRadius: '8px', border: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'center' }}>
            <div style={{ padding: '8px 20px', background: previewBg, borderRadius: '6px', border: transparentBg ? `1px dashed ${theme.border}` : 'none', color: textColor, fontSize: `${fontSize}px`, fontWeight, display: 'flex', alignItems: 'center', justifyContent: alignMap[textAlign], gap: '6px', minWidth: '120px' }}>
              <Navigation size={fontSize - 2} />
              {label || 'Navigate'}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={displayTrend} onChange={(e) => setDisplayTrend(e.target.checked)} style={{ width: '15px', height: '15px' }} />
            <span style={{ fontSize: '13px', fontWeight: '600', color: theme.text }}>Display Trend on Click</span>
          </label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onClose} style={{ padding: '10px 20px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '8px', color: theme.text, cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Cancel</button>
            <button onClick={() => onSave({ ...component, label, targetPanelId, buttonColor, transparentBg, textColor, fontSize, fontWeight, textAlign, displayTrend })} style={{ padding: '10px 20px', background: theme.accent, border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Line Drawing Config Modal ─────────────────────────────────────────────
function LineConfigModal({ component, onSave, onClose, darkMode }) {
  const [orientation, setOrientation] = useState(component?.orientation || 'horizontal');
  const [lineStyle, setLineStyle]     = useState(component?.lineStyle || 'solid');
  const [lineThickness, setLineThickness] = useState(component?.lineThickness ?? 2);
  const [dotWidth,  setDotWidth]  = useState(component?.dotWidth  ?? 5);
  const [dashWidth, setDashWidth] = useState(component?.dashWidth ?? 10);
  const [gapWidth,  setGapWidth]  = useState(component?.gapWidth  ?? 5);
  const [animEnabled, setAnimEnabled] = useState(component?.animEnabled || false);
  const [animSpeed,   setAnimSpeed]   = useState(component?.animSpeed   ?? 10);
  const [highColor,   setHighColor]   = useState(component?.highColor   || '#000000');
  const [lowColor,    setLowColor]    = useState(component?.lowColor    || '#141212');
  const [borderColor, setBorderColor] = useState(component?.borderColor || '#000000');
  const [borderWidth, setBorderWidth] = useState(component?.borderWidth ?? 0);
  const [compWidth,   setCompWidth]   = useState(component?.width  ?? 310);
  const [compHeight,  setCompHeight]  = useState(component?.height ?? 10);
  const [startMarker, setStartMarker] = useState(component?.startMarker || 'none');
  const [endMarker,   setEndMarker]   = useState(component?.endMarker   || 'none');
  const [opacity,     setOpacity]     = useState(component?.opacity     ?? 1);

  const t = darkMode
    ? { bg: '#0f1117', card: '#1a1d29', text: '#e5e7eb', sub: '#9ca3af', bd: '#374151' }
    : { bg: '#ffffff', card: '#f3f4f6', text: '#111827', sub: '#6b7280', bd: '#e5e7eb' };

  const inp  = { width: '100%', padding: '6px 9px', background: t.card, border: `1px solid ${t.bd}`, borderRadius: 5, color: t.text, fontSize: 12, boxSizing: 'border-box' };
  const row  = { display: 'grid', gridTemplateColumns: '130px 1fr', alignItems: 'center', gap: 8, marginBottom: 7 };
  const lbl  = { fontSize: 12, color: t.text, fontWeight: 500 };

  const pxRow = (label, val, setter, disabled = false) => (
    <div style={{ ...row, opacity: disabled ? 0.4 : 1 }}>
      <span style={lbl}>{label}</span>
      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
        <input type="number" value={val} min={0} disabled={disabled}
          onChange={e => setter(Number(e.target.value))}
          style={{ ...inp, flex: 1, cursor: disabled ? 'not-allowed' : 'auto' }} />
        <span style={{ fontSize: 11, color: t.sub }}>px</span>
      </div>
    </div>
  );

  const colorRow = (label, val, setter) => (
    <div style={row}>
      <span style={lbl}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="color" value={val} onChange={e => setter(e.target.value)}
          style={{ width: 30, height: 30, border: `1px solid ${t.bd}`, borderRadius: '50%', cursor: 'pointer', padding: 1, background: t.card }} />
        <span style={{ fontSize: 12, color: t.text, fontFamily: 'monospace' }}>{val}</span>
      </div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ background: t.bg, borderRadius: 12, width: 360, maxHeight: '90vh', overflowY: 'auto', padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: `1px solid ${t.bd}` }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: t.text }}>Line Properties</h3>

        <div style={row}>
          <span style={lbl}>Orientation</span>
          <select value={orientation} onChange={e => setOrientation(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
            <option value="horizontal">Horizontal</option>
            <option value="vertical">Vertical</option>
          </select>
        </div>

        <div style={row}>
          <span style={lbl}>Line Style</span>
          <select value={lineStyle} onChange={e => setLineStyle(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
            <option value="solid">Solid</option>
            <option value="dotted">Dotted</option>
            <option value="dashed">Dashed</option>
          </select>
        </div>

        {pxRow('Line Thickness', lineThickness, setLineThickness)}
        {lineStyle === 'dotted' && pxRow('Dot Width', dotWidth, setDotWidth)}
        {lineStyle === 'dashed' && pxRow('Dash Width', dashWidth, setDashWidth)}
        {lineStyle === 'dashed' && pxRow('Gap Width', gapWidth, setGapWidth)}

        {(lineStyle === 'dotted' || lineStyle === 'dashed') && (
          <>
            <div style={row}>
              <span style={lbl}>Enable Animation</span>
              <input type="checkbox" checked={animEnabled} onChange={e => setAnimEnabled(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }} />
            </div>
            <div style={{ ...row, opacity: animEnabled ? 1 : 0.4 }}>
              <span style={lbl}>Animation Speed</span>
              <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                <input type="number" value={animSpeed} min={1} max={500} disabled={!animEnabled}
                  onChange={e => setAnimSpeed(Number(e.target.value))}
                  style={{ ...inp, flex: 1, cursor: animEnabled ? 'auto' : 'not-allowed' }} />
                <span style={{ fontSize: 11, color: t.sub }}>px/s</span>
              </div>
            </div>
          </>
        )}

        {colorRow('High Color', highColor, setHighColor)}
        {colorRow('Low Color', lowColor, setLowColor)}
        {colorRow('Border Color', borderColor, setBorderColor)}
        {pxRow('Border Width', borderWidth, setBorderWidth)}
        {pxRow('Component Width', compWidth, setCompWidth)}
        {pxRow('Component Height', compHeight, setCompHeight)}

        <div style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: '14px 0 8px', paddingBottom: 4, borderBottom: `1px solid ${t.bd}` }}>Line Endings</div>
        {[['Start', startMarker, setStartMarker], ['End', endMarker, setEndMarker]].map(([label, val, setter]) => (
          <div key={label} style={row}>
            <span style={lbl}>{label} Marker</span>
            <div style={{ display: 'flex', gap: 3 }}>
              {[
                { key: 'none',    label: 'None',    icon: <line x1="2" y1="8" x2="22" y2="8" stroke={t.text} strokeWidth="2"/> },
                { key: 'arrow',   label: 'Arrow',   icon: <><line x1="2" y1="8" x2="18" y2="8" stroke={t.text} strokeWidth="2"/><polygon points="14,4 22,8 14,12" fill={t.text}/></> },
                { key: 'circle',  label: 'Circle',  icon: <><line x1="2" y1="8" x2="16" y2="8" stroke={t.text} strokeWidth="2"/><circle cx="19" cy="8" r="4" fill="none" stroke={t.text} strokeWidth="1.5"/></> },
                { key: 'diamond', label: 'Diamond', icon: <><line x1="2" y1="8" x2="14" y2="8" stroke={t.text} strokeWidth="2"/><polygon points="18,4 22,8 18,12 14,8" fill="none" stroke={t.text} strokeWidth="1.5"/></> },
              ].map(m => (
                <button key={m.key} title={m.label} onClick={() => setter(m.key)}
                  style={{ width: 32, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: val === m.key ? 'rgba(102,126,234,.25)' : t.card, border: `1px solid ${val === m.key ? '#667eea' : t.bd}`, borderRadius: 4, cursor: 'pointer', padding: 0 }}>
                  <svg width="24" height="16" viewBox="0 0 24 16">{m.icon}</svg>
                </button>
              ))}
            </div>
          </div>
        ))}

        <div style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: '14px 0 8px', paddingBottom: 4, borderBottom: `1px solid ${t.bd}` }}>Opacity</div>
        <div style={row}>
          <span style={lbl}>Opacity</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="range" min={0.05} max={1} step={0.05} value={opacity} onChange={e => setOpacity(parseFloat(e.target.value))}
              style={{ flex: 1, cursor: 'pointer', accentColor: '#667eea' }} />
            <span style={{ fontSize: 11, color: t.sub, minWidth: 30 }}>{Math.round(opacity * 100)}%</span>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 10 }}>
          <button onClick={onClose} style={{ padding: '8px 20px', background: t.card, border: `1px solid ${t.bd}`, borderRadius: 7, color: t.text, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
          <button onClick={() => onSave({ ...component, orientation, lineStyle, lineThickness, dotWidth, dashWidth, gapWidth, animEnabled, animSpeed, highColor, lowColor, borderColor, borderWidth, width: compWidth, height: compHeight, startMarker, endMarker, opacity })}
            style={{ padding: '8px 24px', background: 'linear-gradient(135deg,#667eea,#764ba2)', border: 'none', borderRadius: 7, color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Shape Config Modal ────────────────────────────────────────────────────
function ShapeConfigModal({ component, onSave, onClose, darkMode }) {
  const [width,        setWidth]        = useState(component?.width        ?? 100);
  const [height,       setHeight]       = useState(component?.height       ?? 100);
  const [shapeType,    setShapeType]    = useState(component?.shapeType    || 'rectangle');
  const [cornerRadius, setCornerRadius] = useState(component?.cornerRadius ?? 5);
  const [enableBorder, setEnableBorder] = useState(component?.enableBorder !== false);
  const [borderWidth,  setBorderWidth]  = useState(component?.borderWidth  ?? 2);
  const [fillColor,    setFillColor]    = useState(component?.fillColor    || '#272E35');
  const [borderColor,  setBorderColor]  = useState(component?.borderColor  || '#000000');
  const [fillType,     setFillType]     = useState(component?.fillType     || 'solid');
  const [gradientColor2, setGradientColor2] = useState(component?.gradientColor2 || '#667eea');
  const [gradientAngle, setGradientAngle] = useState(component?.gradientAngle ?? 180);
  const [opacity,      setOpacity]      = useState(component?.opacity      ?? 1);

  const t = darkMode
    ? { bg: '#0f1117', card: '#1a1d29', text: '#e5e7eb', sub: '#9ca3af', bd: '#374151', accent: '#667eea' }
    : { bg: '#ffffff', card: '#f3f4f6', text: '#111827', sub: '#6b7280', bd: '#e5e7eb', accent: '#667eea' };

  const inp = { width: '100%', padding: '6px 9px', background: t.card, border: `1px solid ${t.bd}`, borderRadius: 5, color: t.text, fontSize: 12, boxSizing: 'border-box' };
  const sec = (title) => <div style={{ fontSize: 13, fontWeight: 700, color: t.text, margin: '14px 0 8px', paddingBottom: 4, borderBottom: `1px solid ${t.bd}` }}>{title}</div>;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ background: t.bg, borderRadius: 12, width: 360, maxHeight: '90vh', overflowY: 'auto', padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: `1px solid ${t.bd}` }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: t.text }}>Rectangle Configuration</h3>

        {sec('Dimensions')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[['Width', width, setWidth], ['Height', height, setHeight]].map(([l, v, s]) => (
            <div key={l}>
              <div style={{ fontSize: 11, fontWeight: 600, color: t.sub, marginBottom: 3 }}>{l}:</div>
              <input type="number" value={v} min={4} onChange={e => s(Number(e.target.value))} style={inp} />
            </div>
          ))}
        </div>

        {sec('Shape & Style')}
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.sub, marginBottom: 5 }}>Shape Type:</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, marginBottom: 10 }}>
            {[
              { key: 'rectangle',     label: 'Rect' },
              { key: 'roundedRect',   label: 'Rounded' },
              { key: 'square',        label: 'Square' },
              { key: 'circle',        label: 'Circle' },
              { key: 'ellipse',       label: 'Ellipse' },
              { key: 'triangle',      label: 'Triangle' },
              { key: 'diamond',       label: 'Diamond' },
              { key: 'pentagon',      label: 'Pentagon' },
              { key: 'hexagon',       label: 'Hexagon' },
              { key: 'star',          label: 'Star' },
              { key: 'arrow',         label: 'Arrow' },
              { key: 'cross',         label: 'Cross' },
              { key: 'parallelogram', label: 'Parallel' },
              { key: 'trapezoid',     label: 'Trapezoid' },
            ].map(s => (
              <button key={s.key} onClick={() => setShapeType(s.key)}
                style={{ padding: '5px 2px', background: shapeType === s.key ? (t.accent + '33') : t.card, border: `1px solid ${shapeType === s.key ? t.accent : t.bd}`, borderRadius: 5, color: shapeType === s.key ? t.accent : t.text, fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                {s.label}
              </button>
            ))}
          </div>
          {(shapeType === 'rectangle' || shapeType === 'roundedRect' || shapeType === 'square') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: t.sub }}>Corner Radius:</span>
              <input type="number" value={cornerRadius} min={0} onChange={e => setCornerRadius(Number(e.target.value))}
                style={{ ...inp, width: 80 }} />
            </div>
          )}
        </div>

        {sec('Border')}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: t.text }}>
            <input type="checkbox" checked={enableBorder} onChange={e => setEnableBorder(e.target.checked)}
              style={{ width: 14, height: 14, cursor: 'pointer' }} />
            Enable Border
          </label>
          {enableBorder && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, color: t.sub, fontWeight: 600 }}>Border Width:</span>
              <input type="number" value={borderWidth} min={0} onChange={e => setBorderWidth(Number(e.target.value))}
                style={{ ...inp, width: 60 }} />
            </div>
          )}
        </div>

        {sec('Fill')}
        <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
          {['solid', 'linear', 'radial'].map(ft => (
            <button key={ft} onClick={() => setFillType(ft)}
              style={{ flex: 1, padding: '5px 2px', background: fillType === ft ? (t.accent + '33') : t.card, border: `1px solid ${fillType === ft ? t.accent : t.bd}`, borderRadius: 5, color: fillType === ft ? t.accent : t.text, fontSize: 10, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
              {ft}
            </button>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: fillType !== 'solid' ? '1fr 1fr' : '1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: t.sub, marginBottom: 5 }}>{fillType === 'solid' ? 'Fill Color' : 'Color 1'}:</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="color" value={fillColor} onChange={e => setFillColor(e.target.value)}
                style={{ width: 36, height: 36, border: `1px solid ${t.bd}`, borderRadius: 4, cursor: 'pointer', padding: 2, background: t.card }} />
              <span style={{ fontSize: 11, color: t.text, fontFamily: 'monospace' }}>{fillColor}</span>
            </div>
          </div>
          {fillType !== 'solid' && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: t.sub, marginBottom: 5 }}>Color 2:</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="color" value={gradientColor2} onChange={e => setGradientColor2(e.target.value)}
                  style={{ width: 36, height: 36, border: `1px solid ${t.bd}`, borderRadius: 4, cursor: 'pointer', padding: 2, background: t.card }} />
                <span style={{ fontSize: 11, color: t.text, fontFamily: 'monospace' }}>{gradientColor2}</span>
              </div>
            </div>
          )}
        </div>
        {fillType === 'linear' && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: t.sub }}>Angle</span>
              <span style={{ fontSize: 11, color: t.sub }}>{gradientAngle}°</span>
            </div>
            <input type="range" min={0} max={360} step={15} value={gradientAngle} onChange={e => setGradientAngle(Number(e.target.value))}
              style={{ width: '100%', cursor: 'pointer', accentColor: t.accent }} />
          </div>
        )}
        {/* Gradient preview */}
        {fillType !== 'solid' && (
          <div style={{ height: 24, borderRadius: 4, border: `1px solid ${t.bd}`, marginBottom: 10,
            background: fillType === 'linear'
              ? `linear-gradient(${gradientAngle}deg, ${fillColor}, ${gradientColor2})`
              : `radial-gradient(circle, ${fillColor}, ${gradientColor2})`
          }} />
        )}

        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.sub, marginBottom: 5 }}>Border Color:</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="color" value={borderColor} onChange={e => setBorderColor(e.target.value)}
              style={{ width: 36, height: 36, border: `1px solid ${t.bd}`, borderRadius: 4, cursor: 'pointer', padding: 2, background: t.card }} />
            <span style={{ fontSize: 11, color: t.text, fontFamily: 'monospace' }}>{borderColor}</span>
          </div>
        </div>

        {sec('Opacity')}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: t.sub }}>Opacity</span>
            <span style={{ fontSize: 11, color: t.sub }}>{Math.round(opacity * 100)}%</span>
          </div>
          <input type="range" min={0.05} max={1} step={0.05} value={opacity} onChange={e => setOpacity(parseFloat(e.target.value))}
            style={{ width: '100%', cursor: 'pointer', accentColor: t.accent }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20, gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '9px', background: t.card, border: `1px solid ${t.bd}`, borderRadius: 8, color: t.text, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
          <button onClick={() => onSave({ ...component, width, height, shapeType, cornerRadius, enableBorder, borderWidth, fillColor, borderColor, fillType, gradientColor2, gradientAngle, opacity })}
            style={{ flex: 1, padding: '9px', background: t.card, border: `1px solid ${t.bd}`, borderRadius: 8, color: t.text, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Symbols Panel categories ───────────────────────────────────────────────
const CATEGORIES = [
  { key: 'pump',         label: 'Pump'          },
  { key: 'sov',          label: 'SOV'           },
  { key: 'controlValve', label: 'Control Valve' },
  { key: 'flowmeter',    label: 'Flow Meter'    },
  { key: 'heater',       label: 'Heater'        },
  { key: 'stirrer',      label: 'Stirrer'       },
  { key: 'boiler',       label: 'Boiler'        },
  { key: 'cooler',       label: 'Cooler'        },
  { key: 'tank',         label: 'Tank'          },
  { key: 'arrow',        label: 'Arrow'         },
  { key: 'pipe',         label: 'Pipe'          },
];

function SymbolsPanel({ SVG_COMPONENTS, componentToAdd, selectedTool, setSelectedTool, setComponentToAdd, setCustomComponentToAdd, setDrawingLine, setPendingShapeType, darkMode, customComponents, onAddCustomComponent, onRemoveCustomComponent, onSelectCustomForPlacement }) {
  const [search, setSearch]       = React.useState('');
  const [filterCat, setFilterCat] = React.useState('all');
  const [collapsed, setCollapsed] = React.useState({});

  const BG   = darkMode ? '#111827' : '#f9fafb';
  const BG2  = darkMode ? '#0c111a' : '#ffffff';
  const BD   = darkMode ? '#1a2740' : '#e5e7eb';
  const TEXT = darkMode ? '#dde6f0' : '#111827';
  const TSUB = darkMode ? '#8b9dc3' : '#6b7280';

  const METALLIC_GRAY = '#8a939e';

  const toggleCat = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const allEntries = Object.entries(SVG_COMPONENTS);
  const filtered = allEntries.filter(([key, comp]) => {
    const matchSearch = !search || comp.name.toLowerCase().includes(search.toLowerCase()) || key.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'all' || comp.category === filterCat;
    return matchSearch && matchCat;
  });

  const grouped = {};
  filtered.forEach(([key, comp]) => {
    const cat = comp.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push([key, comp]);
  });

  const catsToShow = filterCat === 'all'
    ? CATEGORIES.filter(c => grouped[c.key]?.length > 0)
    : CATEGORIES.filter(c => c.key === filterCat && grouped[c.key]?.length > 0);

  return (
    <div style={{
      width: '210px', flexShrink: 0,
      height: '100%',
      background: BG,
      borderLeft: `1px solid ${BD}`,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '10px 12px 8px', borderBottom: `1px solid ${BD}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: '700', color: TEXT, letterSpacing: '.2px' }}>Symbols</span>
        </div>
        {/* Filter + Search */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <select
            value={filterCat}
            onChange={e => setFilterCat(e.target.value)}
            style={{
              width: '54px', padding: '4px 6px',
              background: BG2, border: `1px solid ${BD}`,
              borderRadius: '5px', color: TEXT, fontSize: '10px',
              cursor: 'pointer', outline: 'none', flexShrink: 0,
            }}
          >
            <option value="all">All</option>
            {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <div style={{ position: 'relative', flex: 1 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={TSUB} strokeWidth="2" style={{ position: 'absolute', left: '7px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search symbols..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '4px 6px 4px 22px', boxSizing: 'border-box',
                background: BG2, border: `1px solid ${BD}`,
                borderRadius: '5px', color: TEXT, fontSize: '10px',
                outline: 'none',
              }}
            />
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* ── Tools Section (Text + Nav) ── */}
        {(() => {
          const isCollapsed = !!collapsed['_tools'];
          const tools = [
            { key: 'textbox', label: 'Text', shortcut: 'T', svg: <><rect x="4" y="6" width="32" height="28" rx="3" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.2"/><text x="20" y="25" textAnchor="middle" fill={METALLIC_GRAY} fontSize="14" fontWeight="700" fontFamily="sans-serif">T</text></> },
            { key: 'navButton', label: 'Nav Button', shortcut: null, svg: <><rect x="3" y="10" width="34" height="20" rx="4" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.2"/><polygon points="28,20 34,16 34,24" fill={METALLIC_GRAY}/><line x1="8" y1="20" x2="30" y2="20" stroke={METALLIC_GRAY} strokeWidth="1.2"/></> },
          ];
          return (
            <div>
              <button onClick={() => toggleCat('_tools')} style={{ width: '100%', padding: '7px 10px', background: 'transparent', border: 'none', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: TEXT, fontSize: '11px', fontWeight: '600', borderBottom: `1px solid ${BD}` }}>
                <span style={{ fontSize: '9px', color: TSUB, transition: 'transform .15s', display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'none' }}>▼</span>
                Tools
                <span style={{ fontSize: '9px', color: TSUB, fontWeight: '400', marginLeft: 'auto' }}>{tools.length}</span>
              </button>
              {!isCollapsed && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px', padding: '6px 6px', borderBottom: `1px solid ${BD}` }}>
                  {tools.map(t => {
                    const isActive = selectedTool === t.key;
                    return (
                      <button
                        key={t.key}
                        title={t.shortcut ? `${t.label} (${t.shortcut})` : t.label}
                        onClick={() => { setSelectedTool(t.key); setComponentToAdd(null); setCustomComponentToAdd(null); setDrawingLine(null); }}
                        style={{ padding: '6px 4px 5px', background: isActive ? 'rgba(102,126,234,.25)' : 'transparent', border: `1px solid ${isActive ? '#667eea55' : 'transparent'}`, borderRadius: '6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', transition: 'all .12s' }}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,.05)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = isActive ? 'rgba(102,126,234,.25)' : 'transparent'; }}
                      >
                        <div style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="34" height="34" viewBox="0 0 40 40">{t.svg}</svg>
                        </div>
                        <span style={{ fontSize: '9px', color: isActive ? '#667eea' : TEXT, fontWeight: '500', lineHeight: 1.2 }}>{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── LED Section ── */}
        {(() => {
          const isCollapsed = !!collapsed['_led'];
          const isActive = selectedTool === 'led';
          return (
            <div>
              <button onClick={() => toggleCat('_led')} style={{ width: '100%', padding: '7px 10px', background: 'transparent', border: 'none', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: TEXT, fontSize: '11px', fontWeight: '600', borderBottom: `1px solid ${BD}` }}>
                <span style={{ fontSize: '9px', color: TSUB, transition: 'transform .15s', display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'none' }}>▼</span>
                LED Indicators
                <span style={{ fontSize: '9px', color: TSUB, fontWeight: '400', marginLeft: 'auto' }}>1</span>
              </button>
              {!isCollapsed && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px', padding: '6px 6px', borderBottom: `1px solid ${BD}` }}>
                  <button
                    title="LED Indicator (D)"
                    onClick={() => { setSelectedTool('led'); setComponentToAdd(null); setCustomComponentToAdd(null); setDrawingLine(null); }}
                    style={{ padding: '6px 4px 5px', background: isActive ? 'rgba(102,126,234,.25)' : 'transparent', border: `1px solid ${isActive ? '#667eea55' : 'transparent'}`, borderRadius: '6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', transition: 'all .12s' }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isActive ? 'rgba(102,126,234,.25)' : 'transparent'; }}
                  >
                    <div style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="32" height="32" viewBox="0 0 40 40">
                        <circle cx="20" cy="20" r="14" fill="#64748b" stroke="rgba(255,255,255,0.15)" strokeWidth="2"/>
                        <circle cx="20" cy="20" r="8" fill="#94a3b8" opacity="0.5"/>
                        <circle cx="16" cy="16" r="3" fill="rgba(255,255,255,0.3)"/>
                      </svg>
                    </div>
                    <span style={{ fontSize: '9px', color: isActive ? '#667eea' : TEXT, fontWeight: '500', lineHeight: 1.2 }}>LED</span>
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Animated Pipes Section ── */}
        {(() => {
          const isCollapsed = !!collapsed['_animPipes'];
          const isActive = selectedTool === 'pipe';
          const pipeVariants = [
            { label: 'Draw Pipe', desc: 'Click-to-draw animated pipe (L)', icon: 'single' },
          ];
          return (
            <div>
              <button onClick={() => toggleCat('_animPipes')} style={{ width: '100%', padding: '7px 10px', background: 'transparent', border: 'none', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: TEXT, fontSize: '11px', fontWeight: '600', borderBottom: `1px solid ${BD}` }}>
                <span style={{ fontSize: '9px', color: TSUB, transition: 'transform .15s', display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'none' }}>▼</span>
                Animated Pipes
                <span style={{ fontSize: '9px', color: TSUB, fontWeight: '400', marginLeft: 'auto' }}>1</span>
              </button>
              {!isCollapsed && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px', padding: '6px 6px', borderBottom: `1px solid ${BD}` }}>
                  <button
                    title="Draw Animated Pipe (L)"
                    onClick={() => { setSelectedTool('pipe'); setComponentToAdd(null); setCustomComponentToAdd(null); setDrawingLine(null); }}
                    style={{ padding: '6px 4px 5px', background: isActive ? 'rgba(102,126,234,.25)' : 'transparent', border: `1px solid ${isActive ? '#667eea55' : 'transparent'}`, borderRadius: '6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', transition: 'all .12s' }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isActive ? 'rgba(102,126,234,.25)' : 'transparent'; }}
                  >
                    <div style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="38" height="38" viewBox="0 0 44 44">
                        <line x1="4" y1="22" x2="40" y2="22" stroke="#3b82f6" strokeWidth="6" strokeLinecap="round"/>
                        <line x1="4" y1="22" x2="40" y2="22" stroke="#60a5fa" strokeWidth="3" strokeLinecap="round" strokeDasharray="6 4">
                          <animate attributeName="stroke-dashoffset" from="0" to="-20" dur="1s" repeatCount="indefinite"/>
                        </line>
                      </svg>
                    </div>
                    <span style={{ fontSize: '9px', color: isActive ? '#667eea' : TEXT, fontWeight: '500', lineHeight: 1.2 }}>Pipe</span>
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Lines Section ── */}
        {(() => {
          const isCollapsed = !!collapsed['_lines'];
          const isActive = selectedTool === 'drawLine';
          return (
            <div>
              <button onClick={() => toggleCat('_lines')} style={{ width: '100%', padding: '7px 10px', background: 'transparent', border: 'none', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: TEXT, fontSize: '11px', fontWeight: '600', borderBottom: `1px solid ${BD}` }}>
                <span style={{ fontSize: '9px', color: TSUB, transition: 'transform .15s', display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'none' }}>▼</span>
                Lines
                <span style={{ fontSize: '9px', color: TSUB, fontWeight: '400', marginLeft: 'auto' }}>3</span>
              </button>
              {!isCollapsed && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px', padding: '6px 6px', borderBottom: `1px solid ${BD}` }}>
                  {/* Solid Line */}
                  <button
                    title="Solid Line"
                    onClick={() => { setSelectedTool('drawLine'); setComponentToAdd(null); setCustomComponentToAdd(null); setDrawingLine(null); }}
                    style={{ padding: '6px 4px 5px', background: isActive ? 'rgba(102,126,234,.25)' : 'transparent', border: `1px solid ${isActive ? '#667eea55' : 'transparent'}`, borderRadius: '6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', transition: 'all .12s' }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isActive ? 'rgba(102,126,234,.25)' : 'transparent'; }}
                  >
                    <div style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="38" height="38" viewBox="0 0 44 44">
                        <line x1="4" y1="22" x2="40" y2="22" stroke={METALLIC_GRAY} strokeWidth="2"/>
                      </svg>
                    </div>
                    <span style={{ fontSize: '9px', color: TEXT, fontWeight: '500', lineHeight: 1.2 }}>Solid</span>
                  </button>
                  {/* Dashed Line */}
                  <button
                    title="Dashed Line"
                    onClick={() => { setSelectedTool('drawLine'); setComponentToAdd(null); setCustomComponentToAdd(null); setDrawingLine(null); }}
                    style={{ padding: '6px 4px 5px', background: 'transparent', border: '1px solid transparent', borderRadius: '6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', transition: 'all .12s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="38" height="38" viewBox="0 0 44 44">
                        <line x1="4" y1="22" x2="40" y2="22" stroke={METALLIC_GRAY} strokeWidth="2" strokeDasharray="6 4"/>
                      </svg>
                    </div>
                    <span style={{ fontSize: '9px', color: TEXT, fontWeight: '500', lineHeight: 1.2 }}>Dashed</span>
                  </button>
                  {/* Dotted Line */}
                  <button
                    title="Dotted Line"
                    onClick={() => { setSelectedTool('drawLine'); setComponentToAdd(null); setCustomComponentToAdd(null); setDrawingLine(null); }}
                    style={{ padding: '6px 4px 5px', background: 'transparent', border: '1px solid transparent', borderRadius: '6px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', transition: 'all .12s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="38" height="38" viewBox="0 0 44 44">
                        <line x1="4" y1="22" x2="40" y2="22" stroke={METALLIC_GRAY} strokeWidth="2" strokeDasharray="2 4" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <span style={{ fontSize: '9px', color: TEXT, fontWeight: '500', lineHeight: 1.2 }}>Dotted</span>
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Shapes Section ── */}
        {(() => {
          const isCollapsed = !!collapsed['_shapes'];
          const SHAPES = [
            { key: 'rectangle',     label: 'Rectangle',  svg: <rect x="4" y="8" width="32" height="24" rx="1" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.5"/> },
            { key: 'roundedRect',   label: 'Rounded',    svg: <rect x="4" y="8" width="32" height="24" rx="6" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.5"/> },
            { key: 'square',        label: 'Square',     svg: <rect x="6" y="6" width="28" height="28" rx="1" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.5"/> },
            { key: 'circle',        label: 'Circle',     svg: <circle cx="20" cy="20" r="14" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.5"/> },
            { key: 'ellipse',       label: 'Ellipse',    svg: <ellipse cx="20" cy="20" rx="16" ry="10" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.5"/> },
            { key: 'triangle',      label: 'Triangle',   svg: <polygon points="20,4 36,36 4,36" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.5"/> },
            { key: 'diamond',       label: 'Diamond',    svg: <polygon points="20,4 36,20 20,36 4,20" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.5"/> },
            { key: 'pentagon',      label: 'Pentagon',    svg: <polygon points="20,4 37,16 30,36 10,36 3,16" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.5"/> },
            { key: 'hexagon',       label: 'Hexagon',    svg: <polygon points="10,4 30,4 38,20 30,36 10,36 2,20" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.5"/> },
            { key: 'star',          label: 'Star',        svg: <polygon points="20,4 24,15 36,15 27,23 30,35 20,28 10,35 13,23 4,15 16,15" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.5"/> },
            { key: 'arrow',         label: 'Arrow',       svg: <polygon points="2,14 24,14 24,6 38,20 24,34 24,26 2,26" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.5"/> },
            { key: 'cross',         label: 'Cross',       svg: <polygon points="14,4 26,4 26,14 36,14 36,26 26,26 26,36 14,36 14,26 4,26 4,14 14,14" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.5"/> },
            { key: 'parallelogram', label: 'Parallel',    svg: <polygon points="10,6 38,6 30,34 2,34" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.5"/> },
            { key: 'trapezoid',     label: 'Trapezoid',   svg: <polygon points="8,6 32,6 38,34 2,34" fill="none" stroke={METALLIC_GRAY} strokeWidth="1.5"/> },
          ];
          return (
            <div>
              <button onClick={() => toggleCat('_shapes')} style={{ width: '100%', padding: '7px 10px', background: 'transparent', border: 'none', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: TEXT, fontSize: '11px', fontWeight: '600', borderBottom: `1px solid ${BD}` }}>
                <span style={{ fontSize: '9px', color: TSUB, transition: 'transform .15s', display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'none' }}>▼</span>
                Shapes
                <span style={{ fontSize: '9px', color: TSUB, fontWeight: '400', marginLeft: 'auto' }}>{SHAPES.length}</span>
              </button>
              {!isCollapsed && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px', padding: '6px 6px', borderBottom: `1px solid ${BD}` }}>
                  {SHAPES.map(shape => {
                    const isActive = selectedTool === 'shape' && componentToAdd === null;
                    return (
                      <button
                        key={shape.key}
                        title={shape.label}
                        onClick={() => {
                          setPendingShapeType(shape.key);
                          setSelectedTool('shape');
                          setComponentToAdd(null);
                          setCustomComponentToAdd(null);
                          setDrawingLine(null);
                        }}
                        style={{
                          padding: '6px 4px 5px',
                          background: 'transparent',
                          border: '1px solid transparent',
                          borderRadius: '6px', cursor: 'pointer',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
                          transition: 'all .12s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,.05)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <svg width="34" height="34" viewBox="0 0 40 40">{shape.svg}</svg>
                        </div>
                        <span style={{ fontSize: '9px', color: TEXT, fontWeight: '500', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', textAlign: 'center', maxWidth: '56px' }}>{shape.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* Category sections */}
        {catsToShow.map(cat => {
          const items = grouped[cat.key] || [];
          const isCollapsed = !!collapsed[cat.key];
          return (
            <div key={cat.key}>
              {/* Category header */}
              <button
                onClick={() => toggleCat(cat.key)}
                style={{
                  width: '100%', padding: '7px 10px',
                  background: 'transparent', border: 'none',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  cursor: 'pointer', color: TEXT, fontSize: '11px', fontWeight: '600',
                  borderBottom: `1px solid ${BD}`,
                }}
              >
                <span style={{ fontSize: '9px', color: TSUB, transition: 'transform .15s', display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'none' }}>▼</span>
                {cat.label}
                <span style={{ fontSize: '9px', color: TSUB, fontWeight: '400', marginLeft: 'auto' }}>{items.length}</span>
              </button>

              {/* 3-column grid */}
              {!isCollapsed && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px', padding: '6px 6px', borderBottom: `1px solid ${BD}` }}>
                  {items.map(([key, comp]) => {
                    const isActive = componentToAdd === key;
                    const PaletteComp = comp.component;
                    const color = isActive ? '#ffffff' : METALLIC_GRAY;
                    return (
                      <button
                        key={key}
                        title={comp.name}
                        draggable
                        onDragStart={e => {
                          e.dataTransfer.effectAllowed = 'copy';
                          e.dataTransfer.setData('svgSymbol', key);
                        }}
                        onClick={() => {
                          setSelectedTool('component');
                          setComponentToAdd(key);
                          setCustomComponentToAdd(null);
                          setDrawingLine(null);
                        }}
                        style={{
                          padding: '6px 4px 5px',
                          background: isActive ? 'rgba(102,126,234,.25)' : 'transparent',
                          border: `1px solid ${isActive ? '#667eea55' : 'transparent'}`,
                          borderRadius: '6px',
                          cursor: 'grab',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
                          transition: 'all .12s',
                        }}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,.05)'; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <PaletteComp
                            width={38} height={38}
                            startColor={color}
                            midColor={lightenColor(color, 60)}
                            endColor={darkenColor(color, 20)}
                          />
                        </div>
                        <span style={{
                          fontSize: '9px', color: isActive ? '#667eea' : TEXT,
                          fontWeight: '500', lineHeight: 1.2,
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap', width: '100%', textAlign: 'center',
                          maxWidth: '56px',
                        }}>{comp.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Custom Components section */}
        <div style={{ borderBottom: `1px solid ${BD}` }}>
          <button
            onClick={() => toggleCat('_custom')}
            style={{
              width: '100%', padding: '7px 10px',
              background: 'transparent', border: 'none',
              display: 'flex', alignItems: 'center', gap: '6px',
              cursor: 'pointer', color: TEXT, fontSize: '11px', fontWeight: '600',
              borderBottom: `1px solid ${BD}`,
            }}
          >
            <span style={{ fontSize: '9px', color: TSUB, transition: 'transform .15s', display: 'inline-block', transform: collapsed['_custom'] ? 'rotate(-90deg)' : 'none' }}>▼</span>
            Custom Images
            <span style={{ fontSize: '9px', color: TSUB, fontWeight: '400', marginLeft: 'auto' }}>{customComponents?.length || 0}</span>
          </button>
          {!collapsed['_custom'] && (
            <div style={{ padding: '8px' }}>
              <CustomImageUploader
                customComponents={customComponents || []}
                onAddComponent={onAddCustomComponent}
                onRemoveComponent={onRemoveCustomComponent}
                onSelectForPlacement={onSelectCustomForPlacement}
                darkMode={darkMode}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
export default function ScadaDesignerPro({ config, onSave, onAutoSave, onClose, darkMode, plantId, availablePanels = [] }) {
  const [components, setComponents] = useState(config?.scadaElements || config?.components || []);  // ✅ Try scadaElements first, fallback to components
  const [lines, setLines] = useState(config?.scadaConnections || config?.lines || []);  // ✅ Try scadaConnections first, fallback to lines
  const [selectedTool, setSelectedTool] = useState('select');
  const [pendingShapeType, setPendingShapeType] = useState('rectangle');
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedLine, setSelectedLine] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]); // Multiple selection
  const [draggingComponent, setDraggingComponent] = useState(null);
  const [drawingLine, setDrawingLine] = useState(null);
  const [editingLine, setEditingLine] = useState(null); // Which line is being edited
  const [draggingLinePoint, setDraggingLinePoint] = useState(null); // Which point is being dragged
  const [draggingLine, setDraggingLine] = useState(null); // Whole-pipe drag
  const [zoom, setZoom] = useState(config?.scadaConfig?.zoom || 1);
  const [pan, setPan] = useState({ x: config?.scadaConfig?.panX || 0, y: config?.scadaConfig?.panY || 0 });
  // Canonical canvas size — configurable via resolution presets.
  // This keeps component positions consistent across all machines/zoom levels.
  const [canonicalWidth, setCanonicalWidth]   = useState(config?.scadaConfig?.canvasWidth  || 1920);
  const [canonicalHeight, setCanonicalHeight] = useState(config?.scadaConfig?.canvasHeight || 1080);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [showGrid, setShowGrid] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [gridSize] = useState(10);
  const [componentToAdd, setComponentToAdd] = useState(null);
  const [lineColor, setLineColor] = useState('#3b82f6');
  const [lineWidth, setLineWidth] = useState(3);
  const [lineStyle, setLineStyle] = useState('solid');
  const [saved, setSaved] = useState(false);
  const [saveToast, setSaveToast] = useState(null); // { message }
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  const showSaveToast = (message = 'Changes saved!') => {
    setSaveToast({ message });
    setTimeout(() => setSaveToast(null), 2500);
  };
  const [diagramTitle, setDiagramTitle] = useState(config?.title || 'SCADA Diagram');
  const [refreshInterval, setRefreshInterval] = useState(Number(config?.refreshInterval) || 2000);
  const [showTagModal, setShowTagModal] = useState(false);
  const [editingComponentTag, setEditingComponentTag] = useState(null);
  const [showPumpModal, setShowPumpModal] = useState(false);
  const [editingPumpComponent, setEditingPumpComponent] = useState(null);
  const [showValveModal, setShowValveModal] = useState(false);
  const [editingValveComponent, setEditingValveComponent] = useState(null);
  const [showControlValveModal, setShowControlValveModal] = useState(false);
  const [editingControlValveComponent, setEditingControlValveComponent] = useState(null);
  const [showLineModal,  setShowLineModal]  = useState(false);
  const [editingLineComp, setEditingLineComp] = useState(null);
  const [showShapeModal, setShowShapeModal] = useState(false);
  const [editingShapeComp, setEditingShapeComp] = useState(null);
  const [showLedModal, setShowLedModal] = useState(false);
  const [editingLedComponent, setEditingLedComponent] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagValues, setTagValues] = useState({});
  const [selectionBox, setSelectionBox] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const historyRef = useRef(history);
  const historyIndexRef = useRef(historyIndex);
  historyRef.current = history;
  historyIndexRef.current = historyIndex;
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showPropsPanel, setShowPropsPanel] = useState(false);
  const [showSymbols, setShowSymbols] = useState(true);
  const [lockedComponents, setLockedComponents] = useState(new Set());
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // 'saving' | 'saved' | null
  const autoSaveTimer = useRef(null);
  // Drag performance refs — avoid setComponents on every mousemove frame
  const isDraggingRef = useRef(false);   // true while mouse is held during a drag
  const dragRafRef   = useRef(null);     // pending requestAnimationFrame id
  const pendingDragRef = useRef(null);   // latest computed positions, consumed by RAF

  // New state for enhanced features
  const [backgroundImage, setBackgroundImage] = useState(config?.scadaConfig?.backgroundImage || null);
  const [backgroundOpacity, setBackgroundOpacity] = useState(config?.scadaConfig?.backgroundOpacity || 0.3);
  const [backgroundFit, setBackgroundFit] = useState(config?.scadaConfig?.backgroundFit || 'contain');
  const [backgroundAlignment, setBackgroundAlignment] = useState(config?.scadaConfig?.backgroundAlignment || 'xMidYMid');
  const [backgroundTileSize, setBackgroundTileSize] = useState(config?.scadaConfig?.backgroundTileSize || 200);
  const [backgroundColor, setBackgroundColor] = useState(config?.scadaConfig?.backgroundColor || '');
  const [customComponents, setCustomComponents] = useState(config?.customComponents || []);
  const [resizingComponent, setResizingComponent] = useState(null);
  const [customComponentToAdd, setCustomComponentToAdd] = useState(null);
  const [showTextBoxModal, setShowTextBoxModal] = useState(false);
  const [editingTextBox, setEditingTextBox] = useState(null);
  const [showPipeModal, setShowPipeModal] = useState(false);
  const [editingPipe, setEditingPipe] = useState(null);
  const [showNavButtonModal, setShowNavButtonModal] = useState(false);
  const [editingNavButton, setEditingNavButton] = useState(null);
  const [pipeConfig, setPipeConfig] = useState({
    width: DEFAULT_PIPE.width,
    color: DEFAULT_PIPE.color,
    style: DEFAULT_PIPE.style,
    flowEnabled: DEFAULT_PIPE.flowEnabled,
    flowSpeed: DEFAULT_PIPE.flowSpeed,
    flowDirection: DEFAULT_PIPE.flowDirection,
    flowColor: DEFAULT_PIPE.flowColor
  });

  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);

  const theme = darkMode ? {
    bg: '#0a0b0f',
    card: '#13141a',
    hover: '#1a1c24',
    text: '#e5e7eb',
    textSecondary: '#9ca3af',
    border: '#242731',
    accent: '#5b68ea',
    success: '#10b981',
    danger: '#ef4444'
  } : {
    bg: '#fafafa',
    card: '#ffffff',
    hover: '#f5f5f5',
    text: '#111827',
    textSecondary: '#6b7280',
    border: '#e5e7eb',
    accent: '#5b68ea',
    success: '#10b981',
    danger: '#ef4444'
  };

  // Snap to grid function
  const snapToGridFn = (value) => {
    if (!snapToGrid) return value;
    return Math.round(value / gridSize) * gridSize;
  };

  // Convert screen mouse event to canvas coordinates.
  // With viewBox + preserveAspectRatio="xMidYMid meet", the SVG content is
  // scaled uniformly and centered — we must account for the letterbox offset.
  const screenToCanvas = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = rect.width / canonicalWidth;
    const scaleY = rect.height / canonicalHeight;
    const scale = Math.min(scaleX, scaleY); // "meet" uses the smaller scale
    const renderedW = canonicalWidth * scale;
    const renderedH = canonicalHeight * scale;
    const offsetX = (rect.width - renderedW) / 2;  // letterbox padding
    const offsetY = (rect.height - renderedH) / 2;
    const svgX = (e.clientX - rect.left - offsetX) / scale;
    const svgY = (e.clientY - rect.top - offsetY) / scale;
    return {
      x: (svgX - pan.x) / zoom,
      y: (svgY - pan.y) / zoom,
      svgX, svgY, rect,
    };
  };

  // Save state to history for undo/redo
  const saveToHistory = () => {
    const newState = { components: [...components], lines: [...lines] };
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newState);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // Undo/Redo — use refs to always access latest history state
  const handleUndo = useCallback(() => {
    const idx = historyIndexRef.current;
    const hist = historyRef.current;
    if (idx > 0) {
      const prevState = hist[idx - 1];
      setComponents(prevState.components);
      setLines(prevState.lines);
      setHistoryIndex(idx - 1);
    }
  }, []);

  const handleRedo = useCallback(() => {
    const idx = historyIndexRef.current;
    const hist = historyRef.current;
    if (idx < hist.length - 1) {
      const nextState = hist[idx + 1];
      setComponents(nextState.components);
      setLines(nextState.lines);
      setHistoryIndex(idx + 1);
    }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;

      // Delete
      if (e.key === 'Delete') {
        if (selectedItems.length > 0) {
          setComponents(prev => prev.filter(c => !selectedItems.includes(c.id)));
          setLines(prev => prev.filter(l => !selectedItems.includes(l.id)));
          setSelectedItems([]);
          saveToHistory();
        } else if (selectedComponent) {
          handleDeleteComponent();
        } else if (selectedLine) {
          handleDeleteLine();
        }
      }
      // Ctrl/Cmd shortcuts — use e.key.toLowerCase() so Caps Lock doesn't break shortcuts
      if (e.ctrlKey || e.metaKey) {
        const k = e.key.toLowerCase();
        if (k === 'z' && !e.shiftKey) {
          e.preventDefault();
          handleUndo();
        } else if ((k === 'z' && e.shiftKey) || k === 'y') {
          e.preventDefault();
          handleRedo();
        }
        if (k === 'c') {
          e.preventDefault();
          handleCopy();
        }
        if (k === 'x') {
          e.preventDefault();
          handleCut();
        }
        if (k === 'v') {
          e.preventDefault();
          handlePaste();
          return;
        }
        if (k === 'd') {
          e.preventDefault();
          handleDuplicate();
        }
        if (k === 'a') {
          e.preventDefault();
          handleSelectAll();
        }
        if (k === 's') {
          e.preventDefault();
          setShowSaveConfirm(true);
        }
      }
      // Arrow keys - move selected components (RAF-throttled to avoid re-render storm on key repeat)
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        const hasSelection = selectedItems.length > 0 || selectedComponent || selectedLine;
        if (!hasSelection) return;
        e.preventDefault();
        const step = e.shiftKey ? 10 : (snapToGrid ? gridSize : 1);
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;

        // Accumulate deltas so rapid key repeats merge into one frame update
        if (!pendingDragRef.current) pendingDragRef.current = { dx: 0, dy: 0 };
        pendingDragRef.current.dx += dx;
        pendingDragRef.current.dy += dy;

        if (!dragRafRef.current) {
          dragRafRef.current = requestAnimationFrame(() => {
            dragRafRef.current = null;
            const { dx: adx, dy: ady } = pendingDragRef.current || { dx: 0, dy: 0 };
            pendingDragRef.current = null;
            if (selectedItems.length > 0) {
              setComponents(prev => prev.map(c =>
                selectedItems.includes(c.id) ? { ...c, x: Math.max(0, c.x + adx), y: Math.max(0, c.y + ady) } : c
              ));
              setLines(prev => prev.map(l =>
                selectedItems.includes(l.id)
                  ? { ...l, startX: l.startX + adx, startY: l.startY + ady, endX: l.endX + adx, endY: l.endY + ady, controlPoints: (l.controlPoints || []).map(p => ({ x: p.x + adx, y: p.y + ady })) }
                  : l
              ));
            } else if (selectedComponent) {
              setComponents(prev => prev.map(c =>
                c.id === selectedComponent ? { ...c, x: Math.max(0, c.x + adx), y: Math.max(0, c.y + ady) } : c
              ));
            } else if (selectedLine) {
              setLines(prev => prev.map(l =>
                l.id === selectedLine
                  ? { ...l, startX: l.startX + adx, startY: l.startY + ady, endX: l.endX + adx, endY: l.endY + ady, controlPoints: (l.controlPoints || []).map(p => ({ x: p.x + adx, y: p.y + ady })) }
                  : l
              ));
            }
          });
        }
      }
      // Escape - deselect
      if (e.key === 'Escape') {
        setSelectedComponent(null);
        setSelectedLine(null);
        setSelectedItems([]);
        setDrawingLine(null);
        setCustomComponentToAdd(null);
        setSelectedTool('select');
      }
      // Tool shortcuts (only without Ctrl/Meta to avoid conflicting with Ctrl+V etc.)
      if (!e.ctrlKey && !e.metaKey) {
        if (e.key === 'v' || e.key === 'V') {
          setSelectedTool('select');
          setComponentToAdd(null);
        }
        if (e.key === 'l' || e.key === 'L') {
          setSelectedTool('pipe');
          setComponentToAdd(null);
        }
        if (e.key === 'h' || e.key === 'H') {
          setSelectedTool('pan');
          setComponentToAdd(null);
        }
        if (e.key === 't' || e.key === 'T') {
          setSelectedTool('textbox');
          setComponentToAdd(null);
        }
        if (e.key === 'd' || e.key === 'D') {
          setSelectedTool('led');
          setComponentToAdd(null);
        }
      }
    };

    // window listener handles all cases; tabIndex+focus() on mousedown ensures
    // the renderer retains keyboard focus on every machine.
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedComponent, selectedLine, selectedItems, components, lines, snapToGrid, gridSize, handleUndo, handleRedo]);

  // Enter key to confirm save dialog
  useEffect(() => {
    if (!showSaveConfirm) return;
    const handleEnter = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setShowSaveConfirm(false);
        handleSave();
      } else if (e.key === 'Escape') {
        setShowSaveConfirm(false);
      }
    };
    window.addEventListener('keydown', handleEnter);
    return () => window.removeEventListener('keydown', handleEnter);
  }, [showSaveConfirm]);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const sql = `SELECT * FROM scada_wide LIMIT 1`;
        const result = await questdbService.query(sql, plantId);
        
        if (result.length > 0) {
          const columns = Object.keys(result[0]).filter(col => col !== 'timestamp' && col !== 'bridge_id');
          setAvailableTags(columns);
        }
      } catch (error) {
        console.error('Error fetching tags from scada_wide:', error);
      }
    };
    fetchTags();
  }, []);

  useEffect(() => {
    const fetchTagValues = async () => {
      try {
        const tagNames = [...new Set([
          ...components.flatMap(c => {
            const tags = [];
            if (c.tagName) tags.push(c.tagName);
            if (c.conditions) c.conditions.forEach(cond => cond.rules?.forEach(r => { if (r.tagName) tags.push(r.tagName); }));
            return tags;
          }),
          ...lines.filter(l => l.tagName).map(l => l.tagName)
        ])];

        if (tagNames.length === 0) return;

        const columnsToSelect = ['timestamp', ...tagNames].join(', ');
        const sql = `SELECT ${columnsToSelect} FROM scada_wide ORDER BY timestamp DESC LIMIT 1`;

        const result = await questdbService.query(sql, plantId);

        if (result.length > 0) {
          const valueMap = {};
          const latestRow = result[0];

          tagNames.forEach(tagName => {
            if (latestRow[tagName] !== undefined && latestRow[tagName] !== null) {
              valueMap[tagName] = String(latestRow[tagName]);
            }
          });

          setTagValues(valueMap);
        }
      } catch (error) {
        console.error('Error fetching tag values:', error);
      }
    };

    fetchTagValues();
    const pollMs = Math.max(500, Number(refreshInterval) || 2000);
    const interval = setInterval(fetchTagValues, pollMs);
    return () => clearInterval(interval);
  }, [components, lines, refreshInterval]);

  // Evaluate a single rule against current tag values
  const evaluateRule = (rule) => {
    const tagVal = tagValues[rule.tagName];
    if (tagVal === undefined || tagVal === null) return false;
    const tv = String(tagVal);
    const rv = String(rule.value);
    switch (rule.operator) {
      case '==': return tv === rv;
      case '!=': return tv !== rv;
      case '>': return parseFloat(tv) > parseFloat(rv);
      case '<': return parseFloat(tv) < parseFloat(rv);
      case '>=': return parseFloat(tv) >= parseFloat(rv);
      case '<=': return parseFloat(tv) <= parseFloat(rv);
      case 'contains': return tv.toLowerCase().includes(rv.toLowerCase());
      default: return tv === rv;
    }
  };

  const evaluateCondition = (cond) => {
    if (!cond.rules || cond.rules.length === 0) return false;
    const validRules = cond.rules.filter(r => r.tagName);
    if (validRules.length === 0) return false;
    if (cond.logic === 'OR') return validRules.some(evaluateRule);
    return validRules.every(evaluateRule);
  };

  const getMatchingCondition = (comp) => {
    if (comp.conditions?.length) {
      // Evaluate conditions with more rules first (most specific wins)
      const sorted = [...comp.conditions].sort((a, b) => (b.rules?.length || 0) - (a.rules?.length || 0));
      return sorted.find(evaluateCondition) || null;
    }
    if (comp.tagName && comp.colorMappings?.length) {
      const tagValue = tagValues[comp.tagName];
      if (tagValue === undefined) return null;
      const mapping = comp.colorMappings.find(m => String(m.value) === String(tagValue));
      return mapping ? { color: mapping.color, label: mapping.label } : null;
    }
    return null;
  };

  const getLedColorAndStatus = (comp) => {
    const cfg = comp.ledConfig;
    if (!cfg) return null;
    const onTag = cfg.onFeedback?.tagName;
    const offTag = cfg.offFeedback?.tagName;
    if (onTag) {
      const val = tagValues[onTag];
      if (val !== undefined) {
        if (parseFloat(val) >= 1) return { color: cfg.onFeedback.color || '#00ff00', label: cfg.onFeedback.label || 'ON' };
        // If off feedback tag is not assigned, use off feedback color for value 0
        return { color: cfg.offFeedback?.color || '#ff0000', label: cfg.offFeedback?.label || 'OFF' };
      }
    }
    if (offTag) {
      const val = tagValues[offTag];
      if (val !== undefined) {
        if (parseFloat(val) >= 1) return { color: cfg.offFeedback.color || '#ff0000', label: cfg.offFeedback.label || 'OFF' };
        return { color: cfg.onFeedback?.color || '#00ff00', label: cfg.onFeedback?.label || 'ON' };
      }
    }
    return null;
  };

  const getComponentColor = (comp) => {
    if (comp.type === 'led' && comp.ledConfig) {
      const led = getLedColorAndStatus(comp);
      return led ? led.color : (comp.defaultColor || '#64748b');
    }
    const match = getMatchingCondition(comp);
    return match ? match.color : (comp.defaultColor || '#64748b');
  };

  const getComponentStatus = (comp) => {
    if (comp.type === 'led' && comp.ledConfig) {
      const led = getLedColorAndStatus(comp);
      return led?.label || null;
    }
    const match = getMatchingCondition(comp);
    return match?.label || null;
  };

  const fetchSingleTagValue = async (tagName) => {
    if (!tagName) return;
    try {
      const sql = `SELECT timestamp, ${tagName} FROM scada_wide ORDER BY timestamp DESC LIMIT 1`;
      const result = await questdbService.query(sql, plantId);
      
      if (result.length > 0 && result[0][tagName] !== undefined) {
        setTagValues(prev => ({
          ...prev,
          [tagName]: String(result[0][tagName])
        }));
      }
    } catch (error) {
      console.error('Error fetching single tag value:', error);
    }
  };

  const handleCanvasMouseDown = (e) => {
    if (e.button === 2) return; // Ignore right click

    const { x, y, svgX, svgY } = screenToCanvas(e);

    // Pan tool or space key
    if (selectedTool === 'pan' || e.spaceKey || e.button === 1) {
      setIsPanning(true);
      setPanStart({ x: svgX - pan.x, y: svgY - pan.y });
      return;
    }

    if (selectedTool === 'component' && componentToAdd) {
      const newComponent = {
        id: `comp_${Date.now()}`,
        type: componentToAdd,
        x: snapToGridFn(x - SVG_COMPONENTS[componentToAdd].width / 2),
        y: snapToGridFn(y - SVG_COMPONENTS[componentToAdd].height / 2),
        rotation: 0,
        label: SVG_COMPONENTS[componentToAdd].name,
        tagName: '',
        colorMappings: [
          { value: '0', color: '#ef4444', label: 'Off' },
          { value: '1', color: '#10b981', label: 'On' }
        ],
        defaultColor: '#64748b'
      };
      setComponents([...components, newComponent]);
      saveToHistory();
      setSelectedComponent(newComponent.id);
      setComponentToAdd(null);
      setSelectedTool('select');
    } else if (selectedTool === 'customImage' && customComponentToAdd) {
      // Place custom image at click position
      const comp = customComponentToAdd;
      const newComponent = {
        id: `comp_${Date.now()}`,
        type: 'customImage',
        x: snapToGridFn(x - comp.width / 2),
        y: snapToGridFn(y - comp.height / 2),
        rotation: 0,
        label: comp.name,
        imageData: comp.imageData,
        width: comp.width,
        height: comp.height,
        tagName: '',
        colorMappings: [
          { value: '0', color: '#ef4444', label: 'Off' },
          { value: '1', color: '#10b981', label: 'On' }
        ],
        defaultColor: '#64748b'
      };
      setComponents([...components, newComponent]);
      saveToHistory();
      setSelectedComponent(newComponent.id);
      setCustomComponentToAdd(null);
      setSelectedTool('select');
    } else if (selectedTool === 'textbox') {
      // Place text box
      const newTextBox = {
        id: `textbox_${Date.now()}`,
        type: 'textbox',
        x: snapToGridFn(x - DEFAULT_TEXTBOX.width / 2),
        y: snapToGridFn(y - DEFAULT_TEXTBOX.height / 2),
        ...DEFAULT_TEXTBOX
      };
      setComponents([...components, newTextBox]);
      saveToHistory();
      setSelectedComponent(newTextBox.id);
      setSelectedTool('select');
    } else if (selectedTool === 'led') {
      const newLed = {
        id: `led_${Date.now()}`,
        type: 'led',
        x: snapToGridFn(x - 20),
        y: snapToGridFn(y - 20),
        width: 40,
        height: 40,
        rotation: 0,
        label: 'LED',
        tagName: '',
        conditions: [],
        colorMappings: [],
        defaultColor: '#64748b',
        ledConfig: {
          onFeedback:  { tagName: '', color: '#00ff00', label: 'ON' },
          offFeedback: { tagName: '', color: '#ff0000', label: 'OFF' },
        }
      };
      setComponents([...components, newLed]);
      saveToHistory();
      setSelectedComponent(newLed.id);
      setSelectedTool('select');
    } else if (selectedTool === 'navButton') {
      const newNavBtn = {
        id: `navbtn_${Date.now()}`,
        type: 'navButton',
        x: snapToGridFn(x - 60),
        y: snapToGridFn(y - 20),
        width: 120,
        height: 40,
        label: 'Go to Panel',
        targetPanelId: '',
        buttonColor: '#667eea'
      };
      setComponents([...components, newNavBtn]);
      saveToHistory();
      setSelectedComponent(newNavBtn.id);
      setSelectedTool('select');
    } else if (selectedTool === 'drawLine') {
      const newDrawLine = {
        id: `drawLine_${Date.now()}`,
        type: 'drawLine',
        x: snapToGridFn(x - 155),
        y: snapToGridFn(y - 5),
        width: 310, height: 10,
        rotation: 0, label: 'Line',
        orientation: 'horizontal',
        lineStyle: 'solid',
        lineThickness: 2,
        dotWidth: 5, dashWidth: 10, gapWidth: 5,
        animEnabled: false, animSpeed: 10,
        highColor: '#000000', lowColor: '#141212',
        borderColor: '#000000', borderWidth: 0,
      };
      setComponents([...components, newDrawLine]);
      saveToHistory();
      setSelectedComponent(newDrawLine.id);
      setSelectedTool('select');
    } else if (selectedTool === 'shape') {
      const st = pendingShapeType || 'rectangle';
      const isCircular = st === 'circle' || st === 'ellipse';
      const defaults = {
        rectangle:    { w: 120, h: 80,  cr: 0 },
        roundedRect:  { w: 120, h: 80,  cr: 12 },
        square:       { w: 80,  h: 80,  cr: 0 },
        circle:       { w: 80,  h: 80,  cr: 0 },
        ellipse:      { w: 120, h: 70,  cr: 0 },
        triangle:     { w: 100, h: 90,  cr: 0 },
        diamond:      { w: 90,  h: 90,  cr: 0 },
        pentagon:     { w: 90,  h: 90,  cr: 0 },
        hexagon:      { w: 100, h: 90,  cr: 0 },
        star:         { w: 90,  h: 90,  cr: 0 },
        arrow:        { w: 120, h: 60,  cr: 0 },
        cross:        { w: 80,  h: 80,  cr: 0 },
        parallelogram:{ w: 120, h: 70,  cr: 0 },
        trapezoid:    { w: 120, h: 70,  cr: 0 },
      };
      const d = defaults[st] || defaults.rectangle;
      const newShape = {
        id: `shape_${Date.now()}`,
        type: 'shape',
        x: snapToGridFn(x - d.w / 2),
        y: snapToGridFn(y - d.h / 2),
        width: d.w, height: d.h,
        rotation: 0, label: st.charAt(0).toUpperCase() + st.slice(1),
        shapeType: st,
        cornerRadius: d.cr,
        enableBorder: true,
        borderWidth: 2,
        fillColor: '#272E35',
        borderColor: '#000000',
      };
      setComponents([...components, newShape]);
      saveToHistory();
      setSelectedComponent(newShape.id);
      setSelectedTool('select');
    } else if (selectedTool === 'pipe') {
      // Free pipe placement - click to start, click again to finish
      if (!drawingLine) {
        setDrawingLine({
          startX: snapToGridFn(x),
          startY: snapToGridFn(y),
          endX: snapToGridFn(x),
          endY: snapToGridFn(y)
        });
      } else {
        let endX = snapToGridFn(x);
        let endY = snapToGridFn(y);
        if (e.shiftKey) {
          const dx = Math.abs(endX - drawingLine.startX);
          const dy = Math.abs(endY - drawingLine.startY);
          if (dx > dy) endY = drawingLine.startY;
          else endX = drawingLine.startX;
        }
        const newPipe = {
          id: `pipe_${Date.now()}`,
          startX: drawingLine.startX,
          startY: drawingLine.startY,
          endX,
          endY,
          ...pipeConfig,
          controlPoints: []
        };
        setLines([...lines, newPipe]);
        saveToHistory();
        setDrawingLine(null);
        setSelectedTool('select');
      }
    } else if (selectedTool === 'select' && !e.target.closest('[data-component]') && !e.target.closest('[data-line]')) {
      // Start selection box
      setSelectionBox({ startX: x, startY: y, endX: x, endY: y });
      setSelectedComponent(null);
      setSelectedLine(null);
      setSelectedItems([]);
    }
  };

  const handleCanvasMouseMove = (e) => {
    const { x, y, svgX, svgY, rect } = screenToCanvas(e);

    if (isPanning) {
      setPan({
        x: svgX - panStart.x,
        y: svgY - panStart.y
      });
      return;
    }

    if (resizingComponent) {
      // Handle resize — convert screen pixel deltas through viewBox scaling
      const scX = rect.width / canonicalWidth;
      const scY = rect.height / canonicalHeight;
      const sc = Math.min(scX, scY);
      const deltaX = (e.clientX - resizingComponent.startX) / sc / zoom;
      const deltaY = (e.clientY - resizingComponent.startY) / sc / zoom;

      const resizingComp = components.find(c => c.id === resizingComponent.id);
      const minSize = resizingComp?.type === 'textbox' ? 4 : 20;
      const newBounds = calculateResize(
        resizingComponent.handle,
        deltaX,
        deltaY,
        resizingComponent.originalBounds,
        minSize,
        snapToGrid ? gridSize : null
      );

      // Keep LED square
      if (resizingComp?.type === 'led') {
        const s = Math.max(newBounds.width, newBounds.height);
        newBounds.width = s;
        newBounds.height = s;
      }

      setComponents(components.map(c =>
        c.id === resizingComponent.id
          ? { ...c, x: newBounds.x, y: newBounds.y, width: newBounds.width, height: newBounds.height }
          : c
      ));
    } else if (draggingComponent && selectedTool === 'select') {
      const scX2 = rect.width / canonicalWidth;
      const scY2 = rect.height / canonicalHeight;
      const sc2 = Math.min(scX2, scY2);
      const deltaX = (e.clientX - draggingComponent.startX) / sc2 / zoom;
      const deltaY = (e.clientY - draggingComponent.startY) / sc2 / zoom;

      // Store latest drag delta in ref — RAF will pick it up, preventing 60+ setComponents calls per second
      pendingDragRef.current = { deltaX, deltaY };

      if (!dragRafRef.current) {
        dragRafRef.current = requestAnimationFrame(() => {
          dragRafRef.current = null;
          const { deltaX: dx, deltaY: dy } = pendingDragRef.current || {};
          if (dx === undefined) return;

          if (draggingComponent.isMulti && draggingComponent.initPos) {
            const initPos = draggingComponent.initPos;
            setComponents(prev => prev.map(c => {
              if (initPos[c.id]) {
                return { ...c, x: snapToGridFn(Math.max(0, initPos[c.id].x + dx)), y: snapToGridFn(Math.max(0, initPos[c.id].y + dy)) };
              }
              return c;
            }));
            setLines(prev => prev.map(l => {
              if (initPos[l.id]) {
                const ip = initPos[l.id];
                return { ...l, startX: ip.startX + dx, startY: ip.startY + dy, endX: ip.endX + dx, endY: ip.endY + dy, controlPoints: (ip.controlPoints || []).map(p => ({ x: p.x + dx, y: p.y + dy })) };
              }
              return l;
            }));
          } else {
            setComponents(prev => prev.map(c =>
              c.id === draggingComponent.id
                ? { ...c, x: snapToGridFn(Math.max(0, draggingComponent.initialX + dx)), y: snapToGridFn(Math.max(0, draggingComponent.initialY + dy)) }
                : c
            ));
          }
        });
      }
    } else if (draggingLinePoint) {
      // Dragging a line endpoint or control point
      const line = lines.find(l => l.id === draggingLinePoint.lineId);
      if (line) {
        if (draggingLinePoint.pointIndex === 'start') {
          setLines(lines.map(l => l.id === line.id ? { ...l, startX: x, startY: y } : l));
        } else if (draggingLinePoint.pointIndex === 'end') {
          setLines(lines.map(l => l.id === line.id ? { ...l, endX: x, endY: y } : l));
        } else {
          const newControlPoints = [...(line.controlPoints || [])];
          if (draggingLinePoint.pointIndex >= 0 && draggingLinePoint.pointIndex < newControlPoints.length) {
            newControlPoints[draggingLinePoint.pointIndex] = { x, y };
            setLines(lines.map(l => l.id === line.id ? { ...l, controlPoints: newControlPoints } : l));
          }
        }
      }
    } else if (draggingLine) {
      // Dragging the whole pipe body
      const scL = Math.min(rect.width / canonicalWidth, rect.height / canonicalHeight);
      const dx = (e.clientX - draggingLine.startMouseX) / scL / zoom;
      const dy = (e.clientY - draggingLine.startMouseY) / scL / zoom;
      setLines(lines.map(l => l.id === draggingLine.lineId ? {
        ...l,
        startX: draggingLine.origStartX + dx,
        startY: draggingLine.origStartY + dy,
        endX: draggingLine.origEndX + dx,
        endY: draggingLine.origEndY + dy,
        controlPoints: (draggingLine.origControlPoints || []).map(cp => ({ x: cp.x + dx, y: cp.y + dy }))
      } : l));
    } else if (drawingLine && selectedTool === 'pipe') {
      let endX = x, endY = y;
      if (e.shiftKey) {
        const dx = Math.abs(x - drawingLine.startX);
        const dy = Math.abs(y - drawingLine.startY);
        if (dx > dy) endY = drawingLine.startY; // Horizontal
        else endX = drawingLine.startX;          // Vertical
      }
      setDrawingLine({ ...drawingLine, endX, endY });
    } else if (selectionBox) {
      setSelectionBox({ ...selectionBox, endX: x, endY: y });
    }
  };

  const handleCanvasMouseUp = () => {
    if (isPanning) {
      setIsPanning(false);
    }

    if (resizingComponent) {
      saveToHistory();
      setResizingComponent(null);
    }

    if (draggingComponent) {
      // Cancel any pending RAF and mark drag done before saving history
      if (dragRafRef.current) { cancelAnimationFrame(dragRafRef.current); dragRafRef.current = null; }
      pendingDragRef.current = null;
      isDraggingRef.current = false;
      saveToHistory();
      setDraggingComponent(null);
    }

    if (draggingLinePoint) {
      saveToHistory();
      setDraggingLinePoint(null);
    }

    if (draggingLine) {
      saveToHistory();
      setDraggingLine(null);
    }

    if (selectionBox) {
      // Select components within box
      const minX = Math.min(selectionBox.startX, selectionBox.endX);
      const maxX = Math.max(selectionBox.startX, selectionBox.endX);
      const minY = Math.min(selectionBox.startY, selectionBox.endY);
      const maxY = Math.max(selectionBox.startY, selectionBox.endY);

      const selected = components.filter(comp => {
        const dim = getComponentDimensions(comp);
        return comp.x >= minX &&
               comp.x + dim.width <= maxX &&
               comp.y >= minY &&
               comp.y + dim.height <= maxY;
      }).map(c => c.id);

      const selectedLineIds = lines.filter(l =>
        Math.min(l.startX, l.endX) >= minX &&
        Math.max(l.startX, l.endX) <= maxX &&
        Math.min(l.startY, l.endY) >= minY &&
        Math.max(l.startY, l.endY) <= maxY
      ).map(l => l.id);

      setSelectedItems([...selected, ...selectedLineIds]);
      setSelectionBox(null);
    }
  };

  const handleComponentMouseDown = (e, comp) => {
    e.stopPropagation();

    // Locked components: allow selection but not dragging
    if (lockedComponents.has(comp.id)) {
      if (selectedTool === 'select') {
        setSelectedLine(null);
        if (e.ctrlKey || e.metaKey) {
          setSelectedItems(prev =>
            prev.includes(comp.id) ? prev.filter(id => id !== comp.id) : [...prev, comp.id]
          );
        } else {
          setSelectedComponent(comp.id);
          setSelectedItems([]);
        }
      }
      return;
    }

    if (selectedTool === 'select') {
      setSelectedLine(null);

      // Resolve group members for this component
      const groupIds = comp.groupId
        ? components.filter(c => c.groupId === comp.groupId).map(c => c.id)
        : null;

      if (e.ctrlKey || e.metaKey) {
        // Ctrl+click: toggle item (or entire group) in multi-selection
        // If there's a single selectedComponent not yet in selectedItems, seed it in
        if (groupIds) {
          setSelectedItems(prev => {
            let base = prev;
            if (selectedComponent && !prev.includes(selectedComponent)) base = [...prev, selectedComponent];
            const allIn = groupIds.every(id => base.includes(id));
            return allIn
              ? base.filter(id => !groupIds.includes(id))
              : [...new Set([...base, ...groupIds])];
          });
        } else {
          setSelectedItems(prev => {
            let base = prev;
            if (selectedComponent && !prev.includes(selectedComponent)) base = [...prev, selectedComponent];
            return base.includes(comp.id) ? base.filter(id => id !== comp.id) : [...base, comp.id];
          }
          );
        }
        return;
      }

      // Determine effective selection (group-aware)
      const effectiveIds = groupIds || [comp.id];
      const isInMultiSelect = effectiveIds.every(id => selectedItems.includes(id)) && selectedItems.length > 1;

      if (isInMultiSelect || effectiveIds.length > 1) {
        // Drag all selected/grouped items together
        const allIds = isInMultiSelect ? selectedItems : effectiveIds;
        if (!isInMultiSelect) setSelectedItems(effectiveIds);
        setSelectedComponent(comp.id);
        const initPos = {};
        components.filter(c => allIds.includes(c.id)).forEach(c => {
          initPos[c.id] = { x: c.x, y: c.y };
        });
        lines.filter(l => allIds.includes(l.id)).forEach(l => {
          initPos[l.id] = { startX: l.startX, startY: l.startY, endX: l.endX, endY: l.endY, controlPoints: l.controlPoints };
        });
        isDraggingRef.current = true;
        setDraggingComponent({
          id: comp.id,
          startX: e.clientX,
          startY: e.clientY,
          initialX: comp.x,
          initialY: comp.y,
          isMulti: true,
          initPos
        });
      } else {
        setSelectedComponent(comp.id);
        setSelectedItems([]);
        isDraggingRef.current = true;
        setDraggingComponent({
          id: comp.id,
          startX: e.clientX,
          startY: e.clientY,
          initialX: comp.x,
          initialY: comp.y
        });
      }
    } else if (selectedTool === 'pipe') {
      // Get component dimensions for center point
      const compDef = comp.type === 'textbox' ? { width: comp.width || 120, height: comp.height || 40 }
                     : comp.type === 'customImage' ? { width: comp.width || 64, height: comp.height || 64 }
                     : SVG_COMPONENTS[comp.type] || { width: 64, height: 64 };
      const centerX = comp.x + compDef.width / 2;
      const centerY = comp.y + compDef.height / 2;

      if (!drawingLine) {
        setDrawingLine({
          startX: centerX,
          startY: centerY,
          endX: centerX,
          endY: centerY
        });
      } else {
        const newPipe = {
          id: `pipe_${Date.now()}`,
          startX: drawingLine.startX,
          startY: drawingLine.startY,
          endX: centerX,
          endY: centerY,
          ...pipeConfig,
          controlPoints: []
        };
        setLines([...lines, newPipe]);
        saveToHistory();
        setDrawingLine(null);
        setSelectedTool('select');
      }
    }
  };

  const handleLineClick = (e, line) => {
    e.stopPropagation();
    if (selectedTool === 'select') {
      if (e.ctrlKey || e.metaKey) {
        setSelectedItems(prev =>
          prev.includes(line.id) ? prev.filter(id => id !== line.id) : [...prev, line.id]
        );
        return;
      }
      setSelectedLine(line.id);
      setSelectedComponent(null);
      setEditingLine(line.id);
    }
  };

  const handleLinePointMouseDown = (e, lineId, pointIndex) => {
    e.stopPropagation();
    if (selectedTool === 'select') {
      setDraggingLinePoint({ lineId, pointIndex, startX: e.clientX, startY: e.clientY });
    }
  };

  const handleAddControlPoint = (lineId, x, y) => {
    const line = lines.find(l => l.id === lineId);
    if (!line) return;
    
    const newControlPoints = [...(line.controlPoints || []), { x, y }];
    setLines(lines.map(l => l.id === lineId ? { ...l, controlPoints: newControlPoints } : l));
    saveToHistory();
  };

  // Alignment helpers for multi-selected components
  const getSelectedCompsBounds = () => {
    return components
      .filter(c => selectedItems.includes(c.id))
      .map(c => {
        const dim = getComponentDimensions(c);
        return { id: c.id, x: c.x, y: c.y, w: dim.width, h: dim.height };
      });
  };

  const handleAlign = (type) => {
    const bounds = getSelectedCompsBounds();
    if (bounds.length < 2) return;
    const minX = Math.min(...bounds.map(b => b.x));
    const maxX = Math.max(...bounds.map(b => b.x + b.w));
    const minY = Math.min(...bounds.map(b => b.y));
    const maxY = Math.max(...bounds.map(b => b.y + b.h));
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    setComponents(prev => prev.map(c => {
      if (!selectedItems.includes(c.id)) return c;
      const b = bounds.find(b => b.id === c.id);
      switch (type) {
        case 'left':    return { ...c, x: minX };
        case 'centerH': return { ...c, x: centerX - b.w / 2 };
        case 'right':   return { ...c, x: maxX - b.w };
        case 'top':     return { ...c, y: minY };
        case 'centerV': return { ...c, y: centerY - b.h / 2 };
        case 'bottom':  return { ...c, y: maxY - b.h };
        default: return c;
      }
    }));
    saveToHistory();
  };

  const handleDistribute = (axis) => {
    const bounds = getSelectedCompsBounds();
    if (bounds.length < 3) return;
    if (axis === 'h') {
      const sorted = [...bounds].sort((a, b) => a.x - b.x);
      const totalW = sorted.reduce((s, b) => s + b.w, 0);
      const span = sorted[sorted.length - 1].x + sorted[sorted.length - 1].w - sorted[0].x;
      const gap = (span - totalW) / (sorted.length - 1);
      let cursor = sorted[0].x;
      const newX = {};
      sorted.forEach(b => { newX[b.id] = cursor; cursor += b.w + gap; });
      setComponents(prev => prev.map(c => selectedItems.includes(c.id) ? { ...c, x: newX[c.id] } : c));
    } else {
      const sorted = [...bounds].sort((a, b) => a.y - b.y);
      const totalH = sorted.reduce((s, b) => s + b.h, 0);
      const span = sorted[sorted.length - 1].y + sorted[sorted.length - 1].h - sorted[0].y;
      const gap = (span - totalH) / (sorted.length - 1);
      let cursor = sorted[0].y;
      const newY = {};
      sorted.forEach(b => { newY[b.id] = cursor; cursor += b.h + gap; });
      setComponents(prev => prev.map(c => selectedItems.includes(c.id) ? { ...c, y: newY[c.id] } : c));
    }
    saveToHistory();
  };

  const handleMatchSize = () => {
    if (selectedItems.length < 2) return;
    const first = components.find(c => c.id === selectedItems[0]);
    if (!first) return;
    setComponents(prev => prev.map(c =>
      selectedItems.includes(c.id) && c.id !== first.id
        ? { ...c, width: first.width, height: first.height }
        : c
    ));
    saveToHistory();
  };

  // "Format Painter" — copy all properties from the first selected component to others of the same type group
  const getTypeGroup = (comp) => {
    if (!comp) return null;
    if (PUMP_TYPES.has(comp.type)) return 'pump';
    if (VALVE_TYPES.has(comp.type)) return 'valve';
    if (CONTROL_VALVE_TYPES.has(comp.type)) return 'controlValve';
    return comp.type; // textbox, led, navButton, drawLine, shape, customImage
  };

  const handleApplyProperties = () => {
    if (selectedItems.length < 2) return;
    const ref = components.find(c => c.id === selectedItems[0]);
    if (!ref) return;
    const refGroup = getTypeGroup(ref);

    // Properties to never copy (identity / position)
    const SKIP = new Set(['id', 'x', 'y', 'label', 'groupId']);

    setComponents(prev => prev.map(c => {
      if (c.id === ref.id || !selectedItems.includes(c.id)) return c;
      if (getTypeGroup(c) !== refGroup) return c; // different type group — skip

      const updated = { ...c };
      for (const key of Object.keys(ref)) {
        if (SKIP.has(key)) continue;
        // For SVG component types, don't overwrite `type` — the target keeps its own SVG variant
        if (key === 'type' && refGroup !== c.type) continue;
        updated[key] = ref[key];
      }
      // Preserve the target's own type (e.g. centrifugalPump2 stays centrifugalPump2)
      updated.type = c.type;
      return updated;
    }));
    saveToHistory();
  };

  // "Format Painter (Style Only)" — copies all visual properties (including colors inside configs) but preserves tag names
  const handleApplyStyle = () => {
    if (selectedItems.length < 2) return;
    const ref = components.find(c => c.id === selectedItems[0]);
    if (!ref) return;
    const refGroup = getTypeGroup(ref);

    // Merge config: copy colors/labels/visual settings from ref config, but keep target's tagName values
    const mergeConfig = (refCfg, targetCfg) => {
      if (!refCfg) return targetCfg;
      if (!targetCfg) targetCfg = {};
      const merged = { ...targetCfg };
      for (const key of Object.keys(refCfg)) {
        const refVal = refCfg[key];
        if (refVal && typeof refVal === 'object' && !Array.isArray(refVal)) {
          // Sub-object (e.g. onFeedback, offFeedback, trip, mode, pv, sp, op, etc.)
          const targetSub = targetCfg[key] || {};
          const mergedSub = { ...targetSub };
          for (const subKey of Object.keys(refVal)) {
            if (subKey === 'tagName') continue; // preserve target's tag
            mergedSub[subKey] = refVal[subKey];
          }
          merged[key] = mergedSub;
        } else {
          // Top-level scalar (faultColor, intermediateColor, openColor, closeColor, etc.)
          merged[key] = refVal;
        }
      }
      return merged;
    };

    const SKIP = new Set(['id', 'x', 'y', 'label', 'groupId', 'tagName', 'tagConfig', 'tags', 'dataSource', 'table', 'column']);
    const CONFIG_KEYS = new Set(['pumpConfig', 'valveConfig', 'controlValveConfig', 'ledConfig']);

    setComponents(prev => prev.map(c => {
      if (c.id === ref.id || !selectedItems.includes(c.id)) return c;
      if (getTypeGroup(c) !== refGroup) return c;

      const updated = { ...c };
      for (const key of Object.keys(ref)) {
        if (SKIP.has(key) || key === 'type') continue;
        if (CONFIG_KEYS.has(key)) {
          updated[key] = mergeConfig(ref[key], c[key]);
        } else {
          updated[key] = ref[key];
        }
      }
      return updated;
    }));
    saveToHistory();
  };

  const handleConfigureTag = () => {
    if (selectedComponent) {
      const comp = components.find(c => c.id === selectedComponent);
      if (PUMP_TYPES.has(comp?.type)) {
        setEditingPumpComponent(comp);
        setShowPumpModal(true);
      } else if (VALVE_TYPES.has(comp?.type)) {
        setEditingValveComponent(comp);
        setShowValveModal(true);
      } else if (comp?.type === 'led') {
        setEditingLedComponent(comp);
        setShowLedModal(true);
      } else {
        setEditingComponentTag(comp);
        setShowTagModal(true);
      }
    }
  };

  const handleSaveTagConfig = (updatedComponent) => {
    setComponents(prev => prev.map(c => c.id === updatedComponent.id ? updatedComponent : c));
    saveToHistory();
    setShowTagModal(false);
    setEditingComponentTag(null);
    showSaveToast('Component configuration saved!');
    // Fetch all tags referenced in conditions
    const allTags = [];
    if (updatedComponent.tagName) allTags.push(updatedComponent.tagName);
    if (updatedComponent.conditions) {
      updatedComponent.conditions.forEach(cond => cond.rules?.forEach(r => { if (r.tagName) allTags.push(r.tagName); }));
    }
    [...new Set(allTags)].forEach(tag => fetchSingleTagValue(tag));
  };

  const handleSavePumpConfig = (updatedComponent) => {
    setComponents(prev => prev.map(c => c.id === updatedComponent.id ? updatedComponent : c));
    saveToHistory();
    setShowPumpModal(false);
    setEditingPumpComponent(null);
    showSaveToast('Pump configuration saved!');
    // Fetch all pump tags
    const cfg = updatedComponent.pumpConfig || {};
    const allTags = [
      cfg.onFeedback?.tagName, cfg.offFeedback?.tagName, cfg.trip?.tagName,
      cfg.onCommand?.tagName, cfg.offCommand?.tagName, cfg.mode?.tagName
    ].filter(Boolean);
    [...new Set(allTags)].forEach(tag => fetchSingleTagValue(tag));
  };

  const handleSaveValveConfig = (updatedComponent) => {
    setComponents(prev => prev.map(c => c.id === updatedComponent.id ? updatedComponent : c));
    saveToHistory();
    setShowValveModal(false);
    setEditingValveComponent(null);
    showSaveToast('Valve configuration saved!');
    const cfg = updatedComponent.valveConfig || {};
    const allTags = [
      cfg.onFeedback?.tagName, cfg.offFeedback?.tagName, cfg.trip?.tagName,
      cfg.onCommand?.tagName, cfg.offCommand?.tagName, cfg.mode?.tagName, cfg.remoteLocal?.tagName
    ].filter(Boolean);
    [...new Set(allTags)].forEach(tag => fetchSingleTagValue(tag));
  };

  const handleSaveControlValveConfig = (updatedComponent) => {
    setComponents(prev => prev.map(c => c.id === updatedComponent.id ? updatedComponent : c));
    setShowControlValveModal(false);
    setEditingControlValveComponent(null);
    saveToHistory();
    showSaveToast('Control valve configuration saved!');
    const cfg = updatedComponent.controlValveConfig || {};
    const allTags = [cfg.pv?.tagName, cfg.sp?.tagName, cfg.op?.tagName, cfg.mode?.tagName].filter(Boolean);
    [...new Set(allTags)].forEach(tag => fetchSingleTagValue(tag));
  };

  const handleSaveLedConfig = (updatedComponent) => {
    setComponents(prev => prev.map(c => c.id === updatedComponent.id ? updatedComponent : c));
    saveToHistory();
    setShowLedModal(false);
    setEditingLedComponent(null);
    showSaveToast('LED configuration saved!');
    const cfg = updatedComponent.ledConfig || {};
    const allTags = [cfg.onFeedback?.tagName, cfg.offFeedback?.tagName].filter(Boolean);
    [...new Set(allTags)].forEach(tag => fetchSingleTagValue(tag));
  };

  const handleDeleteComponent = () => {
    if (selectedComponent) {
      setComponents(components.filter(c => c.id !== selectedComponent));
      setLines(lines.filter(l => l.from !== selectedComponent && l.to !== selectedComponent));
      saveToHistory();
      setSelectedComponent(null);
    }
  };

  const handleDeleteLine = () => {
    if (selectedLine) {
      setLines(lines.filter(l => l.id !== selectedLine));
      saveToHistory();
      setSelectedLine(null);
    }
  };

  // Copy selection → localStorage (works cross-panel)
  const handleCopy = () => {
    const copiedComps = selectedItems.length > 0
      ? components.filter(c => selectedItems.includes(c.id))
      : selectedComponent
        ? components.filter(c => c.id === selectedComponent)
        : [];
    const copiedLines = selectedItems.length > 0
      ? lines.filter(l => selectedItems.includes(l.id))
      : [];
    if (copiedComps.length === 0 && copiedLines.length === 0) return;
    localStorage.setItem('scada_clipboard', JSON.stringify({ components: copiedComps, lines: copiedLines }));
  };

  // Paste from localStorage (works cross-panel)
  const handlePaste = () => {
    try {
      const raw = localStorage.getItem('scada_clipboard');
      if (!raw) return;
      const clipboard = JSON.parse(raw);
      const offset = 20;
      const newComps = (clipboard.components || []).map(c => ({
        ...c,
        id: `comp_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        x: c.x + offset,
        y: c.y + offset
      }));
      const newLines = (clipboard.lines || []).map(l => ({
        ...l,
        id: `line_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        startX: l.startX + offset, startY: l.startY + offset,
        endX: l.endX + offset, endY: l.endY + offset,
        controlPoints: (l.controlPoints || []).map(p => ({ x: p.x + offset, y: p.y + offset }))
      }));
      // Update clipboard with the new offset positions so next paste shifts further
      const updatedClipboard = {
        components: (clipboard.components || []).map(c => ({ ...c, x: c.x + offset, y: c.y + offset })),
        lines: (clipboard.lines || []).map(l => ({
          ...l,
          startX: l.startX + offset, startY: l.startY + offset,
          endX: l.endX + offset, endY: l.endY + offset,
          controlPoints: (l.controlPoints || []).map(p => ({ x: p.x + offset, y: p.y + offset }))
        }))
      };
      localStorage.setItem('scada_clipboard', JSON.stringify(updatedClipboard));
      setComponents(prev => [...prev, ...newComps]);
      if (newLines.length > 0) setLines(prev => [...prev, ...newLines]);
      setSelectedItems([...newComps.map(c => c.id), ...newLines.map(l => l.id)]);
      saveToHistory();
    } catch (err) {
      console.error('Paste error:', err);
    }
  };

  // Kept for the toolbar button (single-component quick duplicate)
  const handleCopyComponent = () => handleCopy();

  const handleRotateComponent = () => {
    if (selectedComponent) {
      setComponents(components.map(c =>
        c.id === selectedComponent ? { ...c, rotation: (c.rotation + 90) % 360 } : c
      ));
      saveToHistory();
    }
  };

  // ── Cut ───────────────────────────────────────────────
  const handleCut = () => {
    handleCopy();
    if (selectedItems.length > 0) {
      setComponents(prev => prev.filter(c => !selectedItems.includes(c.id)));
      setLines(prev => prev.filter(l => !selectedItems.includes(l.id)));
      setSelectedItems([]);
    } else if (selectedComponent) {
      setComponents(prev => prev.filter(c => c.id !== selectedComponent));
      setSelectedComponent(null);
    }
    saveToHistory();
  };

  // ── Duplicate (paste in-place with offset) ─────────────
  const handleDuplicate = () => {
    handleCopy();
    handlePaste();
  };

  // ── Select All ─────────────────────────────────────────
  const handleSelectAll = () => {
    setSelectedItems([...components.map(c => c.id), ...lines.map(l => l.id)]);
    setSelectedComponent(null);
    setSelectedLine(null);
  };

  // ── Z-Order ────────────────────────────────────────────
  const handleBringToFront = () => {
    const ids = selectedItems.length > 0 ? selectedItems : selectedComponent ? [selectedComponent] : [];
    if (!ids.length) return;
    setComponents(prev => {
      const rest = prev.filter(c => !ids.includes(c.id));
      const top  = prev.filter(c =>  ids.includes(c.id));
      return [...rest, ...top];
    });
    saveToHistory();
  };
  const handleSendToBack = () => {
    const ids = selectedItems.length > 0 ? selectedItems : selectedComponent ? [selectedComponent] : [];
    if (!ids.length) return;
    setComponents(prev => {
      const rest = prev.filter(c => !ids.includes(c.id));
      const bot  = prev.filter(c =>  ids.includes(c.id));
      return [...bot, ...rest];
    });
    saveToHistory();
  };
  const handleBringForward = () => {
    const ids = selectedItems.length > 0 ? selectedItems : selectedComponent ? [selectedComponent] : [];
    if (!ids.length) return;
    setComponents(prev => {
      const arr = [...prev];
      ids.forEach(id => {
        const i = arr.findIndex(c => c.id === id);
        if (i < arr.length - 1) { [arr[i], arr[i+1]] = [arr[i+1], arr[i]]; }
      });
      return arr;
    });
    saveToHistory();
  };
  const handleSendBackward = () => {
    const ids = selectedItems.length > 0 ? selectedItems : selectedComponent ? [selectedComponent] : [];
    if (!ids.length) return;
    setComponents(prev => {
      const arr = [...prev];
      ids.forEach(id => {
        const i = arr.findIndex(c => c.id === id);
        if (i > 0) { [arr[i], arr[i-1]] = [arr[i-1], arr[i]]; }
      });
      return arr;
    });
    saveToHistory();
  };

  // ── Lock / Unlock ──────────────────────────────────────
  const handleToggleLock = () => {
    const ids = selectedItems.length > 0 ? selectedItems : selectedComponent ? [selectedComponent] : [];
    if (!ids.length) return;
    setLockedComponents(prev => {
      const next = new Set(prev);
      const allLocked = ids.every(id => next.has(id));
      if (allLocked) ids.forEach(id => next.delete(id));
      else           ids.forEach(id => next.add(id));
      return next;
    });
  };

  // ── Group ──────────────────────────────────────────────
  const handleGroup = () => {
    if (selectedItems.length < 2) return;
    const groupId = `group_${Date.now()}`;
    setComponents(prev => prev.map(c =>
      selectedItems.includes(c.id) ? { ...c, groupId } : c
    ));
    saveToHistory();
  };
  const handleUngroup = () => {
    const ids = selectedItems.length > 0 ? selectedItems : selectedComponent ? [selectedComponent] : [];
    setComponents(prev => prev.map(c =>
      ids.includes(c.id) ? { ...c, groupId: undefined } : c
    ));
    saveToHistory();
  };

  // ── Flip ──────────────────────────────────────────────
  const handleFlipH = () => {
    const ids = selectedItems.length > 0 ? selectedItems : selectedComponent ? [selectedComponent] : [];
    if (!ids.length) return;
    setComponents(prev => prev.map(c =>
      ids.includes(c.id) ? { ...c, flipH: !c.flipH } : c
    ));
    saveToHistory();
  };
  const handleFlipV = () => {
    const ids = selectedItems.length > 0 ? selectedItems : selectedComponent ? [selectedComponent] : [];
    if (!ids.length) return;
    setComponents(prev => prev.map(c =>
      ids.includes(c.id) ? { ...c, flipV: !c.flipV } : c
    ));
    saveToHistory();
  };

  // Canvas size — panels are now overlays so size is always consistent
  const getNormalizedCanvasSize = () => {
    // Always return the canonical (screen-independent) size so that saving
    // on any machine/zoom level never shifts the component coordinate space.
    return { w: canonicalWidth, h: canonicalHeight };
  };

  const handleSave = () => {
    const { w: canvasW, h: canvasH } = getNormalizedCanvasSize();

    const diagram = {
      id: config?.id || `scada_${Date.now()}`,
      title: diagramTitle,
      type: 'scada',
      scadaElements: components,
      scadaConnections: lines,
      customComponents: customComponents,
      scadaConfig: {
        backgroundImage,
        backgroundOpacity,
        backgroundFit,
        backgroundAlignment,
        backgroundTileSize,
        backgroundColor,
        canvasWidth: canvasW,
        canvasHeight: canvasH,
        panX: pan.x,
        panY: pan.y,
        zoom: zoom
      },
      width: config?.width || 12,
      height: config?.height || 8,
      refreshInterval,
      createdAt: new Date().toISOString()
    };
    if (onSave) onSave(diagram);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    showSaveToast('Diagram saved successfully!');
  };

  // Auto-save: debounce 2s after any change to components/lines
  useEffect(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    // Only update status if it changed — prevents a second re-render on every drag frame
    setAutoSaveStatus(prev => prev === 'saving' ? prev : 'saving');
    autoSaveTimer.current = setTimeout(() => {
      const { w: canvasW, h: canvasH } = getNormalizedCanvasSize();
      const diagram = {
        id: config?.id || `scada_${Date.now()}`,
        // Don't auto-save with an empty title — user may be mid-rename.
        // Fall back to the saved config title or a default to prevent the server
        // from filtering the panel out (server requires panel.id to exist).
        title: diagramTitle.trim() || config?.title || 'SCADA Diagram',
        type: 'scada',
        scadaElements: components,
        scadaConnections: lines,
        customComponents,
        scadaConfig: { backgroundImage, backgroundOpacity, backgroundFit, backgroundAlignment, backgroundTileSize, backgroundColor, canvasWidth: canvasW, canvasHeight: canvasH, panX: pan.x, panY: pan.y, zoom },
        width: config?.width || 12,
        height: config?.height || 8,
        createdAt: new Date().toISOString()
      };
      // Use onAutoSave (no modal close) if provided, otherwise skip silently
      if (onAutoSave) onAutoSave(diagram);
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(null), 2000);
    }, 2000);
    return () => clearTimeout(autoSaveTimer.current);
  }, [components, lines, diagramTitle]);

  const handleExport = () => {
    const diagram = {
      title: diagramTitle,
      components,
      lines,
      canvasWidth: canonicalWidth,
      canvasHeight: canonicalHeight,
      exportedAt: new Date().toISOString()
    };
    const dataBlob = new Blob([JSON.stringify(diagram, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${diagramTitle.replace(/\s+/g, '_')}_${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target.result);

        // Scale coordinates if the exported canvas size differs from the current one
        const srcW = imported.canvasWidth || 1600;
        const srcH = imported.canvasHeight || 900;
        const scaleX = canonicalWidth / srcW;
        const scaleY = canonicalHeight / srcH;
        const needsScale = Math.abs(scaleX - 1) > 0.001 || Math.abs(scaleY - 1) > 0.001;

        let comps = imported.components || [];
        let importedLines = imported.lines || [];

        if (needsScale) {
          comps = comps.map(c => ({
            ...c,
            x: c.x * scaleX,
            y: c.y * scaleY,
            width: c.width ? c.width * scaleX : c.width,
            height: c.height ? c.height * scaleY : c.height,
          }));
          importedLines = importedLines.map(l => ({
            ...l,
            startX: l.startX != null ? l.startX * scaleX : l.startX,
            startY: l.startY != null ? l.startY * scaleY : l.startY,
            endX: l.endX != null ? l.endX * scaleX : l.endX,
            endY: l.endY != null ? l.endY * scaleY : l.endY,
            controlPoints: l.controlPoints?.map(cp => ({
              ...cp,
              x: cp.x * scaleX,
              y: cp.y * scaleY,
            })),
          }));
        }

        setComponents(comps);
        setLines(importedLines);
        if (imported.title) setDiagramTitle(imported.title);
        saveToHistory();
      } catch (error) {
        alert('Failed to import diagram.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const getComponentDimensions = (comp) => {
    if (comp.type === 'navButton') {
      return { width: comp.width || 120, height: comp.height || 40 };
    } else if (comp.type === 'textbox') {
      return { width: comp.width || 120, height: comp.height || 40 };
    } else if (comp.type === 'customImage') {
      return { width: comp.width || 64, height: comp.height || 64 };
    } else if (comp.type === 'led') {
      const s = comp.width || 40;
      return { width: s, height: s };
    } else if (comp.type === 'drawLine') {
      return { width: comp.width || 310, height: comp.height || 10 };
    } else if (comp.type === 'shape') {
      return { width: comp.width || 100, height: comp.height || 100 };
    } else {
      const base = SVG_COMPONENTS[comp.type] || { width: 64, height: 64 };
      return { width: comp.width || base.width, height: comp.height || base.height };
    }
  };

  const getLineCoordinates = (line) => {
    // New format: free pipe with absolute coordinates
    if (line.startX !== undefined) {
      return { x1: line.startX, y1: line.startY, x2: line.endX, y2: line.endY };
    }
    // Legacy format: component-to-component
    const fromComp = components.find(c => c.id === line.from);
    const toComp = components.find(c => c.id === line.to);
    if (!fromComp || !toComp) return null;

    const fromDim = getComponentDimensions(fromComp);
    const toDim = getComponentDimensions(toComp);

    return {
      x1: fromComp.x + fromDim.width / 2,
      y1: fromComp.y + fromDim.height / 2,
      x2: toComp.x + toDim.width / 2,
      y2: toComp.y + toDim.height / 2
    };
  };

  // Generate SVG path for line with control points
  const getLinePath = (line) => {
    const coords = getLineCoordinates(line);
    if (!coords) return null;
    const { x1, y1, x2, y2 } = coords;
    return generatePipePath(x1, y1, x2, y2, line.controlPoints || []);
  };

  // Get the end point and angle for arrow placement
  const getLineEndPoint = (line) => {
    const coords = getLineCoordinates(line);
    if (!coords) return null;

    const { x1, y1, x2, y2 } = coords;
    const controlPoints = line.controlPoints || [];

    let endX = x2;
    let endY = y2;
    let prevX, prevY;

    if (controlPoints.length === 0) {
      prevX = x1;
      prevY = y1;
    } else {
      const lastCP = controlPoints[controlPoints.length - 1];
      prevX = lastCP.x;
      prevY = lastCP.y;
    }

    const angle = Math.atan2(endY - prevY, endX - prevX);
    
    return { x: endX, y: endY, angle };
  };

  // Handle drag-and-drop of custom images onto the canvas
  const handleCanvasDrop = (e) => {
    e.preventDefault();
    const { x, y } = screenToCanvas(e);

    // Drop from symbols panel
    const symbolKey = e.dataTransfer.getData('svgSymbol');
    if (symbolKey && SVG_COMPONENTS[symbolKey]) {
      const svgData = SVG_COMPONENTS[symbolKey];
      const dim = { width: svgData.width || 60, height: svgData.height || 60 };
      const newComponent = {
        id: `comp_${Date.now()}`,
        type: symbolKey,
        x: snapToGridFn(x - dim.width / 2),
        y: snapToGridFn(y - dim.height / 2),
        rotation: 0,
        label: svgData.name,
        width: dim.width,
        height: dim.height,
        tagName: '',
        defaultColor: svgData.color || '#64748b',
        colorMappings: [
          { value: '0', color: '#ef4444', label: 'Off' },
          { value: '1', color: '#10b981', label: 'On' }
        ],
      };
      setComponents(prev => [...prev, newComponent]);
      saveToHistory();
      setSelectedComponent(newComponent.id);
      setSelectedTool('select');
      return;
    }

    // Drop of custom image
    const data = e.dataTransfer.getData('customComponent');
    if (!data) return;
    const comp = JSON.parse(data);
    const newComponent = {
      id: `comp_${Date.now()}`,
      type: 'customImage',
      x: snapToGridFn(x - comp.width / 2),
      y: snapToGridFn(y - comp.height / 2),
      rotation: 0,
      label: comp.name,
      imageData: comp.imageData,
      width: comp.width,
      height: comp.height,
      tagName: '',
      colorMappings: [
        { value: '0', color: '#ef4444', label: 'Off' },
        { value: '1', color: '#10b981', label: 'On' }
      ],
      defaultColor: '#64748b'
    };
    setComponents([...components, newComponent]);
    saveToHistory();
    setSelectedComponent(newComponent.id);
  };


  return (
    <div style={{ 
      position: 'fixed', 
      inset: 0, 
      background: 'rgba(0,0,0,0.85)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000, 
      backdropFilter: 'blur(4px)' 
    }}>
      <div style={{ 
        background: theme.bg, 
        borderRadius: '12px', 
        width: '98vw', 
        height: '95vh', 
        display: 'flex', 
        flexDirection: 'column', 
        boxShadow: '0 25px 80px rgba(0,0,0,0.6)',
        border: `1px solid ${theme.border}`
      }}>
<ScadaToolbar
  // ── File ──────────────────────────────────────────────────────
  onClose={onClose}
  onSave={() => setShowSaveConfirm(true)}
  onExport={handleExport}
  onImport={handleImport}
  saved={saved}
  autoSaveStatus={autoSaveStatus}
  diagramTitle={diagramTitle}
  setDiagramTitle={setDiagramTitle}

  // ── History ───────────────────────────────────────────────────
  onUndo={handleUndo}
  onRedo={handleRedo}
  canUndo={historyIndex > 0}
  canRedo={historyIndex < history.length - 1}

  // ── Clipboard & Selection ─────────────────────────────────────
  onCut={handleCut}
  onCopy={handleCopy}
  onPaste={handlePaste}
  onDuplicate={handleDuplicate}
  onSelectAll={handleSelectAll}
  onDelete={() => {
    if (selectedItems.length > 0) {
      setComponents(prev => prev.filter(c => !selectedItems.includes(c.id)));
      setLines(prev => prev.filter(l => !selectedItems.includes(l.id)));
      setSelectedItems([]);
      saveToHistory();
    } else if (selectedComponent) {
      handleDeleteComponent();
    } else if (selectedLine) {
      handleDeleteLine();
    }
  }}
  hasSelection={selectedItems.length > 0 || !!selectedComponent || !!selectedLine}
  hasMultiSelection={selectedItems.length >= 2}

  // ── Tools ─────────────────────────────────────────────────────
  selectedTool={selectedTool}
  setSelectedTool={setSelectedTool}
  componentToAdd={componentToAdd}
  setComponentToAdd={setComponentToAdd}
  customComponentToAdd={customComponentToAdd}
  setCustomComponentToAdd={setCustomComponentToAdd}
  setDrawingLine={setDrawingLine}

  // ── Canvas ────────────────────────────────────────────────────
  onOpenBackground={() => setShowPropsPanel(true)}
  onOpenCustomImage={() => setShowPropsPanel(true)}

  // ── Zoom ──────────────────────────────────────────────────────
  zoom={zoom}
  onZoomIn={() => setZoom(z => Math.min(z + 0.1, 3))}
  onZoomOut={() => setZoom(z => Math.max(z - 0.1, 0.2))}
  onResetView={() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }}

  // ── Grid ──────────────────────────────────────────────────────
  showGrid={showGrid}
  snapToGrid={snapToGrid}
  onToggleGrid={() => setShowGrid(v => !v)}
  onToggleSnap={() => setSnapToGrid(v => !v)}

  // ── Panels ────────────────────────────────────────────────────
  showPropsPanel={showPropsPanel}
  showSymbols={showSymbols}
  onToggleProps={() => setShowPropsPanel(v => !v)}
  onToggleSymbols={() => setShowSymbols(v => !v)}

  // ── Align / Distribute ────────────────────────────────────────
  onAlignLeft={() => handleAlign('left')}
  onAlignCenterH={() => handleAlign('centerH')}
  onAlignRight={() => handleAlign('right')}
  onAlignTop={() => handleAlign('top')}
  onAlignCenterV={() => handleAlign('centerV')}
  onAlignBottom={() => handleAlign('bottom')}
  onDistributeH={() => handleDistribute('h')}
  onDistributeV={() => handleDistribute('v')}
  onMatchSize={handleMatchSize}
  onApplyProperties={handleApplyProperties}
  onApplyStyle={handleApplyStyle}

  // ── Z-Order ───────────────────────────────────────────────────
  onBringToFront={handleBringToFront}
  onBringForward={handleBringForward}
  onSendBackward={handleSendBackward}
  onSendToBack={handleSendToBack}

  // ── Transform ─────────────────────────────────────────────────
  onRotate={handleRotateComponent}
  onFlipH={handleFlipH}
  onFlipV={handleFlipV}

  // ── Group / Lock ──────────────────────────────────────────────
  onGroup={handleGroup}
  onUngroup={handleUngroup}
  onToggleLock={handleToggleLock}
  isLocked={
    selectedItems.length > 0
      ? selectedItems.every(id => lockedComponents.has(id))
      : !!(selectedComponent && lockedComponents.has(selectedComponent))
  }

  // ── Resolution ──────────────────────────────────────────────────
  canvasWidth={canonicalWidth}
  canvasHeight={canonicalHeight}
  onChangeResolution={(w, h) => {
    if (w === -1 && canvasContainerRef.current) {
      const r = canvasContainerRef.current.getBoundingClientRect();
      setCanonicalWidth(Math.round(r.width));
      setCanonicalHeight(Math.round(r.height));
    } else {
      setCanonicalWidth(w);
      setCanonicalHeight(h);
    }
  }}

  // ── Position / Size ────────────────────────────────────────────
  selectedComponentData={(() => {
    const id = selectedItems.length === 1 ? selectedItems[0] : selectedComponent;
    if (!id) return null;
    const comp = components.find(c => c.id === id) || lines.find(l => l.id === id);
    if (!comp) return null;
    const dim = getComponentDimensions(comp);
    return { x: comp.x ?? 0, y: comp.y ?? 0, w: dim.width, h: dim.height };
  })()}
  onUpdateComponentGeometry={(patch) => {
    const id = selectedItems.length === 1 ? selectedItems[0] : selectedComponent;
    if (!id) return;
    const update = c => {
      if (c.id !== id) return c;
      const next = { ...c };
      if (patch.x !== undefined) next.x = patch.x;
      if (patch.y !== undefined) next.y = patch.y;
      if (patch.w !== undefined) next.width = patch.w;
      if (patch.h !== undefined) next.height = patch.h;
      return next;
    };
    setComponents(prev => prev.map(update));
    setLines(prev => prev.map(update));
    saveToHistory();
  }}
/>

        {/* Thin sub-strip: refresh rate selector for this SCADA panel */}
        <div style={{
          display:'flex', alignItems:'center', gap:10,
          padding:'6px 14px', background: theme.card,
          borderBottom:`1px solid ${theme.border}`,
          fontSize:12, color: theme.textSecondary
        }}>
          <span>Live refresh:</span>
          <select
            value={refreshInterval}
            onChange={(e) => setRefreshInterval(parseInt(e.target.value, 10))}
            style={{
              padding:'4px 8px', borderRadius:6, fontSize:12,
              background: theme.bg, color: theme.text, border:`1px solid ${theme.border}`,
              cursor:'pointer', outline:'none'
            }}
            title="How often this SCADA panel pulls fresh tag values"
          >
            <option value={500}>0.5 s (very fast)</option>
            <option value={1000}>1 s</option>
            <option value={2000}>2 s (default)</option>
            <option value={5000}>5 s</option>
            <option value={10000}>10 s</option>
            <option value={30000}>30 s</option>
            <option value={60000}>1 min</option>
            <option value={300000}>5 min</option>
          </select>
          <span style={{ marginLeft:'auto', fontFamily:'monospace', opacity:0.7 }}>
            saved with diagram · applies after save
          </span>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
          {/* Left Sidebar — overlays canvas */}
          {showPropsPanel && <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0, zIndex: 10,
            width: '220px',
            borderRight: `1px solid ${theme.border}`,
            background: theme.card,
            overflowY: 'auto',
          }}>
            {/* Pipe Settings (when pipe tool is active) */}
            {selectedTool === 'pipe' && (
              <div style={{ padding: '16px', borderBottom: `1px solid ${theme.border}`, background: theme.hover }}>
                <h3 style={{ margin: '0 0 12px', color: theme.text, fontSize: '13px', fontWeight: '700' }}>
                  Pipe Settings
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '11px', fontWeight: '600' }}>
                      Pipe Style
                    </label>
                    <select
                      value={pipeConfig.style}
                      onChange={(e) => setPipeConfig({...pipeConfig, style: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '8px',
                        background: theme.bg,
                        border: `1px solid ${theme.border}`,
                        borderRadius: '6px',
                        color: theme.text,
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      {Object.entries(PIPE_STYLES).map(([key, style]) => (
                        <option key={key} value={key}>{style.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '11px', fontWeight: '600' }}>
                      Thickness: {pipeConfig.width}px
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="40"
                      step="2"
                      value={pipeConfig.width}
                      onChange={(e) => setPipeConfig({...pipeConfig, width: parseInt(e.target.value)})}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '11px', fontWeight: '600' }}>
                      Color
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="color"
                        value={pipeConfig.color}
                        onChange={(e) => setPipeConfig({...pipeConfig, color: e.target.value})}
                        style={{ width: '50px', height: '36px', border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        value={pipeConfig.color}
                        onChange={(e) => setPipeConfig({...pipeConfig, color: e.target.value})}
                        style={{
                          flex: 1,
                          padding: '8px',
                          background: theme.bg,
                          border: `1px solid ${theme.border}`,
                          borderRadius: '6px',
                          color: theme.text,
                          fontSize: '12px',
                          fontFamily: 'monospace'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={pipeConfig.flowEnabled}
                      onChange={(e) => setPipeConfig({...pipeConfig, flowEnabled: e.target.checked})}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '12px', color: theme.text }}>Enable Flow Animation</span>
                  </div>

                  {pipeConfig.flowEnabled && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: theme.textSecondary, fontSize: '11px', fontWeight: '600' }}>
                        Flow Speed: {pipeConfig.flowSpeed}x
                      </label>
                      <input
                        type="range"
                        min="0.5"
                        max="3"
                        step="0.5"
                        value={pipeConfig.flowSpeed}
                        onChange={(e) => setPipeConfig({...pipeConfig, flowSpeed: parseFloat(e.target.value)})}
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Background Color */}
            <div style={{ padding: '16px', borderBottom: `1px solid ${theme.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: theme.text }}>Background Color</label>
                {backgroundColor && (
                  <button onClick={() => setBackgroundColor('')} style={{ fontSize: '10px', color: theme.accent, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Clear</button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="color"
                  value={backgroundColor || '#000000'}
                  onChange={e => setBackgroundColor(e.target.value)}
                  style={{ width: '40px', height: '32px', border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer', padding: '2px' }}
                />
                <input
                  type="text"
                  value={backgroundColor}
                  onChange={e => setBackgroundColor(e.target.value)}
                  placeholder="No color"
                  style={{ flex: 1, padding: '6px 8px', background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: '6px', color: theme.text, fontSize: '12px', fontFamily: 'monospace', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            {/* Canvas Background */}
            <div style={{ padding: '16px', borderBottom: `1px solid ${theme.border}` }}>
              <BackgroundUploader
                backgroundImage={backgroundImage}
                backgroundOpacity={backgroundOpacity}
                backgroundFit={backgroundFit}
                backgroundAlignment={backgroundAlignment}
                backgroundTileSize={backgroundTileSize}
                onImageChange={setBackgroundImage}
                onOpacityChange={setBackgroundOpacity}
                onFitChange={setBackgroundFit}
                onAlignmentChange={setBackgroundAlignment}
                onTileSizeChange={setBackgroundTileSize}
                onClear={() => setBackgroundImage(null)}
                darkMode={darkMode}
              />
            </div>


            {/* Multi-Selection Panel */}
            {selectedItems.length > 1 && (() => {
              const selComps = components.filter(c => selectedItems.includes(c.id));
              const selLines = lines.filter(l => selectedItems.includes(l.id));
              const bounds = selComps.map(c => { const d = getComponentDimensions(c); return { x: c.x, y: c.y, w: d.width, h: d.height }; });
              const bboxX = bounds.length ? Math.min(...bounds.map(b => b.x)) : 0;
              const bboxY = bounds.length ? Math.min(...bounds.map(b => b.y)) : 0;

              const btnStyle = (active) => ({
                flex: 1, padding: '6px 4px', border: `1px solid ${theme.border}`, borderRadius: '4px',
                background: active ? theme.accent : theme.bg, color: active ? '#fff' : theme.text,
                cursor: 'pointer', fontSize: '11px', fontWeight: '600', display: 'flex',
                alignItems: 'center', justifyContent: 'center', minWidth: 0
              });

              return (
                <div style={{ borderTop: `1px solid ${theme.border}`, padding: '14px', background: theme.hover }}>
                  <h3 style={{ margin: '0 0 10px', color: theme.text, fontSize: '13px', fontWeight: '700' }}>
                    Multi-Selection
                  </h3>
                  <div style={{ fontSize: '11px', color: theme.textSecondary, marginBottom: '10px' }}>
                    {selComps.length > 0 && `${selComps.length} component${selComps.length > 1 ? 's' : ''}`}
                    {selComps.length > 0 && selLines.length > 0 && ', '}
                    {selLines.length > 0 && `${selLines.length} pipe${selLines.length > 1 ? 's' : ''}`}
                    {' selected'}
                  </div>

                  {/* Alignment — only if ≥2 components */}
                  {selComps.length >= 2 && (
                    <>
                      <div style={{ fontSize: '11px', color: theme.textSecondary, fontWeight: '600', marginBottom: '6px' }}>Align</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '4px', marginBottom: '8px' }}>
                        <button title="Align Left"   style={btnStyle()} onClick={() => handleAlign('left')}>⇤</button>
                        <button title="Center H"     style={btnStyle()} onClick={() => handleAlign('centerH')}>↔</button>
                        <button title="Align Right"  style={btnStyle()} onClick={() => handleAlign('right')}>⇥</button>
                        <button title="Align Top"    style={btnStyle()} onClick={() => handleAlign('top')}>⇡</button>
                        <button title="Center V"     style={btnStyle()} onClick={() => handleAlign('centerV')}>↕</button>
                        <button title="Align Bottom" style={btnStyle()} onClick={() => handleAlign('bottom')}>⇣</button>
                      </div>

                      {selComps.length >= 3 && (
                        <>
                          <div style={{ fontSize: '11px', color: theme.textSecondary, fontWeight: '600', marginBottom: '6px' }}>Distribute</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '10px' }}>
                            <button style={btnStyle()} onClick={() => handleDistribute('h')}>⇔ Horizontal</button>
                            <button style={btnStyle()} onClick={() => handleDistribute('v')}>⇕ Vertical</button>
                          </div>
                        </>
                      )}

                      {/* Position offset — move group */}
                      <div style={{ fontSize: '11px', color: theme.textSecondary, fontWeight: '600', marginBottom: '6px' }}>Position</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '10px' }}>
                        {[['X', bboxX], ['Y', bboxY]].map(([label, val]) => (
                          <div key={label}>
                            <div style={{ fontSize: '10px', color: theme.textSecondary, marginBottom: '2px' }}>{label}</div>
                            <input
                              type="number"
                              defaultValue={Math.round(val)}
                              key={`${label}-${Math.round(val)}`}
                              onBlur={(e) => {
                                const newVal = parseFloat(e.target.value);
                                if (isNaN(newVal)) return;
                                const delta = newVal - (label === 'X' ? bboxX : bboxY);
                                setComponents(prev => prev.map(c =>
                                  selectedItems.includes(c.id)
                                    ? { ...c, ...(label === 'X' ? { x: Math.max(0, c.x + delta) } : { y: Math.max(0, c.y + delta) }) }
                                    : c
                                ));
                                saveToHistory();
                              }}
                              style={{
                                width: '100%', padding: '5px 7px', background: theme.card,
                                border: `1px solid ${theme.border}`, borderRadius: '4px',
                                color: theme.text, fontSize: '11px', boxSizing: 'border-box'
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Common Properties for selected components */}
                  {selComps.length >= 1 && (() => {
                    const inputStyle = {
                      width: '100%', padding: '5px 7px', background: theme.card,
                      border: `1px solid ${theme.border}`, borderRadius: '4px',
                      color: theme.text, fontSize: '11px', boxSizing: 'border-box'
                    };
                    const labelStyle = { fontSize: '10px', color: theme.textSecondary, marginBottom: '2px' };

                    // Helper: get common value or null if mixed
                    const getCommon = (arr, key) => {
                      const vals = arr.map(c => c[key]);
                      const first = vals[0];
                      return vals.every(v => v === first) ? first : null;
                    };

                    const commonLabel = getCommon(selComps, 'label');
                    const commonRotation = getCommon(selComps, 'rotation');
                    const commonDefaultColor = getCommon(selComps, 'defaultColor');

                    // Width/Height: use getComponentDimensions for actual values
                    const dims = selComps.map(c => getComponentDimensions(c));
                    const commonWidth = dims.every(d => d.width === dims[0].width) ? dims[0].width : null;
                    const commonHeight = dims.every(d => d.height === dims[0].height) ? dims[0].height : null;

                    // Check if any selected are rotatable (not textbox/navButton/drawLine/shape)
                    const nonRotatable = new Set(['textbox', 'navButton', 'drawLine', 'shape']);
                    const rotatableComps = selComps.filter(c => !nonRotatable.has(c.type));
                    const hasRotatable = rotatableComps.length > 0;

                    // Check if any have defaultColor
                    const colorComps = selComps.filter(c => c.defaultColor !== undefined);
                    const hasColorComps = colorComps.length > 0;

                    return (
                      <>
                        <div style={{ fontSize: '11px', color: theme.textSecondary, fontWeight: '600', marginBottom: '6px', marginTop: '4px' }}>
                          Common Properties
                        </div>

                        {/* Label */}
                        <div style={{ marginBottom: '6px' }}>
                          <div style={labelStyle}>Label</div>
                          <input
                            type="text"
                            placeholder={commonLabel === null ? 'Mixed' : ''}
                            defaultValue={commonLabel ?? ''}
                            key={`ml-${commonLabel}`}
                            onBlur={(e) => {
                              const val = e.target.value;
                              if (!val && commonLabel === null) return;
                              setComponents(prev => prev.map(c =>
                                selectedItems.includes(c.id) ? { ...c, label: val } : c
                              ));
                              saveToHistory();
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            style={inputStyle}
                          />
                        </div>

                        {/* Width & Height */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '6px' }}>
                          <div>
                            <div style={labelStyle}>W</div>
                            <input
                              type="number"
                              placeholder={commonWidth === null ? 'Mixed' : ''}
                              defaultValue={commonWidth ?? ''}
                              key={`mw-${commonWidth}`}
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value);
                                if (isNaN(val) || val <= 0) return;
                                setComponents(prev => prev.map(c =>
                                  selectedItems.includes(c.id) ? { ...c, width: val } : c
                                ));
                                saveToHistory();
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              style={inputStyle}
                            />
                          </div>
                          <div>
                            <div style={labelStyle}>H</div>
                            <input
                              type="number"
                              placeholder={commonHeight === null ? 'Mixed' : ''}
                              defaultValue={commonHeight ?? ''}
                              key={`mh-${commonHeight}`}
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value);
                                if (isNaN(val) || val <= 0) return;
                                setComponents(prev => prev.map(c =>
                                  selectedItems.includes(c.id) ? { ...c, height: val } : c
                                ));
                                saveToHistory();
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              style={inputStyle}
                            />
                          </div>
                        </div>

                        {/* Rotation — only if some are rotatable */}
                        {hasRotatable && (
                          <div style={{ marginBottom: '6px' }}>
                            <div style={labelStyle}>Rotation (°)</div>
                            <input
                              type="number"
                              placeholder={commonRotation === null ? 'Mixed' : ''}
                              defaultValue={commonRotation ?? ''}
                              key={`mr-${commonRotation}`}
                              step={90}
                              onBlur={(e) => {
                                const val = parseFloat(e.target.value);
                                if (isNaN(val)) return;
                                setComponents(prev => prev.map(c =>
                                  selectedItems.includes(c.id) && !nonRotatable.has(c.type)
                                    ? { ...c, rotation: val % 360 }
                                    : c
                                ));
                                saveToHistory();
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              style={inputStyle}
                            />
                          </div>
                        )}

                        {/* Default Color */}
                        {hasColorComps && (
                          <div style={{ marginBottom: '10px' }}>
                            <div style={labelStyle}>Default Color</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <input
                                type="color"
                                value={commonDefaultColor ?? '#64748b'}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setComponents(prev => prev.map(c =>
                                    selectedItems.includes(c.id) && c.defaultColor !== undefined
                                      ? { ...c, defaultColor: val }
                                      : c
                                  ));
                                }}
                                onBlur={() => saveToHistory()}
                                style={{
                                  width: '28px', height: '28px', padding: '0', border: `1px solid ${theme.border}`,
                                  borderRadius: '4px', cursor: 'pointer', background: 'transparent'
                                }}
                              />
                              <span style={{ fontSize: '11px', color: theme.textSecondary }}>
                                {commonDefaultColor ?? 'Mixed'}
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  <button
                    onClick={() => {
                      setComponents(prev => prev.filter(c => !selectedItems.includes(c.id)));
                      setLines(prev => prev.filter(l => !selectedItems.includes(l.id)));
                      setSelectedItems([]);
                      saveToHistory();
                    }}
                    style={{
                      width: '100%', padding: '8px 12px', background: theme.danger, border: 'none',
                      borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px',
                      fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                    }}
                  >
                    <Trash2 size={14} />
                    Delete Selected
                  </button>
                </div>
              );
            })()}

            {/* Properties Panel */}
            {!selectedItems.length && selectedComponent && (() => {
              const selectedComp = components.find(c => c.id === selectedComponent);
              const isTextbox = selectedComp?.type === 'textbox';
              const isCustomImage = selectedComp?.type === 'customImage';
              const isNavButton = selectedComp?.type === 'navButton';
              const isDrawLine = selectedComp?.type === 'drawLine';
              const isShape = selectedComp?.type === 'shape';

              return (
                <div style={{ borderTop: `1px solid ${theme.border}`, padding: '16px', background: theme.hover }}>
                  <h3 style={{ margin: '0 0 12px', color: theme.text, fontSize: '13px', fontWeight: '700' }}>
                    {isTextbox ? 'Text Box Properties' : isCustomImage ? 'Custom Image Properties' : isNavButton ? 'Nav Button Properties' : isDrawLine ? 'Line Properties' : isShape ? 'Shape Properties' : 'Properties'}
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* Configure Navigation button for navButton */}
                    {isNavButton && (
                      <button
                        onClick={() => { setEditingNavButton(selectedComp); setShowNavButtonModal(true); }}
                        style={{
                          padding: '10px 12px', background: theme.accent, border: 'none',
                          borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px',
                          fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px'
                        }}
                      >
                        <Navigation size={16} />
                        Configure Navigation
                      </button>
                    )}
                    {/* Edit Text button for textbox */}
                    {isTextbox && (
                      <button
                        onClick={() => {
                          setEditingTextBox(selectedComp);
                          setShowTextBoxModal(true);
                        }}
                        style={{
                          padding: '10px 12px',
                          background: theme.accent,
                          border: 'none',
                          borderRadius: '6px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <Type size={16} />
                        Edit Text
                      </button>
                    )}
                    {/* Configure Line */}
                    {isDrawLine && (
                      <button onClick={() => { setEditingLineComp(selectedComp); setShowLineModal(true); }}
                        style={{ padding: '10px 12px', background: theme.accent, border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Settings size={16} />
                        Configure Line
                      </button>
                    )}
                    {/* Configure Shape */}
                    {isShape && (
                      <button onClick={() => { setEditingShapeComp(selectedComp); setShowShapeModal(true); }}
                        style={{ padding: '10px 12px', background: theme.accent, border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Settings size={16} />
                        Configure Shape
                      </button>
                    )}
                    {/* Configure Tag for non-textbox, non-navButton, non-line, non-shape components */}
                    {!isTextbox && !isNavButton && !isDrawLine && !isShape && (
                      <button
                        onClick={handleConfigureTag}
                        style={{
                          padding: '10px 12px',
                          background: theme.bg,
                          border: `1px solid ${theme.border}`,
                          borderRadius: '6px',
                          color: theme.text,
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <Tag size={16} />
                        Configure Tag
                      </button>
                    )}
                    {/* Rotate - only for non-textbox, non-navButton, non-line, non-shape */}
                    {!isTextbox && !isNavButton && !isDrawLine && !isShape && (
                      <button
                        onClick={handleRotateComponent}
                        style={{
                          padding: '10px 12px',
                          background: theme.bg,
                          border: `1px solid ${theme.border}`,
                          borderRadius: '6px',
                          color: theme.text,
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <RotateCw size={16} />
                        Rotate 90°
                      </button>
                    )}
                    <button
                      onClick={handleCopyComponent}
                      style={{
                        padding: '10px 12px',
                        background: theme.bg,
                        border: `1px solid ${theme.border}`,
                        borderRadius: '6px',
                        color: theme.text,
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Copy size={16} />
                      Duplicate
                    </button>
                    <button
                      onClick={handleDeleteComponent}
                      style={{
                        padding: '10px 12px',
                        background: theme.danger,
                        border: 'none',
                        borderRadius: '6px',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              );
            })()}

            {!selectedItems.length && selectedLine && (() => {
              const selectedPipe = lines.find(l => l.id === selectedLine);
              return (
                <div style={{ borderTop: `1px solid ${theme.border}`, padding: '16px', background: theme.hover }}>
                  <h3 style={{ margin: '0 0 12px', color: theme.text, fontSize: '13px', fontWeight: '700' }}>
                    Pipe Properties
                  </h3>
                  <div style={{ marginBottom: '12px', padding: '8px', background: theme.bg, borderRadius: '6px' }}>
                    <div style={{ fontSize: '11px', color: theme.textSecondary, marginBottom: '4px' }}>
                      Control Points: {(selectedPipe?.controlPoints || []).length}
                    </div>
                    <div style={{ fontSize: '10px', color: theme.textSecondary, lineHeight: '1.4' }}>
                      • Double-click pipe to add bend point
                      <br />
                      • Drag points to reshape
                      <br />
                      • Click × to remove point
                    </div>
                  </div>
                  {/* Quick style replace */}
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '11px', color: theme.textSecondary, fontWeight: '600', marginBottom: '6px' }}>Quick Style</div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {['single', 'double', 'insulated'].map(s => (
                        <button key={s} onClick={() => {
                          setLines(lines.map(l => l.id === selectedLine ? { ...l, style: s } : l));
                          saveToHistory();
                        }} style={{
                          flex: 1, padding: '6px 4px', fontSize: '10px', fontWeight: '600',
                          background: selectedPipe?.style === s ? theme.accent : theme.bg,
                          color: selectedPipe?.style === s ? 'white' : theme.text,
                          border: `1px solid ${selectedPipe?.style === s ? theme.accent : theme.border}`,
                          borderRadius: '5px', cursor: 'pointer', textTransform: 'capitalize'
                        }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (selectedPipe) {
                        setEditingPipe(selectedPipe);
                        setShowPipeModal(true);
                      }
                    }}
                    style={{
                      padding: '10px 12px',
                      background: theme.accent,
                      border: 'none',
                      borderRadius: '6px',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      fontWeight: '600',
                      width: '100%',
                      marginBottom: '8px'
                    }}
                  >
                    <Settings size={16} />
                    Configure Pipe
                  </button>
                  {/* Tag assignment for pipe flow control */}
                  <div style={{ marginBottom: '8px', padding: '10px', background: theme.bg, borderRadius: '6px', border: `1px solid ${theme.border}` }}>
                    <div style={{ fontSize: '11px', color: theme.textSecondary, fontWeight: '600', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Tag size={12} />
                      Flow Tag Control
                    </div>
                    <SearchableTagSelect
                      value={selectedPipe?.tagName || ''}
                      onChange={(val) => {
                        setLines(lines.map(l => l.id === selectedLine ? { ...l, tagName: val } : l));
                        saveToHistory();
                      }}
                      tags={availableTags}
                      placeholder="No tag (use flow setting)"
                      darkMode={darkMode}
                      style={{ marginBottom: '6px' }}
                    />
                    {selectedPipe?.tagName && (
                      <div>
                        <label style={{ display: 'block', fontSize: '10px', color: theme.textSecondary, marginBottom: '3px' }}>Flow when tag equals:</label>
                        <input
                          type="text"
                          value={selectedPipe?.flowOnValue ?? '1'}
                          onChange={(e) => {
                            setLines(lines.map(l => l.id === selectedLine ? { ...l, flowOnValue: e.target.value } : l));
                          }}
                          placeholder="e.g. 1"
                          style={{ width: '100%', padding: '6px 8px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '4px', color: theme.text, fontSize: '11px' }}
                        />
                        {selectedPipe?.tagName && tagValues[selectedPipe.tagName] !== undefined && (
                          <div style={{ marginTop: '4px', fontSize: '10px', color: theme.textSecondary }}>
                            Current value: <span style={{ color: String(tagValues[selectedPipe.tagName]) === String(selectedPipe.flowOnValue ?? '1') ? theme.success : theme.text, fontWeight: '600' }}>{tagValues[selectedPipe.tagName]}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => {
                      if (selectedPipe) {
                        const coords = getLineCoordinates(selectedPipe);
                        if (coords) {
                          const midX = (coords.x1 + coords.x2) / 2;
                          const midY = (coords.y1 + coords.y2) / 2;
                          handleAddControlPoint(selectedLine, midX, midY);
                        }
                      }
                    }}
                    style={{
                      padding: '10px 12px',
                      background: theme.bg,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '6px',
                      color: theme.text,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      fontWeight: '600',
                      width: '100%',
                      marginBottom: '8px'
                    }}
                  >
                    <Plus size={16} />
                    Add Bend Point
                  </button>
                  <button
                    onClick={handleDeleteLine}
                    style={{
                      padding: '10px 12px',
                      background: theme.danger,
                      border: 'none',
                      borderRadius: '6px',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      fontWeight: '600',
                      width: '100%'
                    }}
                  >
                    <Trash2 size={16} />
                    Delete Pipe
                  </button>
                </div>
              );
            })()}
          </div>}

          {/* Canvas Area */}
          <div ref={canvasContainerRef} style={{
            flex: 1, position: 'relative', overflow: 'hidden',
            background: theme.bg,
          }}>
            {/* Status Banner */}
            {selectedTool === 'component' && componentToAdd && (
              <div style={{ 
                position: 'absolute', 
                top: '16px', 
                left: '50%', 
                transform: 'translateX(-50%)', 
                padding: '10px 20px', 
                background: theme.accent, 
                color: 'white', 
                borderRadius: '8px', 
                fontSize: '13px', 
                fontWeight: '600', 
                zIndex: 10,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}>
                Click to place {SVG_COMPONENTS[componentToAdd].name}
              </div>
            )}

            {selectedTool === 'pipe' && !drawingLine && (
              <div style={{
                position: 'absolute',
                top: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '10px 20px',
                background: theme.accent,
                color: 'white',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                zIndex: 10,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}>
                Click anywhere to start pipe
              </div>
            )}

            {selectedTool === 'pipe' && drawingLine && (
              <div style={{
                position: 'absolute',
                top: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '10px 20px',
                background: theme.success,
                color: 'white',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                zIndex: 10,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}>
                Click anywhere to finish pipe
              </div>
            )}

            {selectedTool === 'customImage' && customComponentToAdd && (
              <div style={{
                position: 'absolute',
                top: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '10px 20px',
                background: theme.accent,
                color: 'white',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                zIndex: 10,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}>
                Click to place {customComponentToAdd.name}
              </div>
            )}

            {selectedTool === 'textbox' && (
              <div style={{
                position: 'absolute',
                top: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '10px 20px',
                background: theme.accent,
                color: 'white',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                zIndex: 10,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}>
                Click to place text box
              </div>
            )}

            {selectedTool === 'led' && (
              <div style={{
                position: 'absolute',
                top: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '10px 20px',
                background: theme.accent,
                color: 'white',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                zIndex: 10,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}>
                Click to place LED
              </div>
            )}

            {selectedTool === 'navButton' && (
              <div style={{
                position: 'absolute',
                top: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '10px 20px',
                background: '#667eea',
                color: 'white',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                zIndex: 10,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}>
                Click to place Navigation Button
              </div>
            )}

            {selectedTool === 'drawLine' && (
              <div style={{ position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)', padding: '10px 20px', background: theme.accent, color: 'white', borderRadius: '8px', fontSize: '13px', fontWeight: '600', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                Click to place Line
              </div>
            )}

            {selectedTool === 'shape' && (
              <div style={{ position: 'absolute', top: '16px', left: '50%', transform: 'translateX(-50%)', padding: '10px 20px', background: theme.accent, color: 'white', borderRadius: '8px', fontSize: '13px', fontWeight: '600', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                Click to place Shape
              </div>
            )}

            {/* Canvas SVG */}
            <svg
              ref={canvasRef}
              width="100%"
              height="100%"
              viewBox={`0 0 ${canonicalWidth} ${canonicalHeight}`}
              preserveAspectRatio="xMidYMid meet"
              tabIndex={0}
              onMouseDown={(e) => { canvasRef.current?.focus({ preventScroll: true }); handleCanvasMouseDown(e); }}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={handleCanvasMouseUp}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleCanvasDrop}
              style={{
                cursor: selectedTool === 'component' ? 'crosshair' :
                       selectedTool === 'customImage' ? 'crosshair' :
                       selectedTool === 'pipe' ? 'crosshair' :
                       selectedTool === 'textbox' ? 'crosshair' :
                       selectedTool === 'pan' || isPanning ? 'grab' :
                       'default',
                display: 'block',
                outline: 'none',
              }}
            >
              {/* Background color — fills entire SVG so areas outside canvas aren't empty */}
              <rect width="100%" height="100%" fill={backgroundColor || (darkMode ? '#0f1117' : '#f1f5f9')} style={{ pointerEvents: 'none' }} />

              {/* Background Image — fixed to canvas, does NOT move with pan/zoom */}
              {backgroundImage && backgroundFit === 'tile' ? (
                <>
                  <defs>
                    <pattern id="scada-bg-tile" x="0" y="0" width={backgroundTileSize} height={backgroundTileSize} patternUnits="userSpaceOnUse">
                      <image href={backgroundImage} x="0" y="0" width={backgroundTileSize} height={backgroundTileSize} preserveAspectRatio="xMidYMid meet" opacity={backgroundOpacity} />
                    </pattern>
                  </defs>
                  <rect x={0} y={0} width={canonicalWidth} height={canonicalHeight} fill="url(#scada-bg-tile)" style={{ pointerEvents: 'none' }} />
                </>
              ) : backgroundImage ? (
                <image
                  href={backgroundImage}
                  x={0} y={0}
                  width={canonicalWidth} height={canonicalHeight}
                  preserveAspectRatio={
                    backgroundFit === 'contain' ? `${backgroundAlignment} meet` :
                    backgroundFit === 'cover' ? `${backgroundAlignment} slice` :
                    'none'
                  }
                  opacity={backgroundOpacity}
                  style={{ pointerEvents: 'none' }}
                />
              ) : null}

              {/* Grid Pattern — outside transform so dots stay fixed size */}
              {showGrid && (
                <defs>
                  <pattern id="grid" width={gridSize * zoom} height={gridSize * zoom} patternUnits="userSpaceOnUse">
                    <circle
                      cx={gridSize * zoom / 2}
                      cy={gridSize * zoom / 2}
                      r="1"
                      fill={darkMode ? '#2d3348' : '#d1d5db'}
                      opacity="0.5"
                    />
                  </pattern>
                </defs>
              )}
              {showGrid && <rect width="100%" height="100%" fill="url(#grid)" />}

              <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
                {/* Lines */}
                {lines.map(line => {
                  const coords = getLineCoordinates(line);
                  const path = getLinePath(line);
                  if (!coords || !path) return null;

                  const isSelected = selectedLine === line.id || selectedItems.includes(line.id);
                  const isEditing = editingLine === line.id;

                  // Resolve flow: tag overrides static flowEnabled setting
                  const resolvedFlowEnabled = line.flowEnabled && (
                    !line.tagName || String(tagValues[line.tagName]) === String(line.flowOnValue ?? '1')
                  );

                  return (
                    <g key={line.id}>
                      {/* 3D pipe rendering - no pointer events */}
                      <g style={{ pointerEvents: 'none' }}>
                        <PipeElement
                          id={line.id}
                          path={path}
                          width={line.width || DEFAULT_PIPE.width}
                          color={line.color || DEFAULT_PIPE.color}
                          style={['single','double','insulated'].includes(line.style) ? line.style : 'single'}
                          flowEnabled={resolvedFlowEnabled}
                          flowSpeed={line.flowSpeed || DEFAULT_PIPE.flowSpeed}
                          flowDirection={line.flowDirection || DEFAULT_PIPE.flowDirection}
                          flowColor={line.flowColor || DEFAULT_PIPE.flowColor}
                          isSelected={isSelected}
                          darkMode={darkMode}
                        />
                      </g>

                      {/* Invisible wider path for easier clicking/dragging */}
                      <path
                        d={path}
                        stroke="transparent"
                        strokeWidth={Math.max((line.width || DEFAULT_PIPE.width) + 12, 20)}
                        fill="none"
                        style={{ cursor: selectedTool === 'select' ? 'move' : 'pointer' }}
                        onClick={(e) => handleLineClick(e, line)}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          const { x, y } = screenToCanvas(e);
                          handleAddControlPoint(line.id, x, y);
                        }}
                        onMouseDown={(e) => {
                          if (selectedTool !== 'select') return;
                          e.stopPropagation();
                          setDraggingLine({
                            lineId: line.id,
                            startMouseX: e.clientX,
                            startMouseY: e.clientY,
                            origStartX: line.startX,
                            origStartY: line.startY,
                            origEndX: line.endX,
                            origEndY: line.endY,
                            origControlPoints: [...(line.controlPoints || [])]
                          });
                          setSelectedLine(line.id);
                          setSelectedComponent(null);
                          setEditingLine(line.id);
                        }}
                        data-line="true"
                      />

                      {/* Control points (when selected) */}
                      {isEditing && (line.controlPoints || []).map((cp, index) => (
                        <g key={`cp-${index}`}>
                          {/* Control point handle */}
                          <circle 
                            cx={cp.x} 
                            cy={cp.y} 
                            r="8" 
                            fill="white"
                            stroke={theme.accent}
                            strokeWidth="2"
                            style={{ cursor: 'move' }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handleLinePointMouseDown(e, line.id, index);
                            }}
                          />
                          {/* Delete button for control point */}
                          <g
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              const newControlPoints = line.controlPoints.filter((_, i) => i !== index);
                              setLines(lines.map(l => l.id === line.id ? { ...l, controlPoints: newControlPoints } : l));
                              saveToHistory();
                            }}
                          >
                            <circle 
                              cx={cp.x + 12} 
                              cy={cp.y - 12} 
                              r="8" 
                              fill={theme.danger}
                            />
                            <text
                              x={cp.x + 12}
                              y={cp.y - 8}
                              textAnchor="middle"
                              fill="white"
                              fontSize="12"
                              fontWeight="bold"
                              style={{ pointerEvents: 'none' }}
                            >
                              ×
                            </text>
                          </g>
                        </g>
                      ))}
                      
                      {/* Start and end point drag handles (when selected) */}
                      {isSelected && (
                        <>
                          <circle cx={coords.x1} cy={coords.y1} r="8" fill={theme.success}
                            stroke="white" strokeWidth="2" style={{ cursor: 'move' }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              setDraggingLinePoint({ lineId: line.id, pointIndex: 'start' });
                            }}
                          />
                          <circle cx={coords.x2} cy={coords.y2} r="8" fill={theme.danger}
                            stroke="white" strokeWidth="2" style={{ cursor: 'move' }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              setDraggingLinePoint({ lineId: line.id, pointIndex: 'end' });
                            }}
                          />
                        </>
                      )}
                    </g>
                  );
                })}

                {/* Drawing Pipe Preview */}
                {drawingLine && (
                  <g>
                    <line
                      x1={drawingLine.startX}
                      y1={drawingLine.startY}
                      x2={drawingLine.endX}
                      y2={drawingLine.endY}
                      stroke={pipeConfig.color}
                      strokeWidth={pipeConfig.width}
                      strokeDasharray="10,5"
                      strokeLinecap="butt"
                      opacity="0.6"
                    />
                    <circle cx={drawingLine.startX} cy={drawingLine.startY} r={pipeConfig.width / 2} fill={pipeConfig.color} />
                  </g>
                )}

                {/* Components */}
                {components.map(comp => {
                  const dim = getComponentDimensions(comp);
                  const isSelected = selectedComponent === comp.id || selectedItems.includes(comp.id);
                  const componentColor = getComponentColor(comp);
                  const status = getComponentStatus(comp);

                  // Render Navigation Button component
                  if (comp.type === 'navButton') {
                    const btnW = dim.width;
                    const btnH = dim.height;
                    const btnFill = comp.transparentBg ? 'transparent' : (comp.buttonColor || '#667eea');
                    const btnTextColor = comp.textColor || '#ffffff';
                    const btnFontSize = comp.fontSize || 13;
                    const btnFontWeight = comp.fontWeight || '700';
                    const btnTextAnchor = comp.textAlign === 'left' ? 'start' : comp.textAlign === 'right' ? 'end' : 'middle';
                    const btnTextX = comp.textAlign === 'left' ? 8 : comp.textAlign === 'right' ? btnW - 8 : btnW / 2;
                    return (
                      <g key={comp.id} transform={`translate(${comp.x}, ${comp.y})`} data-component="true">
                        <rect x={0} y={0} width={btnW} height={btnH} fill={btnFill} rx={8} stroke={comp.transparentBg ? '#667eea' : 'none'} strokeWidth={comp.transparentBg ? 1 : 0} strokeDasharray={comp.transparentBg ? '4,2' : 'none'} style={{ pointerEvents: 'none' }} />
                        <text x={btnTextX} y={btnH / 2 + btnFontSize * 0.35} textAnchor={btnTextAnchor} fill={btnTextColor} fontSize={btnFontSize} fontWeight={btnFontWeight}
                          style={{ pointerEvents: 'none', userSelect: 'none' }}>
                          {comp.label || 'Navigate'}
                        </text>
                        <rect x={0} y={0} width={btnW} height={btnH} fill="transparent"
                          style={{ cursor: selectedTool === 'select' ? 'move' : selectedTool === 'pipe' ? 'pointer' : 'default' }}
                          onMouseDown={(e) => handleComponentMouseDown(e, comp)}
                          onDoubleClick={(e) => { e.stopPropagation(); setEditingNavButton(comp); setShowNavButtonModal(true); }}
                        />
                        {isSelected && (
                          <ResizeHandles x={0} y={0} width={btnW} height={btnH} darkMode={darkMode}
                            onResizeStart={(e, handle) => {
                              e.stopPropagation();
                              setResizingComponent({ id: comp.id, handle, startX: e.clientX, startY: e.clientY, originalBounds: { x: comp.x, y: comp.y, width: btnW, height: btnH } });
                            }}
                          />
                        )}
                      </g>
                    );
                  }

                  // Render TextBox component
                  if (comp.type === 'textbox') {
                    return (
                      <g
                        key={comp.id}
                        data-component="true"
                      >
                        <TextBoxElement
                          element={comp}
                          tagValues={tagValues}
                          darkMode={darkMode}
                          isSelected={isSelected}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (selectedTool === 'select') {
                              setSelectedComponent(comp.id);
                              setSelectedLine(null);
                            }
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            setEditingTextBox(comp);
                            setShowTextBoxModal(true);
                          }}
                        />
                        {/* Interaction overlay for drag */}
                        <rect
                          x={comp.x}
                          y={comp.y}
                          width={dim.width}
                          height={dim.height}
                          fill="transparent"
                          style={{
                            cursor: selectedTool === 'select' ? 'move' :
                                   selectedTool === 'pipe' ? 'pointer' : 'default'
                          }}
                          onMouseDown={(e) => handleComponentMouseDown(e, comp)}
                          onDoubleClick={(e) => { e.stopPropagation(); setEditingTextBox(comp); setShowTextBoxModal(true); }}
                        />
                        {/* Resize handles for selected textbox */}
                        {isSelected && (
                          <ResizeHandles
                            x={comp.x}
                            y={comp.y}
                            width={dim.width}
                            height={dim.height}
                            darkMode={darkMode}
                            onResizeStart={(e, handle) => {
                              e.stopPropagation();
                              setResizingComponent({
                                id: comp.id,
                                handle,
                                startX: e.clientX,
                                startY: e.clientY,
                                originalBounds: { x: comp.x, y: comp.y, width: dim.width, height: dim.height }
                              });
                            }}
                          />
                        )}
                      </g>
                    );
                  }

                  // Render Custom Image component
                  if (comp.type === 'customImage') {
                    const flipTransform = `translate(${comp.flipH ? dim.width : 0}, ${comp.flipV ? dim.height : 0}) scale(${comp.flipH ? -1 : 1}, ${comp.flipV ? -1 : 1})`;
                    return (
                      <g
                        key={comp.id}
                        transform={`translate(${comp.x}, ${comp.y})`}
                        data-component="true"
                        style={lockedComponents.has(comp.id) ? { outline: '2px solid #f59e0b' } : undefined}
                      >
                        <g transform={`rotate(${comp.rotation || 0}, ${dim.width/2}, ${dim.height/2})`}>
                          <g transform={flipTransform}>
                          <rect
                            x={0}
                            y={0}
                            width={dim.width}
                            height={dim.height}
                            fill="transparent"
                            style={{
                              cursor: selectedTool === 'select' ? 'move' :
                                     selectedTool === 'pipe' ? 'pointer' : 'default'
                            }}
                            onMouseDown={(e) => handleComponentMouseDown(e, comp)}
                            onDoubleClick={(e) => { e.stopPropagation(); if (PUMP_TYPES.has(comp.type)) { setEditingPumpComponent(comp); setShowPumpModal(true); } else if (VALVE_TYPES.has(comp.type)) { setEditingValveComponent(comp); setShowValveModal(true); } else if (CONTROL_VALVE_TYPES.has(comp.type)) { setEditingControlValveComponent(comp); setShowControlValveModal(true); } else { setEditingComponentTag(comp); setShowTagModal(true); } }}
                          />
                          <image
                            href={comp.imageData}
                            width={dim.width}
                            height={dim.height}
                            style={{ pointerEvents: 'none' }}
                          />
                          {comp.tagName && (
                            <rect
                              x={0}
                              y={0}
                              width={dim.width}
                              height={dim.height}
                              fill={componentColor}
                              opacity="0.4"
                              style={{ mixBlendMode: 'multiply', pointerEvents: 'none' }}
                            />
                          )}
                          </g>{/* end flipTransform */}
                        </g>
                        {/* Lock indicator */}
                        {lockedComponents.has(comp.id) && (
                          <rect x={0} y={0} width={dim.width} height={dim.height} fill="none" stroke="#f59e0b" strokeWidth="2" style={{ pointerEvents: 'none' }}/>
                        )}
                        {/* Label */}
                        {comp.label && (
                          <text
                            x={dim.width / 2}
                            y={dim.height + 16}
                            textAnchor="middle"
                            fill={theme.text}
                            fontSize="11"
                            fontWeight="600"
                            style={{ pointerEvents: 'none', userSelect: 'none' }}
                          >
                            {comp.label}
                          </text>
                        )}
                        {/* Resize handles */}
                        {isSelected && (
                          <ResizeHandles
                            x={0}
                            y={0}
                            width={dim.width}
                            height={dim.height}
                            rotation={comp.rotation}
                            darkMode={darkMode}
                            onResizeStart={(e, handle) => {
                              e.stopPropagation();
                              setResizingComponent({
                                id: comp.id,
                                handle,
                                startX: e.clientX,
                                startY: e.clientY,
                                originalBounds: { x: comp.x, y: comp.y, width: dim.width, height: dim.height }
                              });
                            }}
                          />
                        )}
                      </g>
                    );
                  }

                  // ── Render drawLine component ──────────────────────────────
                  if (comp.type === 'drawLine') {
                    const w = dim.width;
                    const h = dim.height;
                    const isH = comp.orientation !== 'vertical';
                    const t = comp.lineThickness || 2;
                    const dw = comp.dotWidth || 5;
                    const dsh = comp.dashWidth || 10;
                    const gw = comp.gapWidth || 5;
                    let dashArray;
                    if (comp.lineStyle === 'dotted') dashArray = `${dw} ${dw}`;
                    else if (comp.lineStyle === 'dashed') dashArray = `${dsh} ${gw}`;
                    else dashArray = undefined;
                    const period = comp.lineStyle === 'dotted' ? dw * 2 : (dsh + gw);
                    const animDur = comp.animEnabled && period > 0 && comp.animSpeed > 0
                      ? `${period / comp.animSpeed}s` : null;
                    const x1 = isH ? 0     : w / 2;
                    const y1 = isH ? h / 2 : 0;
                    const x2 = isH ? w     : w / 2;
                    const y2 = isH ? h / 2 : h;
                    const lineColor = comp.highColor || '#000000';
                    const sm = comp.startMarker || 'none';
                    const em = comp.endMarker || 'none';
                    const markerId = (type, end) => `marker-${comp.id}-${end}-${type}`;
                    const renderMarkerDef = (type, end) => {
                      if (type === 'none') return null;
                      const id = markerId(type, end);
                      const sz = Math.max(t * 3, 8);
                      if (type === 'arrow') {
                        return <marker key={id} id={id} markerWidth={sz} markerHeight={sz} refX={end === 'end' ? sz - 1 : 1} refY={sz/2} orient="auto"><polygon points={end === 'end' ? `0,0 ${sz},${sz/2} 0,${sz}` : `${sz},0 0,${sz/2} ${sz},${sz}`} fill={lineColor}/></marker>;
                      }
                      if (type === 'circle') {
                        return <marker key={id} id={id} markerWidth={sz} markerHeight={sz} refX={sz/2} refY={sz/2} orient="auto"><circle cx={sz/2} cy={sz/2} r={sz/2 - 1} fill="none" stroke={lineColor} strokeWidth="1.5"/></marker>;
                      }
                      if (type === 'diamond') {
                        return <marker key={id} id={id} markerWidth={sz} markerHeight={sz} refX={sz/2} refY={sz/2} orient="auto"><polygon points={`${sz/2},0 ${sz},${sz/2} ${sz/2},${sz} 0,${sz/2}`} fill="none" stroke={lineColor} strokeWidth="1.5"/></marker>;
                      }
                      return null;
                    };
                    const compOpacity = comp.opacity ?? 1;
                    return (
                      <g key={comp.id} transform={`translate(${comp.x}, ${comp.y})`} opacity={compOpacity} data-component="true">
                        <defs>
                          {renderMarkerDef(sm, 'start')}
                          {renderMarkerDef(em, 'end')}
                        </defs>
                        {/* Background fill */}
                        <rect x={0} y={0} width={w} height={h}
                          fill={comp.lowColor || '#141212'}
                          stroke={comp.borderWidth > 0 ? (comp.borderColor || '#000000') : 'none'}
                          strokeWidth={comp.borderWidth || 0}
                          style={{ pointerEvents: 'none' }}
                        />
                        {/* The line */}
                        <line x1={x1} y1={y1} x2={x2} y2={y2}
                          stroke={lineColor}
                          strokeWidth={t}
                          strokeDasharray={dashArray}
                          strokeLinecap="round"
                          markerStart={sm !== 'none' ? `url(#${markerId(sm, 'start')})` : undefined}
                          markerEnd={em !== 'none' ? `url(#${markerId(em, 'end')})` : undefined}
                          style={{ pointerEvents: 'none' }}
                        >
                          {animDur && dashArray && (
                            <animate attributeName="stroke-dashoffset"
                              from="0" to={`-${period}`}
                              dur={animDur} repeatCount="indefinite" />
                          )}
                        </line>
                        {/* Hit area */}
                        <rect x={0} y={0} width={w} height={h} fill="transparent"
                          style={{ cursor: selectedTool === 'select' ? 'move' : 'default' }}
                          onMouseDown={(e) => handleComponentMouseDown(e, comp)}
                          onDoubleClick={(e) => { e.stopPropagation(); setEditingLineComp(comp); setShowLineModal(true); }}
                        />
                        {isSelected && (
                          <ResizeHandles x={0} y={0} width={w} height={h} darkMode={darkMode}
                            onResizeStart={(e, handle) => {
                              e.stopPropagation();
                              setResizingComponent({ id: comp.id, handle, startX: e.clientX, startY: e.clientY, originalBounds: { x: comp.x, y: comp.y, width: w, height: h } });
                            }}
                          />
                        )}
                      </g>
                    );
                  }

                  // ── Render shape component ──────────────────────────────
                  if (comp.type === 'shape') {
                    const w = dim.width;
                    const h = dim.height;
                    const solidFill = comp.fillColor || '#272E35';
                    const stroke = comp.enableBorder ? (comp.borderColor || '#000000') : 'none';
                    const sw = comp.enableBorder ? (comp.borderWidth || 2) : 0;
                    const cr = comp.cornerRadius || 0;
                    const st = comp.shapeType || 'rectangle';
                    const ft = comp.fillType || 'solid';
                    const compOpacity = comp.opacity ?? 1;

                    // Gradient support
                    const gradId = `grad-${comp.id}`;
                    let fill = solidFill;
                    let gradientDef = null;
                    if (ft === 'linear') {
                      const angle = (comp.gradientAngle ?? 180) * Math.PI / 180;
                      const x1g = 50 - 50 * Math.cos(angle);
                      const y1g = 50 - 50 * Math.sin(angle);
                      const x2g = 50 + 50 * Math.cos(angle);
                      const y2g = 50 + 50 * Math.sin(angle);
                      gradientDef = (
                        <linearGradient id={gradId} x1={`${x1g}%`} y1={`${y1g}%`} x2={`${x2g}%`} y2={`${y2g}%`}>
                          <stop offset="0%" stopColor={solidFill}/>
                          <stop offset="100%" stopColor={comp.gradientColor2 || '#667eea'}/>
                        </linearGradient>
                      );
                      fill = `url(#${gradId})`;
                    } else if (ft === 'radial') {
                      gradientDef = (
                        <radialGradient id={gradId} cx="50%" cy="50%" r="50%">
                          <stop offset="0%" stopColor={solidFill}/>
                          <stop offset="100%" stopColor={comp.gradientColor2 || '#667eea'}/>
                        </radialGradient>
                      );
                      fill = `url(#${gradId})`;
                    }

                    // Build the shape SVG element based on shapeType
                    let shapeEl;
                    if (st === 'circle' || st === 'ellipse') {
                      shapeEl = <ellipse cx={w/2} cy={h/2} rx={w/2} ry={h/2} fill={fill} stroke={stroke} strokeWidth={sw} style={{ pointerEvents: 'none' }} />;
                    } else if (st === 'triangle') {
                      shapeEl = <polygon points={`${w/2},0 ${w},${h} 0,${h}`} fill={fill} stroke={stroke} strokeWidth={sw} style={{ pointerEvents: 'none' }} />;
                    } else if (st === 'diamond') {
                      shapeEl = <polygon points={`${w/2},0 ${w},${h/2} ${w/2},${h} 0,${h/2}`} fill={fill} stroke={stroke} strokeWidth={sw} style={{ pointerEvents: 'none' }} />;
                    } else if (st === 'pentagon') {
                      const pts = Array.from({length:5},(_,i)=>{const a=Math.PI*2*i/5-Math.PI/2;return`${w/2+w/2*Math.cos(a)},${h/2+h/2*Math.sin(a)}`;}).join(' ');
                      shapeEl = <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={sw} style={{ pointerEvents: 'none' }} />;
                    } else if (st === 'hexagon') {
                      const pts = Array.from({length:6},(_,i)=>{const a=Math.PI*2*i/6-Math.PI/6;return`${w/2+w/2*Math.cos(a)},${h/2+h/2*Math.sin(a)}`;}).join(' ');
                      shapeEl = <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={sw} style={{ pointerEvents: 'none' }} />;
                    } else if (st === 'star') {
                      const pts = Array.from({length:10},(_,i)=>{const a=Math.PI*2*i/10-Math.PI/2;const r2=i%2===0?0.5:0.2;return`${w/2+w*r2*Math.cos(a)},${h/2+h*r2*Math.sin(a)}`;}).join(' ');
                      shapeEl = <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={sw} style={{ pointerEvents: 'none' }} />;
                    } else if (st === 'arrow') {
                      const pts = `0,${h*0.3} ${w*0.6},${h*0.3} ${w*0.6},0 ${w},${h/2} ${w*0.6},${h} ${w*0.6},${h*0.7} 0,${h*0.7}`;
                      shapeEl = <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={sw} style={{ pointerEvents: 'none' }} />;
                    } else if (st === 'cross') {
                      const t = 0.3;
                      const pts = `${w*t},0 ${w*(1-t)},0 ${w*(1-t)},${h*t} ${w},${h*t} ${w},${h*(1-t)} ${w*(1-t)},${h*(1-t)} ${w*(1-t)},${h} ${w*t},${h} ${w*t},${h*(1-t)} 0,${h*(1-t)} 0,${h*t} ${w*t},${h*t}`;
                      shapeEl = <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={sw} style={{ pointerEvents: 'none' }} />;
                    } else if (st === 'parallelogram') {
                      const sk = w * 0.2;
                      shapeEl = <polygon points={`${sk},0 ${w},0 ${w-sk},${h} 0,${h}`} fill={fill} stroke={stroke} strokeWidth={sw} style={{ pointerEvents: 'none' }} />;
                    } else if (st === 'trapezoid') {
                      const sk = w * 0.15;
                      shapeEl = <polygon points={`${sk},0 ${w-sk},0 ${w},${h} 0,${h}`} fill={fill} stroke={stroke} strokeWidth={sw} style={{ pointerEvents: 'none' }} />;
                    } else if (st === 'roundedRect') {
                      shapeEl = <rect x={0} y={0} width={w} height={h} rx={cr || 12} ry={cr || 12} fill={fill} stroke={stroke} strokeWidth={sw} style={{ pointerEvents: 'none' }} />;
                    } else {
                      shapeEl = <rect x={0} y={0} width={w} height={h} rx={cr} ry={cr} fill={fill} stroke={stroke} strokeWidth={sw} style={{ pointerEvents: 'none' }} />;
                    }

                    return (
                      <g key={comp.id} transform={`translate(${comp.x}, ${comp.y})`} opacity={compOpacity} data-component="true">
                        {gradientDef && <defs>{gradientDef}</defs>}
                        {shapeEl}
                        {/* Hit area */}
                        <rect x={0} y={0} width={w} height={h} fill="transparent"
                          style={{ cursor: selectedTool === 'select' ? 'move' : 'default' }}
                          onMouseDown={(e) => handleComponentMouseDown(e, comp)}
                          onDoubleClick={(e) => { e.stopPropagation(); setEditingShapeComp(comp); setShowShapeModal(true); }}
                        />
                        {isSelected && (
                          <ResizeHandles x={0} y={0} width={w} height={h} darkMode={darkMode}
                            onResizeStart={(e, handle) => {
                              e.stopPropagation();
                              setResizingComponent({ id: comp.id, handle, startX: e.clientX, startY: e.clientY, originalBounds: { x: comp.x, y: comp.y, width: w, height: h } });
                            }}
                          />
                        )}
                      </g>
                    );
                  }

                  // Render LED component
                  if (comp.type === 'led') {
                    const ledSize = Math.min(dim.width, dim.height);
                    const cx = ledSize / 2;
                    const cy = ledSize / 2;
                    const r = ledSize / 2 - 2;
                    return (
                      <g
                        key={comp.id}
                        transform={`translate(${comp.x}, ${comp.y})`}
                        data-component="true"
                      >
                        <rect
                          x={0} y={0} width={ledSize} height={ledSize}
                          fill="transparent"
                          style={{ cursor: selectedTool === 'select' ? 'move' : selectedTool === 'pipe' ? 'pointer' : 'default' }}
                          onMouseDown={(e) => handleComponentMouseDown(e, comp)}
                          onDoubleClick={(e) => { e.stopPropagation(); setEditingLedComponent(comp); setShowLedModal(true); }}
                        />
                        <circle cx={cx} cy={cy} r={r}
                          fill={componentColor}
                          stroke={darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}
                          strokeWidth={2}
                          style={{ pointerEvents: 'none' }}
                        />
                        <circle cx={cx - r * 0.25} cy={cy - r * 0.25} r={r * 0.3}
                          fill="rgba(255,255,255,0.3)"
                          style={{ pointerEvents: 'none' }}
                        />
                        {comp.label && (
                          <text
                            x={cx} y={ledSize + 16}
                            textAnchor="middle" fill={theme.text}
                            fontSize="11" fontWeight="600"
                            style={{ pointerEvents: 'none', userSelect: 'none' }}
                          >
                            {comp.label}
                          </text>
                        )}
                        {comp.showStatusLabel !== false && status && (
                          <g>
                            <rect
                              x={cx - 28} y={ledSize + 22}
                              width="56" height="16"
                              fill={componentColor} rx="3" opacity="0.95"
                            />
                            <text
                              x={cx} y={ledSize + 33}
                              textAnchor="middle" fill="white"
                              fontSize="9" fontWeight="700"
                              style={{ pointerEvents: 'none', userSelect: 'none' }}
                            >
                              {status}
                            </text>
                          </g>
                        )}
                        {isSelected && (
                          <ResizeHandles
                            x={0} y={0}
                            width={ledSize} height={ledSize}
                            darkMode={darkMode}
                            onResizeStart={(e, handle) => {
                              e.stopPropagation();
                              setResizingComponent({
                                id: comp.id, handle,
                                startX: e.clientX, startY: e.clientY,
                                originalBounds: { x: comp.x, y: comp.y, width: ledSize, height: ledSize }
                              });
                            }}
                          />
                        )}
                      </g>
                    );
                  }

                  // Render standard SVG component
                  const svgData = SVG_COMPONENTS[comp.type];
                  if (!svgData) return null;

                  const svgFlipTransform = `translate(${comp.flipH ? dim.width : 0}, ${comp.flipV ? dim.height : 0}) scale(${comp.flipH ? -1 : 1}, ${comp.flipV ? -1 : 1})`;
                  return (
                    <g
                      key={comp.id}
                      transform={`translate(${comp.x}, ${comp.y})`}
                      data-component="true"
                    >
                      <g transform={`rotate(${comp.rotation || 0}, ${dim.width/2}, ${dim.height/2})`}>
                        <g transform={svgFlipTransform}>
                        {/* Interactive area */}
                        <rect
                          x={0}
                          y={0}
                          width={dim.width}
                          height={dim.height}
                          fill="transparent"
                          style={{
                            cursor: selectedTool === 'select' ? 'move' :
                                   selectedTool === 'pipe' ? 'pointer' :
                                   'default'
                          }}
                          onMouseDown={(e) => handleComponentMouseDown(e, comp)}
                          onDoubleClick={(e) => { e.stopPropagation(); if (PUMP_TYPES.has(comp.type)) { setEditingPumpComponent(comp); setShowPumpModal(true); } else if (VALVE_TYPES.has(comp.type)) { setEditingValveComponent(comp); setShowValveModal(true); } else if (CONTROL_VALVE_TYPES.has(comp.type)) { setEditingControlValveComponent(comp); setShowControlValveModal(true); } else { setEditingComponentTag(comp); setShowTagModal(true); } }}
                        />

                        {/* Component — rendered at natural size then CSS-scaled to comp dimensions.
                            This means MemoizedIcon never re-renders during resize (only on color change),
                            and the SVG truly stretches to fill the bounding box in both axes. */}
                        {(() => {
                          const natW = svgData.width || 64;
                          const natH = svgData.height || 64;
                          const scaleX = dim.width / natW;
                          const scaleY = dim.height / natH;
                          return (
                            <foreignObject width={dim.width} height={dim.height} style={{ pointerEvents: 'none', overflow: 'hidden' }}>
                              <div style={{ width: natW, height: natH, pointerEvents: 'none', transformOrigin: '0 0', transform: `scale(${scaleX}, ${scaleY})` }}>
                                <MemoizedIcon
                                  Component={svgData.component}
                                  width={natW}
                                  height={natH}
                                  startColor={componentColor}
                                  midColor={lightenColor(componentColor, 60)}
                                  endColor={darkenColor(componentColor, 20)}
                                  stemStartColor={VALVE_TYPES.has(comp.type) ? componentColor : undefined}
                                  stemMidColor={VALVE_TYPES.has(comp.type) ? lightenColor(componentColor, 60) : undefined}
                                  stemEndColor={VALVE_TYPES.has(comp.type) ? darkenColor(componentColor, 20) : undefined}
                                  fillPercent={(comp.type === 'controlValveHandActuator' || comp.type === 'controlValveDiaphragm') && comp.controlValveConfig?.op?.tagName ? (() => {
                                    const cfg = comp.controlValveConfig.op;
                                    const raw = parseFloat(tagValues?.[cfg.tagName]);
                                    if (isNaN(raw)) return 0;
                                    const min = cfg.min ?? 0;
                                    const max = cfg.max ?? 100;
                                    return Math.min(100, Math.max(0, ((raw - min) / (max - min || 1)) * 100));
                                  })() : undefined}
                                  fillColor={(comp.type === 'controlValveHandActuator' || comp.type === 'controlValveDiaphragm') ? (comp.controlValveConfig?.openColor || '#00ff00') : undefined}
                                />
                              </div>
                            </foreignObject>
                          );
                        })()}
                        </g>{/* end svgFlipTransform */}
                      </g>
                      {/* Lock indicator */}
                      {lockedComponents.has(comp.id) && (
                        <rect x={0} y={0} width={dim.width} height={dim.height} fill="none" stroke="#f59e0b" strokeWidth="2" style={{ pointerEvents: 'none' }}/>
                      )}

                      {/* Label */}
                      <text
                        x={dim.width / 2}
                        y={dim.height + 18}
                        textAnchor="middle"
                        fill={theme.text}
                        fontSize="11"
                        fontWeight="600"
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                      >
                        {comp.label}
                      </text>

                      {/* Status badge */}
                      {comp.showStatusLabel !== false && status && (
                        <g>
                          <rect
                            x={dim.width / 2 - 28}
                            y={dim.height + 26}
                            width="56"
                            height="16"
                            fill={componentColor}
                            rx="3"
                            opacity="0.95"
                          />
                          <text
                            x={dim.width / 2}
                            y={dim.height + 37}
                            textAnchor="middle"
                            fill="white"
                            fontSize="9"
                            fontWeight="700"
                            style={{ pointerEvents: 'none', userSelect: 'none' }}
                          >
                            {status}
                          </text>
                        </g>
                      )}

                      {/* Tag value */}
                      {comp.showTagValue !== false && comp.tagName && (
                        <text
                          x={dim.width / 2}
                          y={dim.height + (status ? 50 : 34)}
                          textAnchor="middle"
                          fill={theme.accent}
                          fontSize="8"
                          fontWeight="500"
                          style={{ pointerEvents: 'none', userSelect: 'none' }}
                        >
                          {comp.tagName}
                          {tagValues[comp.tagName] && `: ${tagValues[comp.tagName]}`}
                        </text>
                      )}

                      {/* Resize handles for selected SVG component */}
                      {isSelected && (
                        <ResizeHandles
                          x={0}
                          y={0}
                          width={dim.width}
                          height={dim.height}
                          rotation={comp.rotation}
                          darkMode={darkMode}
                          onResizeStart={(e, handle) => {
                            e.stopPropagation();
                            setResizingComponent({
                              id: comp.id,
                              handle,
                              startX: e.clientX,
                              startY: e.clientY,
                              originalBounds: {
                                x: comp.x,
                                y: comp.y,
                                width: dim.width,
                                height: dim.height
                              }
                            });
                          }}
                        />
                      )}
                    </g>
                  );
                })}

                {/* Selection Box */}
                {selectionBox && (
                  <rect
                    x={Math.min(selectionBox.startX, selectionBox.endX)}
                    y={Math.min(selectionBox.startY, selectionBox.endY)}
                    width={Math.abs(selectionBox.endX - selectionBox.startX)}
                    height={Math.abs(selectionBox.endY - selectionBox.startY)}
                    fill={theme.accent}
                    fillOpacity="0.1"
                    stroke={theme.accent}
                    strokeWidth="1"
                    strokeDasharray="4,2"
                  />
                )}
              </g>
            </svg>

            {/* Empty State */}
            {components.length === 0 && (
              <div style={{ 
                position: 'absolute', 
                top: '50%', 
                left: '50%', 
                transform: 'translate(-50%, -50%)', 
                textAlign: 'center', 
                color: theme.textSecondary, 
                pointerEvents: 'none' 
              }}>
                <div style={{ fontSize: '64px', marginBottom: '16px', opacity: 0.2 }}>⚙️</div>
                <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600', color: theme.text }}>
                  Start Building Your SCADA Diagram
                </h3>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  Select a component from the left panel and click to place it
                </p>
              </div>
            )}

            {/* Bottom Status Bar */}
            <div style={{
              position: 'absolute',
              bottom: '16px',
              left: '16px',
              display: 'flex',
              gap: '8px',
              zIndex: 10
            }}>
              {selectedComponent && (
                <div style={{
                  padding: '6px 12px',
                  background: theme.accent,
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  Selected: {components.find(c => c.id === selectedComponent)?.label}
                </div>
              )}
            </div>

            {/* Help overlay — toggle with ? button */}
            <div style={{ position: 'absolute', bottom: '16px', right: '16px', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
              {showShortcuts && (
                <div style={{
                  padding: '10px 14px',
                  background: theme.card,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  fontSize: '11px',
                  color: theme.textSecondary,
                  maxWidth: '320px',
                }}>
                  <div style={{ fontWeight: '700', marginBottom: '6px', color: theme.text }}>Shortcuts & Tips</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
                    <span>V</span><span>Select tool</span>
                    <span>L</span><span>Pipe tool</span>
                    <span>T</span><span>Text box tool</span>
                    <span>H</span><span>Pan tool</span>
                    <span>Del</span><span>Delete selected</span>
                    <span>Ctrl+Z</span><span>Undo</span>
                    <span>Ctrl+Y</span><span>Redo</span>
                    <span>Ctrl+C</span><span>Copy selection</span>
                    <span>Ctrl+V</span><span>Paste (cross-panel)</span>
                    <span>Esc</span><span>Deselect</span>
                    <span style={{ gridColumn: '1 / -1', marginTop: '6px', paddingTop: '6px', borderTop: `1px solid ${theme.border}`, color: theme.text, fontWeight: '600' }}>
                      Pipe Editing:
                    </span>
                    <span>Double-click</span><span>Add bend point</span>
                    <span>Drag point</span><span>Reshape pipe</span>
                  </div>
                </div>
              )}
              <button
                onClick={() => setShowShortcuts(v => !v)}
                title="Shortcuts & Tips"
                style={{
                  width: '28px', height: '28px', borderRadius: '50%', border: `1px solid ${theme.border}`,
                  background: showShortcuts ? theme.accent : theme.card,
                  color: showShortcuts ? 'white' : theme.textSecondary,
                  cursor: 'pointer', fontSize: '13px', fontWeight: '700',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >?</button>
            </div>
          </div>

          {/* RIGHT Symbols Panel — overlays canvas */}
          {showSymbols && <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, zIndex: 10 }}><SymbolsPanel
            SVG_COMPONENTS={SVG_COMPONENTS}
            componentToAdd={componentToAdd}
            selectedTool={selectedTool}
            setSelectedTool={setSelectedTool}
            setComponentToAdd={setComponentToAdd}
            setCustomComponentToAdd={setCustomComponentToAdd}
            setDrawingLine={setDrawingLine}
            setPendingShapeType={setPendingShapeType}
            darkMode={darkMode}
            customComponents={customComponents}
            onAddCustomComponent={(comp) => setCustomComponents([...customComponents, comp])}
            onRemoveCustomComponent={(id) => setCustomComponents(customComponents.filter(c => c.id !== id))}
            onSelectCustomForPlacement={(comp) => {
              setCustomComponentToAdd(comp);
              setSelectedTool('customImage');
              setComponentToAdd(null);
              setDrawingLine(null);
            }}
          /></div>}
        </div>
      </div>

      {/* Save Confirmation Popup */}
      {showSaveConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99998
        }}>
          <div style={{
            background: darkMode ? '#1a1d29' : '#ffffff',
            borderRadius: '16px',
            padding: '32px',
            width: '360px',
            boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
            border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
            textAlign: 'center'
          }}>
            <div style={{
              width: '52px', height: '52px', borderRadius: '50%',
              background: darkMode ? '#1e3a5f' : '#dbeafe',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <Save size={24} color={darkMode ? '#60a5fa' : '#2563eb'} />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '700', color: darkMode ? '#e5e7eb' : '#111827' }}>
              Save Diagram?
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280', lineHeight: '1.5' }}>
              This will save <strong style={{ color: darkMode ? '#e5e7eb' : '#111827' }}>{diagramTitle}</strong> with {components.length} component{components.length !== 1 ? 's' : ''} and {lines.length} pipe{lines.length !== 1 ? 's' : ''}.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowSaveConfirm(false)}
                style={{
                  flex: 1, padding: '12px', borderRadius: '8px',
                  border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                  background: 'transparent', color: darkMode ? '#e5e7eb' : '#111827',
                  fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowSaveConfirm(false); handleSave(); }}
                style={{
                  flex: 1, padding: '12px', borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Toast */}
      {saveToast && (
        <div style={{
          position: 'fixed',
          bottom: '32px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#10b981',
          color: '#ffffff',
          padding: '12px 24px',
          borderRadius: '10px',
          fontSize: '14px',
          fontWeight: '600',
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 99999,
          pointerEvents: 'none',
          animation: 'fadeInUp 0.2s ease'
        }}>
          ✓ {saveToast.message}
        </div>
      )}

      {/* Tag Configuration Modal */}
      {showTagModal && editingComponentTag && (
        <ComponentTagModal
          component={editingComponentTag}
          availableTags={availableTags}
          onSave={handleSaveTagConfig}
          onClose={() => { setShowTagModal(false); setEditingComponentTag(null); }}
          darkMode={darkMode}
        />
      )}

      {/* Pump Configuration Modal */}
      {showPumpModal && editingPumpComponent && (
        <PumpConfigModal
          component={editingPumpComponent}
          availableTags={availableTags}
          onSave={handleSavePumpConfig}
          onClose={() => { setShowPumpModal(false); setEditingPumpComponent(null); }}
          darkMode={darkMode}
        />
      )}

      {/* Valve Configuration Modal */}
      {showValveModal && editingValveComponent && (
        <ValveConfigModal
          component={editingValveComponent}
          availableTags={availableTags}
          onSave={handleSaveValveConfig}
          onClose={() => { setShowValveModal(false); setEditingValveComponent(null); }}
          darkMode={darkMode}
        />
      )}

      {showControlValveModal && editingControlValveComponent && (
        <ControlValveConfigModal
          component={editingControlValveComponent}
          availableTags={availableTags}
          onSave={handleSaveControlValveConfig}
          onClose={() => { setShowControlValveModal(false); setEditingControlValveComponent(null); }}
          darkMode={darkMode}
        />
      )}

      {/* LED Configuration Modal */}
      {showLedModal && editingLedComponent && (
        <LedConfigModal
          component={editingLedComponent}
          availableTags={availableTags}
          onSave={handleSaveLedConfig}
          onClose={() => { setShowLedModal(false); setEditingLedComponent(null); }}
          darkMode={darkMode}
        />
      )}

      {/* TextBox Configuration Modal */}
      {showTextBoxModal && editingTextBox && (
        <TextBoxConfigModal
          element={editingTextBox}
          availableTags={availableTags}
          onSave={(updatedTextBox) => {
            setComponents(components.map(c => c.id === updatedTextBox.id ? updatedTextBox : c));
            saveToHistory();
            setShowTextBoxModal(false);
            setEditingTextBox(null);
            showSaveToast('Text box saved!');
            if (updatedTextBox.tagName) {
              fetchSingleTagValue(updatedTextBox.tagName);
            }
          }}
          onClose={() => { setShowTextBoxModal(false); setEditingTextBox(null); }}
          darkMode={darkMode}
        />
      )}

      {/* Pipe Configuration Modal */}
      {showPipeModal && editingPipe && (
        <PipeConfigModal
          pipe={editingPipe}
          onSave={(updatedPipe) => {
            setLines(lines.map(l => l.id === updatedPipe.id ? updatedPipe : l));
            saveToHistory();
            setShowPipeModal(false);
            setEditingPipe(null);
            showSaveToast('Pipe configuration saved!');
          }}
          onClose={() => { setShowPipeModal(false); setEditingPipe(null); }}
          darkMode={darkMode}
        />
      )}

      {/* Nav Button Configuration Modal */}
      {showNavButtonModal && editingNavButton && (
        <NavButtonConfigModal
          component={editingNavButton}
          availablePanels={availablePanels}
          onSave={(updated) => {
            setComponents(prev => prev.map(c => c.id === updated.id ? updated : c));
            saveToHistory();
            setShowNavButtonModal(false);
            setEditingNavButton(null);
            showSaveToast('Navigation button saved!');
          }}
          onClose={() => { setShowNavButtonModal(false); setEditingNavButton(null); }}
          darkMode={darkMode}
        />
      )}

      {/* Line Configuration Modal */}
      {showLineModal && editingLineComp && (
        <LineConfigModal
          component={editingLineComp}
          onSave={(updated) => {
            setComponents(prev => prev.map(c => c.id === updated.id ? updated : c));
            saveToHistory();
            setShowLineModal(false);
            setEditingLineComp(null);
            showSaveToast('Line saved!');
          }}
          onClose={() => { setShowLineModal(false); setEditingLineComp(null); }}
          darkMode={darkMode}
        />
      )}

      {/* Shape Configuration Modal */}
      {showShapeModal && editingShapeComp && (
        <ShapeConfigModal
          component={editingShapeComp}
          onSave={(updated) => {
            setComponents(prev => prev.map(c => c.id === updated.id ? updated : c));
            saveToHistory();
            setShowShapeModal(false);
            setEditingShapeComp(null);
            showSaveToast('Shape saved!');
          }}
          onClose={() => { setShowShapeModal(false); setEditingShapeComp(null); }}
          darkMode={darkMode}
        />
      )}
    </div>
  );
}
